<template>
  <div
    class="make-clips-page clipr-theme twitch-theme"
    :class="{ 'make-clips-page--library-focused': isLibraryFocused }"
  >
    <div class="make-clips-page-content">
      <div class="url-icons-preload" aria-hidden="true">
        <img v-for="url in platformIconUrls" :key="url" :src="url" alt="" />
      </div>
      <first-free-stream-banner
        v-if="showFirstFreeStreamBanner"
        :countdown-text="firstFreeStreamCountdownText"
      />
      <card
        v-if="(!isAdminMultiView || isAdminSingleView) && !isLibraryFocused"
        class="card main-card"
        :class="{ 'main-card--flow-locked': isFlowSlotLocked }"
      >
      <div class="card-content">
        <form ref="submitSectionPrimary" @submit.prevent="handlePrimaryFormSubmit">
          <div
            class="make-flow-viewport"
            :class="{ 'make-flow-viewport--locked': isFlowSlotLocked }"
          >
          <transition name="make-flow-swap" mode="out-in">
          <section
            v-if="flowPhase === 'url' || flowPhase === 'validating'"
            key="flow-step-url"
            class="make-flow-stage make-flow-stage--url"
          >
          <section class="try-url-form make-clips-url-form">
            <header class="try-url-hero">
              <h1 class="try-url-hero-title">{{ tt("tryClips.urlForm.heroTitle", "Convierte tu stream en clips virales") }}</h1>
              <p
                class="try-url-hero-subtitle"
                v-html="inputMode === 'file'
                  ? tt('makeClips.uploadMp4HeroSubtitle', 'Sube tu MP4 y recibe clips listos para publicar <span class=&quot;try-url-hero-subtitle-accent&quot;>en minutos</span>!')
                  : tt('makeClips.urlHeroSubtitle', 'Pega el enlace de tu video y recibe clips listos para publicar <span class=&quot;try-url-hero-subtitle-accent&quot;>en minutos</span>!')"
              ></p>
            </header>

            <section class="form-section url-section">
              <div class="url-section-label">
                <div class="url-section-label-main">
                  <span>{{ inputMode === "file" ? tt("makeClips.uploadMp4Label", "Archivo MP4") : tt("tryClips.urlForm.urlLabel", "URL del video") }}</span>
                  <div
                    v-if="canUploadMp4"
                    class="source-mode-toggle"
                    role="group"
                    :aria-label="tt('makeClips.sourceModeLabel', 'Origen del video')"
                  >
                    <button
                      type="button"
                      :class="{ active: inputMode === 'url' }"
                      :disabled="loading || isFlowValidating || metadataPreviewLoading"
                      @click="setInputMode('url')"
                    >
                      URL
                    </button>
                    <button
                      type="button"
                      :class="{ active: inputMode === 'file' }"
                      :disabled="loading || isFlowValidating || metadataPreviewLoading"
                      @click="setInputMode('file')"
                    >
                      {{ tt("makeClips.uploadMp4Toggle", "Subir MP4") }}
                    </button>
                  </div>
                </div>
                <div v-if="inputMode === 'url'" class="url-section-platform-icons" aria-hidden="true">
                  <img
                    v-for="url in platformIconUrls"
                    :key="`single-${url}`"
                    :src="url"
                    alt=""
                    class="url-section-platform-icon"
                  />
                </div>
              </div>
              <div
                v-if="inputMode === 'url'"
                class="url-input-unified"
                :class="{ 'url-input-unified--validating': isFlowValidating }"
              >
                <div class="url-input-icon-segment">
                  <i class="ti-link"></i>
                </div>
                <div class="url-input-field">
                  <MakeClipsAnimatedUrlPlaceholder
                    v-if="!videoUrl && !loading && !isFlowValidating"
                  />
                  <input
                    ref="primaryVideoUrlInput"
                    type="url"
                    class="url-input"
                    v-model.trim="videoUrl"
                    placeholder=""
                    :disabled="loading || isFlowValidating"
                    @paste="handlePrimaryUrlPaste"
                  />
                </div>
                <div
                  class="url-input-badge-segment"
                  :class="isFlowValidating ? 'url-input-badge-segment--validating' : (autodetectedSourceType ? `url-input-badge-segment--${autodetectedSourceType}` : 'url-input-badge-segment--pending')"
                >
                  <template v-if="isFlowValidating">
                    <span class="url-badge-spinner" aria-hidden="true"></span>
                    <span>{{ tt("tryClips.validatingBadge", "Validando…") }}</span>
                  </template>
                  <template v-else>
                    <img
                      v-if="sourceTypeIconUrl"
                      :src="sourceTypeIconUrl"
                      alt=""
                      class="url-badge-icon"
                    />
                    <i v-else class="ti-rss-alt"></i>
                    <span v-if="autodetectedSourceType">{{ detectedSourceLabel }}</span>
                    <span v-else>{{ tt("tryClips.urlForm.autodetected", "Autodetectado") }}</span>
                  </template>
                </div>
              </div>
              <div
                v-else
                class="mp4-dropzone"
                :class="{
                  'mp4-dropzone--active': fileDropActive,
                  'mp4-dropzone--selected': selectedFile,
                  'mp4-dropzone--disabled': loading || metadataPreviewLoading,
                }"
                role="button"
                tabindex="0"
                @click="openFilePicker"
                @keydown.enter.prevent="openFilePicker"
                @keydown.space.prevent="openFilePicker"
                @dragenter.prevent="fileDropActive = true"
                @dragover.prevent="fileDropActive = true"
                @dragleave.prevent="fileDropActive = false"
                @drop.prevent="handleFileDrop"
              >
                <input
                  ref="mp4FileInput"
                  class="mp4-file-input"
                  type="file"
                  accept=".mp4,video/mp4"
                  :disabled="loading || metadataPreviewLoading"
                  @change="handleFileInputChange"
                />
                <i :class="metadataPreviewLoading ? 'ti-reload mp4-dropzone-spinner' : 'ti-cloud-up'"></i>
                <div class="mp4-dropzone-copy">
                  <strong v-if="selectedFile">{{ selectedFile.name }}</strong>
                  <strong v-else>{{ tt("makeClips.uploadMp4DropTitle", "Arrastra tu MP4 o selecciónalo") }}</strong>
                  <span v-if="selectedFile">{{ formatBytes(selectedFile.size) }}</span>
                  <span v-else>{{ tt("makeClips.uploadMp4DropHint", "Haz clic para buscar el archivo") }}</span>
                </div>
              </div>
              <p v-if="inputMode === 'file'" class="mp4-requirements">
                <i class="ti-info-alt"></i>
                {{ tt("makeClips.uploadMp4Requirements", "MP4 horizontal, máximo 1080p, 12 h y 100 GiB.") }}
              </p>
              <p v-if="createFlowSuccessMessage" class="make-flow-success-hint">
                <i class="ti-check"></i>
                {{ createFlowSuccessMessage }}
              </p>
              <div v-if="flowPhase === 'url' && metadataPreviewError && !isFlowValidating" class="url-section-error-note">
                <span class="url-section-error-note-icon" aria-hidden="true">
                  <i class="ti-alert"></i>
                </span>
                <p class="url-section-error-note-copy">
                  <strong>{{ tt("makeClips.flowValidationErrorTitle", "No pudimos validar tu video") }}</strong>
                  <span>{{ metadataPreviewError }}</span>
                </p>
              </div>
              <p v-if="inputMode === 'url' && isValidUrl && !isFlowValidating" class="make-flow-url-hint">
                <i class="ti-info-alt"></i>
                {{ tt("makeClips.flowAutoValidateHint", "Validaremos tu video automáticamente. Presiona Enter si no avanza.") }}
              </p>
              <div v-if="inputMode === 'url' && videoUrl && !isValidUrl" class="url-section-error-note">
                <span class="url-section-error-note-icon" aria-hidden="true">
                  <i class="ti-alert"></i>
                </span>
                <p class="url-section-error-note-copy">
                  <strong>{{ urlErrorTitle }}</strong>
                  <span>{{ urlErrorMessage }}</span>
                </p>
              </div>
              <div v-if="inputMode === 'url' && autodetectedSourceType === 'google_drive'" class="url-section-drive-note">
                <span class="url-section-drive-note-icon" aria-hidden="true">
                  <i class="ti-info-alt"></i>
                </span>
                <p class="url-section-drive-note-copy">
                  <strong>{{ tt("tryClips.urlForm.drivePermissionsTitle", "Permisos públicos requeridos") }}</strong>
                  <span>{{ tt("tryClips.urlForm.drivePermissionsBody", "Asegúrate de que el archivo en Google Drive sea público: \"Cualquiera con el enlace\" y con descarga permitida.") }}</span>
                </p>
              </div>
              <div v-if="inputMode === 'url' && isFacebookReelSource" class="url-section-drive-note">
                <span class="url-section-drive-note-icon" aria-hidden="true">
                  <i class="ti-info-alt"></i>
                </span>
                <p class="url-section-drive-note-copy">
                  <strong>{{ tt("tryClips.urlForm.facebookReelHorizontalTitle", "Formato horizontal requerido") }}</strong>
                  <span>{{ tt("tryClips.urlForm.facebookReelHorizontalBody", "Si usas Facebook Reels, asegúrate de que tu Reel esté en formato horizontal (16:9).") }}</span>
                </p>
              </div>
            </section>
          </section>
          </section>

          <section
            v-else-if="flowPhase === 'configure'"
            key="flow-step-configure"
            class="make-flow-stage make-flow-stage--configure"
            :class="{ 'make-flow-stage--configure-choosing': typeChoosing }"
          >
            <div class="make-flow-step-header">
              <button
                type="button"
                class="make-flow-back-btn"
                :disabled="loading || typeChoosing"
                :aria-label="tt('makeClips.flowBack', 'Volver')"
                @click="backToUrlStep"
              >
                <i class="ti-arrow-left"></i>
              </button>
              <div class="make-flow-step-heading">
                <h2 class="make-flow-step-title">{{ tt("makeClips.flowConfigureTitle", "Elige el tipo de video") }}</h2>
              </div>
            </div>

            <div
              ref="flowConfigureBody"
              class="make-flow-configure-body"
              :class="{ 'make-flow-configure-body--choosing': typeChoosing }"
            >
              <div class="make-flow-configure-preview">
                <try-source-download
                  compact
                  class="make-flow-download"
                  :class="{ 'make-flow-download--choosing': typeChoosing }"
                  :thumbnail="flowThumbnail"
                  :title="flowVideoTitle"
                  :platform-icon-url="sourceTypeIconUrl"
                  :platform-label="detectedSourceLabel"
                  :hint="flowDurationHint"
                />
              </div>

              <div class="make-flow-subtitles-wrap">
                <label
                  class="make-flow-subtitles-pill"
                  :class="subtitlesEnabled ? 'make-flow-subtitles-pill--active' : 'make-flow-subtitles-pill--inactive'"
                >
                  <span class="make-flow-subtitles-pill-row">
                    <span class="make-flow-subtitles-pill-label">
                      {{ tt("makeClips.enableSubtitles", "Agregar subtítulos") }}
                    </span>
                    <span class="make-flow-subtitles-pill-switch">
                      <input
                        v-model="subtitlesEnabled"
                        @change="saveSubtitlesPreference(subtitlesEnabled)"
                        type="checkbox"
                        :disabled="loading || typeChoosing"
                        :aria-label="tt('makeClips.enableSubtitles', 'Agregar subtítulos')"
                      >
                      <span class="make-flow-subtitles-pill-track" aria-hidden="true">
                        <span class="make-flow-subtitles-pill-thumb"></span>
                      </span>
                    </span>
                  </span>
                  <span
                    class="make-flow-subtitles-pill-status"
                    :class="subtitlesEnabled ? 'is-on' : 'is-off'"
                  >
                    {{ subtitlesEnabled
                      ? tt("makeClips.subtitlesStatusOn", "Activados")
                      : tt("makeClips.subtitlesStatusOff", "Desactivados") }}
                  </span>
                </label>
                <button
                  type="button"
                  class="make-flow-subtitle-style-button"
                  :disabled="loading || typeChoosing"
                  @click="openSubtitleStyleModal"
                >
                  <i class="ti-pencil-alt" aria-hidden="true"></i>
                  {{ tt("userProfile.subtitleStyleEdit", "Editar") }}
                </button>
              </div>

              <section class="make-flow-type-section">
                <div
                  ref="flowTypeGrid"
                  class="confirm-video-type-grid make-flow-type-grid"
                  role="radiogroup"
                  :aria-label="tt('makeClips.videoTypeLabel', 'Tipo de video')"
                >
                <button
                  v-for="option in videoTypeOptions"
                  ref="flowTypeCards"
                  :key="`flow-video-type-${option.value}`"
                  type="button"
                  class="confirm-video-type-option"
                  :class="{
                    'confirm-video-type-option--active': adminVideoType === option.value,
                    'make-flow-type-option--chosen': typeChoosing && adminVideoType === option.value,
                    'make-flow-type-option--dismiss': typeChoosing && adminVideoType !== option.value,
                    'make-flow-type-option--leaving': typeLeaving && adminVideoType === option.value,
                  }"
                  :style="(typeChoosing && adminVideoType === option.value)
                    ? { '--make-chosen-x': chosenTypeCardOffset.x + 'px', '--make-chosen-y': chosenTypeCardOffset.y + 'px' }
                    : null"
                  :aria-pressed="adminVideoType === option.value"
                  :disabled="loading || typeChoosing"
                  @click="handleFlowVideoTypeSelect(option.value)"
                >
                  <span class="confirm-video-type-option-shine" aria-hidden="true"></span>
                  <span class="confirm-video-type-option-icon">
                    <LayoutTypeIcon :type="option.value" />
                  </span>
                  <span class="confirm-video-type-option-copy">
                    <strong>{{ tt(option.labelKey, option.fallback) }}</strong>
                    <small>{{ tt(option.descriptionKey, option.descriptionFallback) }}</small>
                  </span>
                  <span class="confirm-video-type-option-check">
                    <i class="ti-check"></i>
                  </span>
                </button>
              </div>
              <transition name="confirm-context-reveal">
                <div v-if="adminVideoType === 'irl' && !typeChoosing && !typeLeaving" class="confirm-video-context-card">
                  <div class="confirm-video-context-header">
                    <i class="ti-align-left"></i>
                    <span>{{ tt("makeClips.videoGeneralContext", "Contexto general del video") }}</span>
                  </div>
                  <label class="confirm-video-context-toggle">
                    <input
                      v-model="irlUserContextEnabled"
                      type="checkbox"
                    >
                    <span class="confirm-video-context-switch"></span>
                    <span class="confirm-video-context-toggle-label">
                      {{ tt("makeClips.enableVideoGeneralContext", "Activar contexto") }}
                    </span>
                  </label>
                  <template v-if="irlUserContextEnabled">
                    <p class="confirm-video-context-helper">
                      {{ tt("makeClips.videoGeneralContextHelper", "Añade detalles que ayuden a la IA a entender la escena") }}
                    </p>
                    <textarea
                      v-model="userContext"
                      class="confirm-video-context-input"
                      rows="3"
                      :placeholder="tt('makeClips.videoGeneralContextPlaceholder', 'Agrega contexto general para este video')"
                    />
                  </template>
                </div>
              </transition>
              </section>
            </div>
          </section>

          <section
            v-else-if="flowPhase === 'processing' && shouldShowProcessingAnimation"
            key="flow-step-processing"
            class="make-flow-stage make-flow-stage--processing"
          >
            <try-download-animation
              v-if="isProcessingRenderingPhase"
              class="make-flow-processing-render"
              :title="processingRenderingTitle"
              :platform-icon-url="processingIconUrl"
              :platform-label="processingLabel"
            />
            <try-source-download
              v-else
              compact
              class="make-flow-download make-flow-processing-download"
              :thumbnail="processingThumbnail"
              :platform-icon-url="processingIconUrl"
              :platform-label="processingLabel"
              :eyebrow="tt('tryClips.loader.downloading', 'Descargando...')"
              :hint="processingEtaMessage"
              :size-bytes="processingSizeBytes"
            />
          </section>

          <section
            v-else
            key="flow-step-confirm"
            class="make-flow-stage make-flow-stage--confirm"
          >
            <div class="make-flow-step-header">
              <button
                type="button"
                class="make-flow-back-btn"
                :disabled="loading"
                :aria-label="tt('makeClips.flowBack', 'Volver')"
                @click="backToConfigureStep"
              >
                <i class="ti-arrow-left"></i>
              </button>
              <div class="make-flow-step-heading">
                <h2 class="make-flow-step-title">{{ tt("makeClips.flowConfirmTitle", "Revisa y confirma") }}</h2>
              </div>
            </div>

            <div v-if="canAssignOnCreate" class="make-flow-admin-bar">
              <div class="make-flow-admin-bar-copy">
                <span class="make-flow-admin-bar-label">{{ tt("makeClips.assignedUser", "Usuario asignado") }}</span>
                <span v-if="selectedAssignee && selectedAssignee.username" class="make-flow-admin-bar-user">
                  {{ selectedAssignee.username }}
                </span>
                <span v-else class="make-flow-admin-bar-user make-flow-admin-bar-user--empty">
                  {{ tt("makeClips.unassignedCurrentUser", "Sin reasignar (se crea para tu usuario actual)") }}
                </span>
              </div>
              <button
                type="button"
                class="make-flow-admin-assign-btn"
                :disabled="loading"
                @click="openAssignModal"
              >
                <i class="ti-user"></i>
                {{ tt("common.assign", "Asignar") }}
              </button>
            </div>

            <div class="make-flow-confirm-body">
              <div v-if="metadataPreview" class="make-flow-confirm-video">
                <div class="make-flow-confirm-video-thumb">
                  <img
                    v-if="metadataPreview.thumbnail"
                    :src="metadataPreview.thumbnail"
                    alt="Miniatura"
                  />
                  <div v-else class="make-flow-confirm-video-thumb-placeholder">
                    <i class="ti-video-camera"></i>
                  </div>
                  <span class="make-flow-confirm-video-duration">
                    {{ formatDuration(metadataPreview.duration) }}
                  </span>
                </div>
                <div class="make-flow-confirm-video-copy">
                  <h3 class="make-flow-confirm-video-title">
                    {{ metadataPreview.title || tt("makeClips.untitled", "Sin título") }}
                  </h3>
                  <div class="make-flow-confirm-video-meta">
                    <span v-if="metadataPreview.user || detectedSourceLabel" class="make-flow-confirm-video-source">
                      <img v-if="sourceTypeIconUrl" :src="sourceTypeIconUrl" alt="" />
                      <i v-else class="ti-user"></i>
                      {{ metadataPreview.user || detectedSourceLabel }}
                    </span>
                    <span class="make-flow-confirm-video-meta-detail">
                      <i class="ti-calendar"></i>{{ formatUploadDate(metadataPreview.upload_date) }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="make-flow-confirm-grid">
                <div
                  class="make-flow-confirm-main"
                  :class="{ 'make-flow-confirm-main--with-trim': segments.length > 0 }"
                >
                  <div class="make-flow-confirm-type">
                    <div class="make-flow-confirm-section-head">
                      <span>{{ tt("makeClips.videoTypeLabel", "Tipo de video") }}</span>
                    </div>
                    <div v-if="flowSelectedTypeOption && !isConfirmVideoTypeChangeOpen" class="make-flow-type-selected">
                      <span class="make-flow-type-selected-icon">
                        <LayoutTypeIcon :type="flowSelectedTypeOption.value" />
                      </span>
                      <span class="make-flow-type-selected-copy">
                        <strong>{{ tt(flowSelectedTypeOption.labelKey, flowSelectedTypeOption.fallback) }}</strong>
                        <small>{{ tt(flowSelectedTypeOption.descriptionKey, flowSelectedTypeOption.descriptionFallback) }}</small>
                      </span>
                      <button
                        type="button"
                        class="make-flow-change-type-btn"
                        :aria-expanded="isConfirmVideoTypeChangeOpen"
                        @click="isConfirmVideoTypeChangeOpen = !isConfirmVideoTypeChangeOpen"
                      >
                        <i class="ti-pencil" aria-hidden="true"></i>
                        {{ tt("makeClips.changeVideoType", "Cambiar") }}
                      </button>
                    </div>
                    <transition name="confirm-video-type-change">
                      <div v-if="isConfirmVideoTypeChangeOpen" class="confirm-video-type-grid make-flow-confirm-type-grid">
                        <button
                          v-for="option in videoTypeOptions"
                          :key="`confirm-video-type-${option.value}`"
                          type="button"
                          class="confirm-video-type-option"
                          :class="{ 'confirm-video-type-option--active': adminVideoType === option.value }"
                          :aria-pressed="adminVideoType === option.value"
                          @click="setConfirmVideoType(option.value)"
                        >
                          <span class="confirm-video-type-option-shine" aria-hidden="true"></span>
                          <span class="confirm-video-type-option-icon">
                            <LayoutTypeIcon :type="option.value" />
                          </span>
                          <span class="confirm-video-type-option-copy">
                            <strong>{{ tt(option.labelKey, option.fallback) }}</strong>
                            <small>{{ tt(option.descriptionKey, option.descriptionFallback) }}</small>
                          </span>
                          <span class="confirm-video-type-option-check">
                            <i class="ti-check"></i>
                          </span>
                        </button>
                      </div>
                    </transition>
                    <p
                      v-if="adminVideoType === 'irl' && irlUserContextEnabled && userContext.trim() && !isConfirmVideoTypeChangeOpen"
                      class="make-flow-context-summary"
                    >
                      <i class="ti-align-left"></i>
                      {{ userContext }}
                    </p>
                  </div>

                  <div v-if="segments.length > 0" class="make-flow-confirm-trim">
                    <div class="make-flow-confirm-section-head">
                      <span>{{ tt("makeClips.trimVideoTitle", "Ajusta el rango del video") }}</span>
                    </div>
                    <make-clips-segments-timeline
                      v-if="videoDurationSeconds > 0"
                      class="confirm-segments-timeline make-flow-confirm-timeline"
                      :duration-seconds="videoDurationSeconds"
                      :segments="segments"
                      :disabled="loading"
                      :compact="true"
                      @segment-change="onTimelineSegmentChange"
                      @add-segment="onTimelineAddSegment"
                      @drag-end="onTimelineDragEnd"
                    />
                    <div v-else class="confirm-manual-trim">
                      <div class="confirm-manual-trim-fields">
                        <label class="segment-time-label">{{ tt("makeClips.start", "INICIO") }}</label>
                        <input
                          type="text"
                          class="segment-time-input"
                          v-model.trim="segments[0].startTime"
                          placeholder="HH:MM:SS"
                          :disabled="loading"
                          @input="updateSegment(0)"
                          @blur="normalizeSegment(0, 'startTime')"
                        />
                        <span class="segment-time-arrow" aria-hidden="true">
                          <i class="ti-arrow-right"></i>
                        </span>
                        <label class="segment-time-label">{{ tt("makeClips.end", "FIN") }}</label>
                        <input
                          type="text"
                          class="segment-time-input"
                          v-model.trim="segments[0].endTime"
                          placeholder="HH:MM:SS"
                          :disabled="loading"
                          @input="updateSegment(0)"
                          @blur="normalizeSegment(0, 'endTime')"
                        />
                      </div>
                      <small v-if="!segmentsValidation.valid" class="text-danger d-block mt-2">
                        {{ segmentsValidation.message }}
                      </small>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div v-if="inputMode === 'file' && loading" class="upload-progress-card">
              <div class="upload-progress-head">
                <span>{{ uploadMessage }}</span>
                <strong>{{ uploadProgress }}%</strong>
              </div>
              <div class="upload-progress-track" role="progressbar" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100">
                <span :style="{ width: `${uploadProgress}%` }"></span>
              </div>
              <small v-if="uploadQueuePosition != null">
                {{ tt("makeClips.uploadQueuePosition", "Posición en cola: {position}", { position: uploadQueuePosition }) }}
              </small>
            </div>

            <p v-if="createFlowSubmitError" class="submit-hint admin-error-hint make-flow-submit-error">
              <i class="ti-alert"></i>
              {{ createFlowSubmitError }}
            </p>

            <div class="make-flow-footer make-flow-footer--confirm">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--primary make-flow-confirm-submit-btn"
                :disabled="isFlowConfirmDisabled"
                @click="confirmCreateFlow"
              >
                <span v-if="loading" class="make-flow-queue-spinner" aria-hidden="true">
                  <span class="make-flow-queue-spinner-aura"></span>
                  <span class="make-flow-queue-spinner-ring make-flow-queue-spinner-ring--outer"></span>
                  <span class="make-flow-queue-spinner-ring make-flow-queue-spinner-ring--inner"></span>
                  <span class="make-flow-queue-spinner-orbit"></span>
                </span>
                <i v-else class="fa-solid fa-clapperboard" aria-hidden="true"></i>
                <span class="make-flow-confirm-submit-copy">
                  <span>{{ loading ? (uploadMessage || tt("makeClips.inQueue", "En cola...")) : tt("makeClips.confirmAndCreate", "Crear clips") }}</span>
                  <small v-if="!loading">{{ tt("makeClips.processingDurationHint", "Se procesará {duration}", { duration: formatDuration(processingDurationSeconds) }) }}</small>
                </span>
              </button>
            </div>
          </section>
          </transition>
          </div>
        </form>
      </div>
    </card>

    <div v-else-if="!isLibraryFocused" class="admin-forms-grid">
      <card
        v-for="(adminForm, cardIndex) in adminForms"
        :key="adminForm.id"
        class="card main-card admin-main-card"
      >
        <div class="card-content">
          <div class="admin-card-topbar">
            <h3 class="admin-card-title">{{ tt("makeClips.clipLabel", "Clip") }} #{{ cardIndex + 1 }}</h3>
            <button
              type="button"
              class="admin-add-card-btn"
              :disabled="adminForm.loading"
              :title="tt('makeClips.addAnotherClip', 'Agregar otro clip')"
              @click="addAdminForm(cardIndex)"
            >
              <i class="ti-plus"></i>
            </button>
          </div>

          <section class="form-section url-section">
            <div class="url-section-label">
              <span>{{ tt("tryClips.urlForm.urlLabel", "URL del video") }}</span>
              <div class="url-section-platform-icons" aria-hidden="true">
                <img
                  v-for="url in platformIconUrls"
                  :key="`admin-${url}`"
                  :src="url"
                  alt=""
                  class="url-section-platform-icon"
                />
              </div>
            </div>
            <div class="url-input-unified">
              <div class="url-input-icon-segment">
                <i class="ti-link"></i>
              </div>
              <div class="url-input-field">
                <MakeClipsAnimatedUrlPlaceholder
                  v-if="!adminForm.videoUrl && !adminForm.loading"
                />
                <input
                  v-model.trim="adminForm.videoUrl"
                  type="url"
                  class="url-input"
                  placeholder=""
                  :disabled="adminForm.loading"
                />
              </div>
              <div
                class="url-input-badge-segment"
                :class="detectSourceType(adminForm.videoUrl) ? `url-input-badge-segment--${detectSourceType(adminForm.videoUrl)}` : 'url-input-badge-segment--pending'"
              >
                <img
                  v-if="getSourceTypeIconUrl(detectSourceType(adminForm.videoUrl))"
                  :src="getSourceTypeIconUrl(detectSourceType(adminForm.videoUrl))"
                  alt=""
                  class="url-badge-icon"
                />
                <i v-else class="ti-rss-alt"></i>
                <span v-if="detectSourceType(adminForm.videoUrl)">{{ getDetectedSourceLabelByValue(adminForm.videoUrl) }}</span>
                <span v-else>{{ tt("tryClips.urlForm.autodetected", "Autodetectado") }}</span>
              </div>
            </div>
            <div v-if="adminForm.videoUrl && !getAdminUrlValidation(adminForm).valid" class="url-section-error-note">
              <span class="url-section-error-note-icon" aria-hidden="true">
                <i class="ti-alert"></i>
              </span>
              <p class="url-section-error-note-copy">
                <strong>{{ getAdminUrlErrorTitle(adminForm) }}</strong>
                <span>{{ getAdminUrlValidation(adminForm).message }}</span>
              </p>
            </div>
            <div v-if="detectSourceType(adminForm.videoUrl) === 'google_drive'" class="url-section-drive-note">
              <span class="url-section-drive-note-icon" aria-hidden="true">
                <i class="ti-info-alt"></i>
              </span>
              <p class="url-section-drive-note-copy">
                <strong>{{ tt("tryClips.urlForm.drivePermissionsTitle", "Permisos públicos requeridos") }}</strong>
                <span>{{ tt("tryClips.urlForm.drivePermissionsBody", "Asegúrate de que el archivo en Google Drive sea público: \"Cualquiera con el enlace\" y con descarga permitida.") }}</span>
              </p>
            </div>
            <div v-if="isFacebookReelUrl(adminForm.videoUrl)" class="url-section-drive-note">
              <span class="url-section-drive-note-icon" aria-hidden="true">
                <i class="ti-info-alt"></i>
              </span>
              <p class="url-section-drive-note-copy">
                <strong>{{ tt("tryClips.urlForm.facebookReelHorizontalTitle", "Formato horizontal requerido") }}</strong>
                <span>{{ tt("tryClips.urlForm.facebookReelHorizontalBody", "Si usas Facebook Reels, asegúrate de que tu Reel esté en formato horizontal (16:9).") }}</span>
              </p>
            </div>
          </section>

          <section class="form-section segments-section">
            <div class="section-label section-label-with-action">
              <div class="section-label-main">
                <i class="ti-list"></i>
                <span>{{ tt("makeClips.streamSegments", "Segmentos del stream") }}</span>
              </div>
            </div>

            <div class="d-flex gap-2 mb-2" v-if="adminForm.segments.length === 0">
              <button
                type="button"
                class="add-segment-btn add-first-segment-btn"
                @click="addAdminSegment(cardIndex)"
                :disabled="adminForm.loading || !getAdminUrlValidation(adminForm).valid"
              >
                <i class="ti-plus mr-1"></i>
                {{ tt("makeClips.addSegment", "Añadir segmento") }}
              </button>
            </div>

            <div v-if="adminForm.segments.length > 0" class="admin-simple-segments">
              <div
                v-for="(segment, segmentIndex) in adminForm.segments"
                :key="segment.id"
                class="admin-simple-segment-row"
              >
                <span class="admin-simple-segment-index">#{{ segmentIndex + 1 }}</span>
                <input
                  v-model.trim="segment.startTime"
                  type="text"
                  class="admin-simple-time-input"
                  placeholder="HH:MM:SS"
                  :disabled="adminForm.loading"
                  @blur="normalizeAdminSegment(cardIndex, segmentIndex, 'startTime')"
                />
                <span class="admin-simple-separator">-</span>
                <input
                  v-model.trim="segment.endTime"
                  type="text"
                  class="admin-simple-time-input"
                  placeholder="HH:MM:SS"
                  :disabled="adminForm.loading"
                  @blur="normalizeAdminSegment(cardIndex, segmentIndex, 'endTime')"
                />
                <button
                  type="button"
                  class="btn btn-sm btn-danger segment-remove-btn"
                  :disabled="adminForm.loading"
                  @click="removeAdminSegment(cardIndex, segmentIndex)"
                >
                  <i class="ti-close"></i>
                </button>
              </div>

              <div class="row mt-3">
                <div class="col-12">
                  <button
                    type="button"
                    class="add-segment-btn"
                    @click="addAdminSegment(cardIndex)"
                    :disabled="adminForm.loading || !getAdminUrlValidation(adminForm).valid || !getAdminSegmentsValidation(adminForm).valid"
                  >
                    <i class="ti-plus mr-1"></i>
                    {{ tt("makeClips.addSegment", "Añadir segmento") }}
                  </button>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col-12 text-center">
                  <small class="text-muted d-block">
                    Tiempo total cortado: {{ formatMinutes(getAdminTotalCutSeconds(adminForm)) }} min
                  </small>
                  <small v-if="!getAdminSegmentsValidation(adminForm).valid" class="text-danger d-block mt-1">
                    {{ getAdminSegmentsValidation(adminForm).message }}
                  </small>
                </div>
              </div>
            </div>
          </section>

          <section class="submit-section">
            <div class="submit-wrapper">
              <p v-if="adminForm.successMessage" class="submit-hint admin-success-hint">
                <i class="ti-check"></i>
                {{ adminForm.successMessage }}
              </p>
              <p v-if="adminForm.errorMessage" class="submit-hint admin-error-hint">
                <i class="ti-alert"></i>
                {{ adminForm.errorMessage }}
              </p>
            </div>
          </section>
        </div>
      </card>
    </div>

    <card
      v-if="isAdminMultiView && adminForms.length > 1 && !isLibraryFocused"
      class="card admin-submit-card"
    >
      <div class="card-content admin-submit-card-content">
        <div v-if="canAssignOnCreate" class="assign-target-wrap">
          <button
            type="button"
            class="assign-target-btn"
            :disabled="adminBulkSubmitting"
            @click="openAssignModal"
          >
            <i class="ti-user mr-1"></i>
            {{ tt("common.assign", "Asignar") }}
          </button>
          <span v-if="selectedAssignee" class="assign-target-user">
            {{ selectedAssignee.username }}
          </span>
        </div>
        <p-button
          type="info"
          round
          @click.native="openSubmitModal"
          :disabled="adminBulkSubmitting || !hasRequiredAssignee"
          class="submit-btn"
          :title="!hasRequiredAssignee ? tt('makeClips.selectAssigneeFirst', 'Debes seleccionar un usuario en Asignar antes de crear clips') : tt('makeClips.startCreateClips', 'Comenzar a crear clips de tu video')"
        >
          <i class="ti-video-camera mr-1" v-if="!adminBulkSubmitting"></i>
          <i class="ti-reload rotating mr-1" v-if="adminBulkSubmitting"></i>
          {{ adminBulkSubmitting ? tt("makeClips.queueingClips", "Encolando...") : tt("makeClips.createClips", "Crear clips") }}
        </p-button>
        <p v-if="adminBulkSuccessMessage" class="submit-hint admin-success-hint">
          <i class="ti-check"></i>
          {{ adminBulkSuccessMessage }}
        </p>
        <p v-if="adminBulkErrorMessage" class="submit-hint admin-error-hint">
          <i class="ti-alert"></i>
          {{ adminBulkErrorMessage }}
        </p>
      </div>
    </card>

    <section
      v-show="!isCreateFlowStepsActive"
      ref="makeClipsLibrarySection"
      class="make-clips-library-section"
      :class="{
        'make-clips-library-section--flow-top': isFlowSlotLocked,
        'make-clips-library-section--focused': isLibraryFocused,
      }"
      :style="makeClipsLibraryGapStyle"
    >
      <make-clips-schedule-library
        v-if="shouldMountLibraryEmbed"
        ref="makeClipsScheduleEmbed"
        class="make-clips-schedule-embed"
        @collection-focus-change="handleLibraryFocusChange"
        @collection-deleted="handleCollectionDeleted"
        @collection-insufficient-tokens-click="handleCollectionInsufficientTokensClick"
        @clip-schedule-click="handleScheduleClipClick"
        @clip-editor-visibility-change="handleLibraryEditorVisibilityChange"
      />
    </section>

    <transition name="modal-fade">
      <body-portal v-if="showSubmitModal && isAdminBulkModal">
        <div
          class="confirm-modal-overlay"
          @click.self="closeSubmitModal"
        >
        <div
          class="confirm-modal"
          :class="['confirm-modal--create', { 'confirm-modal--bulk': isAdminBulkModal }]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-submit-title"
        >
          <div class="confirm-modal-header">
            <h3 id="confirm-submit-title" class="confirm-modal-title">{{ tt("makeClips.confirmCreateTitle", "Confirmar creación de clips") }}</h3>
            <button
              type="button"
              class="confirm-modal-close"
              :aria-label="tt('common.close', 'Cerrar')"
              @click="closeSubmitModal"
            >
              <i class="ti-close"></i>
            </button>
          </div>

          <div class="confirm-modal-body">
            <template v-if="isAdminBulkModal">
              <div v-if="adminBulkMetadataLoading" class="confirm-modal-state">
                <div class="confirm-modal-spinner"></div>
                <span>{{ tt("makeClips.loadingVodInfo", "Obteniendo información de los VODs...") }}</span>
              </div>
              <div v-else-if="adminBulkMetadataError" class="confirm-modal-state confirm-modal-state-error">
                <i class="ti-alert-circle"></i>
                <span>{{ adminBulkMetadataError }}</span>
              </div>
              <div v-else-if="adminBulkPreviewItems.length > 0" class="confirm-modal-content confirm-modal-content--bulk">
                <div class="confirm-bulk-left">
                  <div v-if="canAssignOnCreate" class="confirm-assignee-card">
                    <div class="confirm-assignee-header">
                      <i class="ti-user"></i>
                      <span>{{ tt("makeClips.assignedUser", "Usuario asignado") }}</span>
                    </div>
                    <div class="confirm-assignee-body">
                      <span v-if="selectedAssignee && selectedAssignee.username" class="confirm-assignee-name">
                        {{ selectedAssignee.username }}
                      </span>
                      <span v-else class="confirm-assignee-empty">
                        {{ tt("makeClips.unassignedCurrentUser", "Sin reasignar (se crea para tu usuario actual)") }}
                      </span>
                    </div>
                  </div>
                  <div
                    class="confirm-token-card"
                    :class="{ 'confirm-token-card--insufficient': modalHasInsufficientTokens }"
                  >
                    <div class="confirm-token-header">
                      <i class="fa-solid fa-coins" aria-hidden="true"></i>
                      <div class="confirm-token-header-copy">
                        <span class="confirm-token-header-title">{{ tt("makeClips.tokensSummary", "Resumen de tokens") }}</span>
                        <small class="confirm-token-header-rate">{{ tt("makeClips.tokenMinuteRate", "1 token = 1 minuto de render") }}</small>
                      </div>
                    </div>
                    <div class="confirm-token-body">
                      <div class="confirm-token-row">
                        <span>{{ tt("makeClips.vodsToProcess", "VODs a procesar") }}</span>
                        <span class="confirm-token-balance">{{ adminBulkPreviewItems.length }}</span>
                      </div>
                      <div class="confirm-token-row">
                        <span>{{ tt("makeClips.totalTime", "Tiempo total") }}</span>
                        <span class="confirm-token-balance">{{ formatDuration(modalProcessingDurationSeconds) }}</span>
                      </div>
                      <div class="confirm-token-row">
                        <span>{{ tt("makeClips.totalEstimatedCost", "Costo estimado total") }}</span>
                        <span class="confirm-token-cost">{{ modalEstimatedTokenCost }} {{ modalEstimatedTokenCost === 1 ? tt("makeClips.tokenSingular", "token") : tt("makeClips.tokenPlural", "tokens") }}</span>
                      </div>
                      <div class="confirm-token-row">
                        <span>{{ tt("makeClips.yourBalance", "Tu saldo") }}</span>
                        <span class="confirm-token-balance">{{ formattedUserCreditsRemaining }}</span>
                      </div>
                      <div class="confirm-token-divider"></div>
                      <div class="confirm-token-row confirm-token-row--result">
                        <span>{{ tt("makeClips.remainingAfter", "Quedarían") }}</span>
                        <span
                          class="confirm-token-remaining"
                          :class="{ 'confirm-token-remaining--negative': modalHasInsufficientTokens }"
                        >
                          {{ formattedModalEstimatedRemainingTokens }} {{ tt("makeClips.tokenPlural", "tokens") }}
                        </span>
                      </div>
                    </div>
                    <div v-if="modalHasInsufficientTokens" class="confirm-token-alert">
                      <div class="confirm-token-alert-message">
                        <i class="ti-alert-circle"></i>
                        <span>{{ tt("makeClips.notEnoughTokensForVods", "No tienes tokens suficientes para procesar estos VODs.") }}</span>
                      </div>
                      <button
                        type="button"
                        class="confirm-token-alert-cta"
                        @click="goToBillingPacks"
                      >
                        {{ tt("makeClips.buyTokens", "Comprar tokens") }}
                      </button>
                    </div>
                    <p v-if="modalHasInsufficientTokens" class="confirm-token-insufficient-note">
                      {{ tt("makeClips.insufficientTokensWithClipSuggestion", "No tienes tokens suficientes. Puedes adquirir más tokens o recortar un segmento de tu stream usando la herramienta de recorte debajo de \"Enter video url\".") }}
                    </p>
                  </div>
                </div>

                <div class="confirm-vods-summary">
                  <div
                    v-for="item in adminBulkPreviewItems"
                    :key="`bulk-preview-${item.formIndex}`"
                    class="confirm-vod-summary-item"
                  >
                    <div class="confirm-video-card">
                      <div class="confirm-video-thumb-wrap">
                        <img
                          v-if="item.metadata.thumbnail"
                          :src="item.metadata.thumbnail"
                          alt="Miniatura"
                          class="confirm-video-thumb"
                        />
                        <div v-else class="confirm-video-thumb-placeholder">
                          <i class="ti-video-camera"></i>
                        </div>
                        <div class="confirm-video-duration-badge">
                          {{ formatDuration(item.metadata.duration) }}
                        </div>
                      </div>
                      <div class="confirm-video-info">
                        <h4 class="confirm-video-title">{{ formatBulkVodTitle(item.metadata.title) }}</h4>
                        <div class="confirm-video-meta">
                          <span class="confirm-video-meta-item">
                            <i class="ti-user"></i>
                            {{ item.metadata.user || "--" }}
                          </span>
                          <span class="confirm-video-meta-item">
                            <i class="ti-eye"></i>
                            {{ formatCount(item.metadata.view_count) }} vistas
                          </span>
                          <span class="confirm-video-meta-item">
                            <i class="ti-calendar"></i>
                            {{ formatUploadDate(item.metadata.upload_date) }}
                          </span>
                        </div>
                        <div class="confirm-video-type-pill" v-if="item.videoType">
                          {{ tt("makeClips.videoTypeLabel", "Tipo de video") }}: {{ getVideoTypeLabel(item.videoType) }}
                        </div>
                      </div>
                    </div>

                    <div class="confirm-video-type-card confirm-video-type-card--compact">
                      <div class="confirm-video-type-header">
                        <i class="ti-video-camera"></i>
                        <span>{{ tt("makeClips.videoTypeLabel", "Tipo de video") }}</span>
                      </div>
                      <div
                        class="confirm-video-type-grid confirm-video-type-grid--compact"
                        role="radiogroup"
                        :aria-label="tt('makeClips.videoTypeLabel', 'Tipo de video')"
                      >
                        <button
                          v-for="option in videoTypeOptions"
                          :key="`bulk-video-type-${item.formIndex}-${option.value}`"
                          type="button"
                          class="confirm-video-type-option"
                          :class="{ 'confirm-video-type-option--active': item.videoType === option.value }"
                          :aria-pressed="item.videoType === option.value"
                          :disabled="adminBulkSubmitting"
                          @click="setAdminBulkVideoType(item, option.value)"
                        >
                          <span class="confirm-video-type-option-shine" aria-hidden="true"></span>
                          <span class="confirm-video-type-option-icon">
                            <LayoutTypeIcon :type="option.value" />
                          </span>
                          <span class="confirm-video-type-option-copy">
                            <strong>{{ tt(option.labelKey, option.fallback) }}</strong>
                            <small>{{ tt(option.descriptionKey, option.descriptionFallback) }}</small>
                          </span>
                          <span class="confirm-video-type-option-check">
                            <i class="ti-check"></i>
                          </span>
                        </button>
                      </div>
                    </div>

                    <div v-if="item.segments.length > 0" class="confirm-segments-card">
                      <div class="confirm-segments-header">
                        <i class="ti-cut"></i>
                        <span>{{ tt("makeClips.selectedSegments", "Segmentos seleccionados") }}</span>
                      </div>
                      <div class="confirm-segments-list">
                        <div
                          v-for="(segment, index) in item.segments"
                          :key="`bulk-segment-${item.formIndex}-${segment.id || index}`"
                          class="confirm-segment-item"
                        >
                          <div class="confirm-segment-main">
                            <span class="confirm-segment-index">#{{ index + 1 }}</span>
                            <span class="confirm-segment-range">
                              {{ formatTime(segment.start) }} - {{ formatTime(segment.end) }}
                            </span>
                          </div>
                          <span class="confirm-segment-duration">
                            {{ formatDuration(getSegmentDurationSeconds(segment)) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <label class="confirm-video-context-toggle make-flow-subtitles-toggle">
                      <input
                        v-model="item.subtitlesEnabled"
                        type="checkbox"
                        :disabled="adminBulkSubmitting"
                        @change="setAdminBulkSubtitles(item)"
                      >
                      <span class="confirm-video-context-switch"></span>
                      <span class="confirm-video-context-toggle-label">
                        {{ tt("makeClips.enableSubtitles", "Agregar subtítulos") }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </template>

          </div>

          <div class="confirm-modal-footer">
            <button
              type="button"
              class="confirm-modal-btn confirm-modal-btn--secondary"
              :disabled="loading || modalMetadataLoading || adminBulkSubmitting"
              @click="closeSubmitModal"
            >
              {{ tt("usersList.cancel", "Cancelar") }}
            </button>
            <button
              type="button"
              class="confirm-modal-btn confirm-modal-btn--primary"
              :disabled="isModalConfirmDisabled"
              @click="confirmSubmitJob"
            >
              <span v-if="loading || adminBulkSubmitting" class="make-flow-queue-spinner" aria-hidden="true">
                <span class="make-flow-queue-spinner-aura"></span>
                <span class="make-flow-queue-spinner-ring make-flow-queue-spinner-ring--outer"></span>
                <span class="make-flow-queue-spinner-ring make-flow-queue-spinner-ring--inner"></span>
                <span class="make-flow-queue-spinner-orbit"></span>
              </span>
              <i v-else class="fa-solid fa-clapperboard" aria-hidden="true"></i>
              {{ loading || adminBulkSubmitting ? tt("makeClips.inQueue", "En cola...") : tt("makeClips.confirmAndCreate", "Crear clips") }}
            </button>
          </div>
        </div>
        </div>
      </body-portal>
    </transition>

    <body-portal>
      <div
        class="modal fade assign-modal"
        :class="{ show: showAssignModal }"
        :style="{ display: showAssignModal ? 'flex' : 'none' }"
        tabindex="-1"
        role="dialog"
        @click.self="closeAssignModal"
      >
        <div class="modal-dialog modal-lg assign-modal-dialog" role="document">
          <div class="modal-content assign-modal-content">
            <div class="modal-header assign-modal-header">
              <div class="assign-modal-header-icon">
                <i class="ti-user"></i>
              </div>
              <div class="assign-modal-header-inner">
                <div class="assign-modal-title-wrap">
                  <h5 class="modal-title assign-modal-title">{{ tt("makeClips.assignCreateTitle", "Asignar creación de clips") }}</h5>
                  <p class="assign-modal-subtitle">{{ tt("makeClips.assignCreateSubtitle", "Selecciona el usuario al que se asignará este VOD al crearlo.") }}</p>
                </div>
              </div>
              <button type="button" class="assign-modal-close" @click="closeAssignModal">
                <i class="ti-close"></i>
              </button>
            </div>
            <div class="modal-body assign-modal-body">
              <div v-if="assignUsersLoading" class="assign-modal-state">
                <i class="ti-reload ti-spin assign-modal-spinner"></i>
                <span>{{ tt("usersList.loadingUsers", "Cargando usuarios...") }}</span>
              </div>
              <div v-else-if="assignUsers.length === 0" class="assign-modal-state assign-modal-empty">
                <i class="ti-user"></i>
                <span>{{ tt("makeClips.noAssignableUsers", "No hay usuarios disponibles con acceso de Twitch.") }}</span>
              </div>
              <div v-else>
                <div class="assign-user-scope-toggle">
                  <button
                    type="button"
                    class="assign-user-scope-btn"
                    :class="{ active: assignUserScope === 'mine' }"
                    @click="setAssignUserScope('mine')"
                  >
                    {{ tt("twitchPostAdmin.showMyUsers", "Mostrar mis usuarios") }}
                  </button>
                  <button
                    type="button"
                    class="assign-user-scope-btn"
                    :class="{ active: assignUserScope === 'all' }"
                    @click="setAssignUserScope('all')"
                  >
                    {{ tt("twitchPostAdmin.showAllUsers", "Mostrar todos los usuarios") }}
                  </button>
                </div>
                <div class="assign-user-search-wrap">
                  <i class="ti-search"></i>
                  <input
                    v-model="assignUserNameFilter"
                    type="text"
                    class="assign-user-search-input"
                    :placeholder="tt('common.filterByNameOrEmail', 'Filtrar por nombre o correo')"
                  />
                </div>
                <div v-if="filteredAssignUsers.length === 0" class="assign-modal-state assign-modal-empty">
                  <i class="ti-search"></i>
                  <span>{{ tt("usersList.noUsersFound", "No se encontraron usuarios.") }}</span>
                </div>
                <div v-else class="assign-users-list">
                  <div
                    v-for="user in filteredAssignUsers"
                    :key="`assign-user-${user.id}`"
                    class="assign-user-row"
                  >
                    <span class="assign-user-avatar">{{ getAssignUserInitial(user) }}</span>
                    <span class="assign-user-copy">
                      <span class="assign-user-name">{{ user.username || "N/A" }}</span>
                      <small>{{ user.email }}</small>
                    </span>
                    <button
                      type="button"
                      class="assign-user-btn"
                      :disabled="loading"
                      @click="selectAssignee(user)"
                    >
                      <i class="ti-check"></i>
                      {{ tt("common.assign", "Asignar") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer assign-modal-footer">
              <button
                type="button"
                class="assign-modal-btn assign-modal-btn--secondary"
                @click="closeAssignModal"
              >
                {{ tt("makeClips.close", "Cerrar") }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showAssignModal"
        class="modal-backdrop fade show"
        @click="closeAssignModal"
      ></div>
      </body-portal>
      <schedule-subscription-prompt-modal
        v-model="showScheduleSubscriptionPromptModal"
        :has-active-plan="scheduleSubscriptionHasActivePlan"
        @close="closeScheduleSubscriptionPromptModal"
        @subscribe="openSubscriptionFromSchedulePrompt"
      />
      <subtitle-style-modal
        :visible="subtitleStyleModalOpen"
        :saving="subtitleStyleSaving"
        :value="subtitleStyle"
        @close="closeSubtitleStyleModal"
        @save="saveSubtitleStyle"
      />
    </div>

  </div>
</template>

<script>
import { supabase } from "@/supabase";
import { getEffectiveUserId, isImpersonating } from "@/utils/impersonation";
import escapeToCloseMixin from "@/mixins/escapeToCloseMixin";
import LayoutTypeIcon from "@/components/common/LayoutTypeIcon.vue";
import ScheduleSubscriptionPromptModal from "@/components/common/ScheduleSubscriptionPromptModal.vue";
import SubtitleStyleModal from "@/components/clipr/SubtitleStyleModal.vue";
import MakeClipsScheduleLibrary from "@/components/make-clips/MakeClipsScheduleLibrary.vue";
import MakeClipsSegmentsTimeline from "@/components/make-clips/MakeClipsSegmentsTimeline.vue";
import MakeClipsAnimatedUrlPlaceholder from "@/components/make-clips/MakeClipsAnimatedUrlPlaceholder.vue";
import TrySourceDownload from "@/components/try/TrySourceDownload.vue";
import TryDownloadAnimation from "@/components/try/TryDownloadAnimation.vue";
import FirstFreeStreamBanner from "@/components/make-clips/FirstFreeStreamBanner.vue";
import { useCurrentUserStore } from "@/stores/currentUser";
import { syncModalScrollLockState } from "@/utils/modalScrollLock";
import {
  CREATE_CLIPS_EDITOR_BACK_EVENT,
  CREATE_CLIPS_SIDEBAR_RESET_EVENT,
} from "@/config/navRoutes";
import { fetchAssignableUsers, updateSubtitlesEnabled } from "@/api/users";
import {
  completeTwitchUpload,
  createTwitchJob,
  createTwitchJobsBatch,
  createTwitchUploadSession,
  fetchTwitchUploadState,
  fetchTwitchJobs,
  fetchTwitchMetadataPreview,
  fetchUserIdByUserInfoId,
  reassignTwitchJob,
  uploadTwitchFileChunk,
} from "@/api/twitch";
import facebookIcon from "@/assets/img/platform-icons/facebook.png";
import googleDriveIcon from "@/assets/img/platform-icons/google-drive.png";
import kickIcon from "@/assets/img/platform-icons/kick.png";
import twitchIcon from "@/assets/img/platform-icons/twitch.png";
import youtubeIcon from "@/assets/img/platform-icons/youtube.png";

const SOURCE_TYPE_ICON_URLS = {
  twitch: twitchIcon,
  youtube: youtubeIcon,
  facebook: facebookIcon,
  kicks: kickIcon,
  google_drive: googleDriveIcon,
};

const PLATFORM_ICON_URLS = [
  SOURCE_TYPE_ICON_URLS.twitch,
  SOURCE_TYPE_ICON_URLS.youtube,
  SOURCE_TYPE_ICON_URLS.kicks,
  SOURCE_TYPE_ICON_URLS.google_drive,
  SOURCE_TYPE_ICON_URLS.facebook,
];

const SOURCE_TYPE_URL_EXAMPLES = {
  youtube: "https://www.youtube.com/watch?v=Rk9mN2pQx7A",
  twitch: "https://www.twitch.tv/videos/1987654321",
  kicks: "https://kick.com/streamzone/videos/7d2c3f41-0a9d-4fbb-bf6d-29e5a61a7430",
  facebook: "https://www.facebook.com/CliprLatam/videos/2456789012345678",
  google_drive: "https://drive.google.com/file/d/1Q2w3E4r5T6y7U8i9O0pA1sD2fG3hJ4kL/view",
};

const FLOW_VALIDATION_MIN_MS = 1500;
const FLOW_VALIDATION_DEBOUNCE_MS = 650;
const FLOW_TYPE_CHOOSE_MOVE_MS = 860;
const FLOW_TYPE_CHOOSE_LEAVE_MS = 380;
const MAKE_CLIPS_PROCESSING_STORAGE_KEY = "clipr_make_clips_processing";
const MP4_MAX_SIZE_BYTES = 107374182400;
const MP4_MAX_DURATION_SECONDS = 43200;
const MP4_MAX_HEIGHT = 1080;
const UPLOAD_POLL_INTERVAL_MS = 2000;

const PROCESSING_ETA_PROFILES = {
  irl: { prepSecondsPerMinute: 2.0, renderSecondsPerClip: 17.0, minutesPerPlannedClip: 8.0 },
  just_chatting: { prepSecondsPerMinute: 2.2, renderSecondsPerClip: 19.0, minutesPerPlannedClip: 7.0 },
  gameplay: { prepSecondsPerMinute: 2.4, renderSecondsPerClip: 20.0, minutesPerPlannedClip: 6.0 },
  podcast: { prepSecondsPerMinute: 2.3, renderSecondsPerClip: 21.8, minutesPerPlannedClip: 8.0 },
};

export default {
  mixins: [escapeToCloseMixin],
  components: {
    LayoutTypeIcon,
    ScheduleSubscriptionPromptModal,
    SubtitleStyleModal,
    MakeClipsScheduleLibrary,
    MakeClipsSegmentsTimeline,
    MakeClipsAnimatedUrlPlaceholder,
    TrySourceDownload,
    TryDownloadAnimation,
    FirstFreeStreamBanner,
  },
  async mounted() {
    this.startFirstFreeStreamCountdown();
    this.setupLibraryEmbedLazyMount();
    this.openLibraryFromQueryShortcut();
    await this.resolveAssignPermissions();
    this.syncSubtitlesPreference();
    this.restoreProcessingSession();
    this.syncEmbeddedScheduleSpacing();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.syncEmbeddedScheduleSpacing);
      window.addEventListener(CREATE_CLIPS_SIDEBAR_RESET_EVENT, this.handleCreateClipsResetNav);
      window.addEventListener(CREATE_CLIPS_EDITOR_BACK_EVENT, this.handleCreateClipsEditorBackNav);
    }
  },
  data() {
    return {
      videoUrl: "",
      inputMode: "url",
      selectedFile: null,
      selectedFileObjectUrl: "",
      fileDropActive: false,
      uploadProgress: 0,
      uploadQueuePosition: null,
      uploadMessage: "",
      uploadPollTimerId: null,
      activeUploadSession: null,
      adminVideoType: "",
      loading: false,
      flowPhase: "url",
      flowUrlAutoKey: "",
      flowValidatedKey: "",
      flowValidationTimerId: null,
      urlValidationPending: false,
      flowValidationUiStartedAt: null,
      createFlowSubmitError: "",
      createFlowSuccessMessage: "",
      createFlowSuccessTimerId: null,
      processingJobId: null,
      processingJobStatus: "",
      processingPollTimerId: null,
      processingPollErrorCount: 0,
      processingStartedAt: null,
      processingEtaNowMs: Date.now(),
      processingEtaTimerId: null,
      processingSnapshot: null,
      segments: [],
      segmentsExpanded: false,
      editingSegmentIndex: null,
      nextSegmentId: 1,
      showSubmitModal: false,
      metadataPreviewLoading: false,
      metadataPreviewError: "",
      metadataPreview: null,
      isLibraryFocused: false,
      isLibraryEditorVisible: false,
      selectedLibraryCollection: null,
      userCreditsRemaining: null,
      userRole: null,
      canAssignOnCreate: false,
      subtitlesEnabled: true,
      subtitleStyleModalOpen: false,
      subtitleStyleSaving: false,
      subtitleStyle: {
        font: "DejaVu Sans",
        activeColor: "#FFF302",
        activeEffect: "text_color",
      },
      irlUserContextEnabled: false,
      userContext: "",
      selectedAssignee: null,
      showAssignModal: false,
      insufficientTokensToastShown: false,
      showScheduleSubscriptionPromptModal: false,
      scheduleSubscriptionHasActivePlan: false,
      pendingScheduleClipFromLibrary: null,
      isModalScrollLocked: false,
      modalLockWrapper: null,
      modalLockWrapperTransform: "",
      modalLockWrapperWebkitTransform: "",
      modalLockMainPanel: null,
      modalLockMainPanelOverflow: "",
      modalLockMainPanelScrollTop: 0,
      modalLockMainPanelTransform: "",
      modalLockMainPanelWebkitTransform: "",
      modalLockBodyOverflow: "",
      modalLockBodyPosition: "",
      modalLockBodyTop: "",
      modalLockBodyLeft: "",
      modalLockBodyRight: "",
      modalLockBodyWidth: "",
      modalLockBodyScrollY: 0,
      modalLockBodyIsFixed: false,
      assignUsers: [],
      assignUsersLoading: false,
      assignUserNameFilter: "",
      assignUserScope: "mine",
      adminForms: [
        {
          id: 1,
          videoUrl: "",
          videoType: "",
          subtitlesEnabled: true,
          segments: [],
          nextSegmentId: 1,
          selectedAssignee: null,
          loading: false,
          successMessage: "",
          errorMessage: "",
        },
      ],
      nextAdminFormId: 2,
      activeAssignFormIndex: null,
      adminBulkSubmitting: false,
      adminBulkSuccessMessage: "",
      adminBulkErrorMessage: "",
      embeddedTimelineGapPx: null,
      embeddedTimelineGapRafId: null,
      shouldMountLibraryEmbed: false,
      libraryEmbedIntersectionObserver: null,
      submitModalMode: "single",
      adminBulkMetadataLoading: false,
      adminBulkMetadataError: "",
      adminBulkPreviewItems: [],
      typeChoosing: false,
      typeLeaving: false,
      isConfirmVideoTypeChangeOpen: false,
      chosenTypeCardOffset: { x: 0, y: 0 },
      videoTypeOptions: [
        {
          value: "just_chatting",
          labelKey: "makeClips.videoTypeJustChatting",
          fallback: "Vertical centrado",
          descriptionKey: "makeClips.videoTypeJustChattingDescription",
          descriptionFallback: "Recorte vertical centrado.",
        },
        {
          value: "gameplay",
          labelKey: "makeClips.videoTypeGameplay",
          fallback: "Rostro + pantalla",
          descriptionKey: "makeClips.videoTypeGameplayDescription",
          descriptionFallback: "Rostro arriba y contenido principal.",
        },
        {
          value: "podcast",
          labelKey: "makeClips.videoTypePodcast",
          fallback: "Podcast",
          descriptionKey: "makeClips.videoTypePodcastDescription",
          descriptionFallback: "2 o más personas en pantalla.",
        },
        {
          value: "irl",
          labelKey: "makeClips.videoTypeIrl",
          fallback: "Escena amplia",
          descriptionKey: "makeClips.videoTypeIrlDescription",
          descriptionFallback: "Video horizontal en clip vertical.",
        },
      ],
      platformIconUrls: PLATFORM_ICON_URLS,
      firstFreeStreamNowMs: Date.now(),
      firstFreeStreamTimerId: null,
    };
  },
  computed: {
    currentUserStore() {
      return useCurrentUserStore();
    },
    firstFreeStreamAvailable() {
      return this.currentUserStore.firstFreeStreamAvailable === true;
    },
    firstFreeStreamDeadlineMs() {
      const deadlineIso = this.currentUserStore.firstFreeStreamDeadline;
      const parsedDeadlineMs =
        typeof deadlineIso === "string" ? Date.parse(deadlineIso) : NaN;
      if (!Number.isFinite(parsedDeadlineMs)) return null;
      return parsedDeadlineMs;
    },
    firstFreeStreamRemainingSeconds() {
      if (!Number.isFinite(this.firstFreeStreamDeadlineMs)) return 0;
      const remainingMs = this.firstFreeStreamDeadlineMs - this.firstFreeStreamNowMs;
      if (remainingMs <= 0) return 0;
      return Math.floor(remainingMs / 1000);
    },
    showFirstFreeStreamBanner() {
      const planTier = String(this.currentUserStore.planTier || "").trim().toLowerCase();
      if (!this.firstFreeStreamAvailable) return false;
      if (planTier !== "free") return false;
      return this.firstFreeStreamRemainingSeconds > 0;
    },
    firstFreeStreamCountdownText() {
      const totalSeconds = Math.max(0, this.firstFreeStreamRemainingSeconds);
      const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
      const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
      const seconds = String(totalSeconds % 60).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    },
    exceedsUnlimitedDailyStreamLimit() {
      if (!this.isUnlimitedPlanUser) return false;
      const duration = Number(this.processingDurationSeconds);
      if (!Number.isFinite(duration) || duration <= 0) return false;
      return duration > this.unlimitedDailyStreamLimitSeconds;
    },
    isUnlimitedPlanUser() {
      return String(this.currentUserStore.planTier || "").trim().toLowerCase() === "unlimited";
    },
    canUploadMp4() {
      return this.isUnlimitedPlanUser
        || this.currentUserStore.hasAdminAccess
        || isImpersonating();
    },
    unlimitedDailyStreamLimitSeconds() {
      return 12 * 60 * 60;
    },
    firstFreeStreamMaxDurationSeconds() {
      return 4 * 60 * 60;
    },
    qualifiesForFirstFreeStream() {
      if (!this.showFirstFreeStreamBanner) return false;
      const duration = Number(this.processingDurationSeconds);
      if (!Number.isFinite(duration) || duration <= 0) return false;
      return duration <= this.firstFreeStreamMaxDurationSeconds;
    },
    firstFreeStreamDurationExceeded() {
      if (!this.showFirstFreeStreamBanner) return false;
      const duration = Number(this.processingDurationSeconds);
      if (!Number.isFinite(duration) || duration <= 0) return false;
      return duration > this.firstFreeStreamMaxDurationSeconds;
    },
    isAdminMultiView() {
      return this.userRole === "admin" && this.canAssignOnCreate;
    },
    isAdminSingleView() {
      return this.isAdminMultiView && this.adminForms.length === 1;
    },
    isAdminBulkModal() {
      return this.submitModalMode === "adminBulk";
    },
    currentAdminUserId() {
      return useCurrentUserStore().currentUserId || "";
    },
    scopeFilteredAssignUsers() {
      if (this.assignUserScope !== "mine") return this.assignUsers;
      const currentAdminUserId = this.normalizeAssignValue(this.currentAdminUserId);
      if (!currentAdminUserId) return [];
      return this.assignUsers.filter(
        (user) => this.normalizeAssignValue(user?.assigned_admin) === currentAdminUserId
      );
    },
    filteredAssignUsers() {
      const users = this.scopeFilteredAssignUsers;
      if (!this.assignUserNameFilter.trim()) {
        return users;
      }
      const q = this.assignUserNameFilter.trim().toLowerCase();
      return users.filter(
        (user) =>
          (user?.username || "").toLowerCase().includes(q) ||
          (user?.email || "").toLowerCase().includes(q)
      );
    },
    escapeToCloseModals() {
      return [
        { show: "showAssignModal", close: "closeAssignModal" },
        { show: "showSubmitModal", close: "closeSubmitModal" },
        { show: "showScheduleSubscriptionPromptModal", close: "closeScheduleSubscriptionPromptModal" },
      ];
    },
    isAnyModalOpen() {
      return this.showAssignModal
        || this.showSubmitModal
        || this.showScheduleSubscriptionPromptModal;
    },
    autodetectedSourceType() {
      return this.detectSourceType(this.videoUrl);
    },
    isFacebookReelSource() {
      return this.autodetectedSourceType === "facebook" && this.isFacebookReelUrl(this.videoUrl);
    },
    detectedSourceLabel() {
      if (this.inputMode === "file") {
        return this.selectedFile?.name || "MP4";
      }
      const labels = {
        twitch: "Twitch",
        youtube: "YouTube",
        facebook: "Facebook",
        kicks: "Kick",
        google_drive: "Google Drive",
      };
      return labels[this.autodetectedSourceType] || this.tt("makeClips.unknownSource", "Desconocida");
    },
    sourceTypeIconUrl() {
      return this.getSourceTypeIconUrl(this.autodetectedSourceType);
    },
    urlValidation() {
      const raw = (this.videoUrl || "").trim();
      if (!raw) {
        return { valid: false, sourceType: null, message: "" };
      }

      const sourceType = this.detectSourceType(raw);
      if (!sourceType) {
        return {
          valid: false,
          sourceType: null,
          message: this.getUnrecognizedUrlErrorMessage(),
        };
      }

      const isValid = this.validateUrl(raw, sourceType);
      if (!isValid) {
        return {
          valid: false,
          sourceType,
          message: this.getUrlErrorBySource(sourceType),
        };
      }

      return { valid: true, sourceType, message: "" };
    },
    isValidUrl() {
      return this.urlValidation.valid;
    },
    isFlowValidating() {
      return this.flowPhase === "validating" || this.urlValidationPending;
    },
    isUrlFlowPhase() {
      return this.flowPhase === "url" || this.flowPhase === "validating";
    },
    isAdminRole() {
      return String(this.userRole || "").trim().toLowerCase() === "admin";
    },
    shouldShowProcessingAnimation() {
      return !this.isAdminRole;
    },
    isFlowSlotLocked() {
      const inProcessing = this.flowPhase === "processing" && this.shouldShowProcessingAnimation;
      return this.isUrlFlowPhase || inProcessing;
    },
    isCreateFlowStepsActive() {
      // Pasos 2 (configurar) y 3 (confirmar): ocultamos la biblioteca/colecciones
      // para que el usuario solo vea los pasos y no se confunda.
      return this.flowPhase === "configure" || this.flowPhase === "confirm";
    },
    videoDurationSeconds() {
      const duration = Number(this.metadataPreview?.duration);
      return Number.isFinite(duration) && duration > 0 ? duration : 0;
    },
    flowThumbnail() {
      return String(this.metadataPreview?.thumbnail || "");
    },
    flowVideoTitle() {
      return String(this.metadataPreview?.title || "");
    },
    flowDurationHint() {
      if (this.videoDurationSeconds <= 0) return "";
      return this.tt(
        "makeClips.flowDurationHint",
        "Duración: {duration}",
        { duration: this.formatDuration(this.videoDurationSeconds) },
      );
    },
    isProcessingRenderingPhase() {
      return this.isProcessingRenderingStatus(this.processingJobStatus);
    },
    processingRenderingTitle() {
      return this.tt("vodClips.statusRendering", "Renderizando");
    },
    processingThumbnail() {
      return this.processingSnapshot ? this.processingSnapshot.thumbnail : "";
    },
    processingTitle() {
      return this.processingSnapshot ? this.processingSnapshot.title : "";
    },
    processingIconUrl() {
      return this.processingSnapshot ? this.processingSnapshot.iconUrl : "";
    },
    processingLabel() {
      return this.processingSnapshot ? this.processingSnapshot.label : "";
    },
    processingSizeBytes() {
      const direct = Number(this.processingSnapshot?.sizeBytes) || 0;
      if (direct > 0) return direct;
      return this.estimateSourceSizeBytes(this.processingSnapshot?.durationSeconds);
    },
    processingEtaMessage() {
      void this.processingEtaNowMs;
      return this.buildProcessingEtaMessage();
    },
    flowSelectedTypeOption() {
      return this.videoTypeOptions.find((option) => option.value === this.adminVideoType) || null;
    },
    flowRequiresSegmentsForCost() {
      return !!this.metadataPreview
        && this.videoDurationSeconds <= 0
        && this.segments.length === 0;
    },
    canContinueToConfirm() {
      return this.hasSelectedVideoType
        && this.segmentsValidation.valid
        && this.processingDurationSeconds > 0;
    },
    isFlowConfirmDisabled() {
      return this.loading
        || !this.metadataPreview
        || !this.hasSelectedVideoType
        || !this.segmentsValidation.valid
        || !this.hasRequiredAssignee
        || this.processingDurationSeconds <= 0
        || this.firstFreeStreamDurationExceeded
        || this.exceedsUnlimitedDailyStreamLimit
        || this.hasInsufficientTokens;
    },
    urlErrorMessage() {
      return this.urlValidation.message;
    },
    urlErrorTitle() {
      const sourceType = this.urlValidation.sourceType;
      if (!sourceType) {
        return this.tt("tryClips.urlForm.errors.invalidUrlTitle", "Formato de URL inválido");
      }

      const titles = {
        twitch: this.tt("tryClips.urlForm.errors.invalidTwitchTitle", "Formato de Twitch inválido"),
        youtube: this.tt("tryClips.urlForm.errors.invalidYoutubeTitle", "Formato de YouTube inválido"),
        facebook: this.tt("tryClips.urlForm.errors.invalidFacebookTitle", "Formato de Facebook inválido"),
        kicks: this.tt("tryClips.urlForm.errors.invalidKickTitle", "Formato de Kick inválido"),
        google_drive: this.tt("tryClips.urlForm.errors.invalidGoogleDriveTitle", "Formato de Google Drive inválido"),
      };
      return titles[sourceType] || this.tt("tryClips.urlForm.errors.invalidUrlTitle", "Formato de URL inválido");
    },
    segmentsValidation() {
      return this.getSegmentsValidation();
    },
    totalCutSeconds() {
      if (!this.segments || this.segments.length === 0) return 0;
      return this.segments.reduce((total, segment) => {
        const duration = this.getSegmentDurationSeconds(segment);
        return duration ? total + duration : total;
      }, 0);
    },
    canAddMoreSegments() {
      if (!this.segments || this.segments.length === 0) return true;
      return this.getSegmentsValidation().valid;
    },
    requiresAssigneeOnCreate() {
      return this.canAssignOnCreate;
    },
    hasRequiredAssignee() {
      return !this.requiresAssigneeOnCreate || !!this.selectedAssignee;
    },
    processingDurationSeconds() {
      const segmentedDuration = Number(this.totalCutSeconds);
      if (this.segments?.length > 0 && Number.isFinite(segmentedDuration) && segmentedDuration > 0) {
        return segmentedDuration;
      }

      const fullDuration = Number(this.metadataPreview?.duration);
      if (!Number.isFinite(fullDuration) || fullDuration <= 0) return 0;
      return fullDuration;
    },
    isUsingSegmentsForCost() {
      return this.segments?.length > 0 && this.processingDurationSeconds === this.totalCutSeconds;
    },
    estimatedTokenCost() {
      if (this.qualifiesForFirstFreeStream) return 0;
      const duration = Number(this.processingDurationSeconds);
      if (!Number.isFinite(duration) || duration <= 0) return 0;
      return Math.ceil(duration / 60);
    },
    estimatedRemainingTokens() {
      if (!Number.isFinite(this.userCreditsRemaining)) return null;
      if (this.qualifiesForFirstFreeStream) return this.userCreditsRemaining;
      return this.userCreditsRemaining - this.estimatedTokenCost;
    },
    hasInsufficientTokens() {
      if (this.qualifiesForFirstFreeStream) return false;
      if (this.exceedsUnlimitedDailyStreamLimit) return false;
      return Number.isFinite(this.estimatedRemainingTokens) && this.estimatedRemainingTokens < 0;
    },
    formattedUserCreditsRemaining() {
      if (!Number.isFinite(this.userCreditsRemaining)) return "--";
      return this.userCreditsRemaining.toLocaleString("es-MX");
    },
    formattedEstimatedRemainingTokens() {
      if (!Number.isFinite(this.estimatedRemainingTokens)) return "--";
      return this.estimatedRemainingTokens.toLocaleString("es-MX");
    },
    adminBulkProcessingDurationSeconds() {
      return (this.adminBulkPreviewItems || []).reduce((total, item) => {
        const duration = Number(item?.processingDurationSeconds || 0);
        return Number.isFinite(duration) && duration > 0 ? total + duration : total;
      }, 0);
    },
    adminBulkEstimatedTokenCost() {
      return (this.adminBulkPreviewItems || []).reduce((total, item) => {
        const tokenCost = Number(item?.estimatedTokenCost || 0);
        return Number.isFinite(tokenCost) && tokenCost > 0 ? total + tokenCost : total;
      }, 0);
    },
    modalProcessingDurationSeconds() {
      return this.isAdminBulkModal
        ? this.adminBulkProcessingDurationSeconds
        : this.processingDurationSeconds;
    },
    modalEstimatedTokenCost() {
      return this.isAdminBulkModal
        ? this.adminBulkEstimatedTokenCost
        : this.estimatedTokenCost;
    },
    modalEstimatedRemainingTokens() {
      if (!Number.isFinite(this.userCreditsRemaining)) return null;
      return this.userCreditsRemaining - this.modalEstimatedTokenCost;
    },
    modalHasInsufficientTokens() {
      return Number.isFinite(this.modalEstimatedRemainingTokens) && this.modalEstimatedRemainingTokens < 0;
    },
    formattedModalEstimatedRemainingTokens() {
      if (!Number.isFinite(this.modalEstimatedRemainingTokens)) return "--";
      return this.modalEstimatedRemainingTokens.toLocaleString("es-MX");
    },
    modalMetadataLoading() {
      return this.isAdminBulkModal ? this.adminBulkMetadataLoading : this.metadataPreviewLoading;
    },
    hasSelectedVideoType() {
      return this.isKnownVideoType(this.adminVideoType);
    },
    hasSelectedAdminBulkVideoTypes() {
      if (!this.isAdminBulkModal) return true;
      return this.adminBulkPreviewItems.length > 0
        && this.adminBulkPreviewItems.every((item) => this.isKnownVideoType(item?.videoType));
    },
    isModalConfirmDisabled() {
      if (this.loading || this.modalMetadataLoading || this.modalHasInsufficientTokens || this.adminBulkSubmitting) {
        return true;
      }
      if (!this.hasRequiredAssignee) {
        return true;
      }
      if (this.isAdminBulkModal) {
        return !!this.adminBulkMetadataError || !this.hasSelectedAdminBulkVideoTypes;
      }
      if (!this.hasSelectedVideoType) {
        return true;
      }
      return !!this.metadataPreviewError || !this.metadataPreview;
    },
    makeClipsLibraryGapStyle() {
      if (!Number.isFinite(this.embeddedTimelineGapPx)) {
        return null;
      }
      return {
        "--make-clips-reference-gap": `${this.embeddedTimelineGapPx}px`,
      };
    },
  },
  beforeDestroy() {
    this.stopFirstFreeStreamCountdown();
    this.clearUploadPollTimer();
    this.revokeSelectedFileObjectUrl();
    this.clearFlowValidationTimer();
    this.clearCreateFlowSuccessTimer();
    this.clearProcessingPollTimer();
    this.stopProcessingEtaTimer();
    if (this.libraryEmbedIntersectionObserver) {
      this.libraryEmbedIntersectionObserver.disconnect();
      this.libraryEmbedIntersectionObserver = null;
    }
    this.syncModalScrollLock(false);
    document.removeEventListener("keydown", this.handleModalKeydown);
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.syncEmbeddedScheduleSpacing);
      window.removeEventListener(CREATE_CLIPS_SIDEBAR_RESET_EVENT, this.handleCreateClipsResetNav);
      window.removeEventListener(CREATE_CLIPS_EDITOR_BACK_EVENT, this.handleCreateClipsEditorBackNav);
      if (this.embeddedTimelineGapRafId != null) {
        window.cancelAnimationFrame(this.embeddedTimelineGapRafId);
        this.embeddedTimelineGapRafId = null;
      }
    }
  },
  watch: {
    $route() {
      if (!this.shouldMountLibraryEmbed && this.hasDeepCreateClipsRoute()) {
        this.mountLibraryEmbed();
      }
    },
    "$route.query.l"() {
      this.openLibraryFromQueryShortcut();
    },
    videoUrl() {
      if (this.inputMode !== "url") return;
      if (this.flowPhase !== "url") return;
      this.metadataPreviewError = "";
      this.clearFlowValidationTimer();
      if (!this.isValidUrl) {
        this.flowUrlAutoKey = "";
        this.clearUrlValidationUi();
        return;
      }
      this.markUrlValidationUiPending();
      this.flowValidationTimerId = setTimeout(() => {
        this.flowValidationTimerId = null;
        this.maybeStartUrlValidation();
      }, FLOW_VALIDATION_DEBOUNCE_MS);
    },
    canUploadMp4(canUpload) {
      if (!canUpload && this.inputMode === "file") {
        this.setInputMode("url");
      }
    },
    "$route.path"(nextPath) {
      const normalizedPath = String(nextPath || "").replace(/\/+$/, "") || "/";
      if (normalizedPath !== "/create-clips") return;
      this.handleCreateClipsResetNav();
    },
    isAnyModalOpen: {
      immediate: true,
      handler(isOpen) {
        this.syncModalScrollLock(isOpen);
      },
    },
    segments: {
      handler(newSegments) {
        if (newSegments && newSegments.length > 0) {
          this.segmentsExpanded = true;
        }
      },
      deep: true,
    },
    videoDurationSeconds() {
      // Cuando se conoce la duración del video, garantizar el segmento completo por defecto.
      this.ensureSingleFullSegment();
    },
    flowPhase(newPhase) {
      if (newPhase === "processing") {
        this.startProcessingEtaTimer();
      } else {
        this.stopProcessingEtaTimer();
      }
      if (newPhase === "configure" || newPhase === "confirm") {
        this.ensureSingleFullSegment();
      }
    },
    showSubmitModal(isOpen) {
      if (!isOpen) {
        this.insufficientTokensToastShown = false;
        return;
      }
      this.notifyInsufficientTokensInModal();
    },
    modalHasInsufficientTokens(hasInsufficientTokens) {
      if (!hasInsufficientTokens) return;
      this.notifyInsufficientTokensInModal();
    },
  },
  methods: {
    formatBytes(bytes) {
      const value = Number(bytes);
      if (!Number.isFinite(value) || value <= 0) return "0 B";
      const units = ["B", "KiB", "MiB", "GiB"];
      const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
      return `${(value / (1024 ** unitIndex)).toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
    },
    setInputMode(mode) {
      if (this.loading || this.isFlowValidating || this.metadataPreviewLoading || mode === this.inputMode) return;
      if (mode === "file" && !this.canUploadMp4) return;
      this.clearFlowValidationTimer();
      this.clearUrlValidationUi();
      this.clearUploadPollTimer();
      this.revokeSelectedFileObjectUrl();
      this.videoUrl = "";
      this.selectedFile = null;
      this.fileDropActive = false;
      this.metadataPreview = null;
      this.metadataPreviewError = "";
      this.metadataPreviewLoading = false;
      this.segments = [];
      this.nextSegmentId = 1;
      this.flowUrlAutoKey = "";
      this.flowValidatedKey = "";
      this.uploadProgress = 0;
      this.uploadQueuePosition = null;
      this.uploadMessage = "";
      this.activeUploadSession = null;
      this.createFlowSubmitError = "";
      this.inputMode = mode;
      if (this.$refs.mp4FileInput) {
        this.$refs.mp4FileInput.value = "";
      }
      this.$nextTick(() => {
        if (mode === "url") this.focusPrimaryVideoUrlInput();
      });
    },
    openFilePicker() {
      if (this.loading || this.metadataPreviewLoading) return;
      this.$refs.mp4FileInput?.click();
    },
    handleFileInputChange(event) {
      const file = event?.target?.files?.[0] || null;
      if (file) this.selectMp4File(file);
    },
    handleFileDrop(event) {
      this.fileDropActive = false;
      if (this.loading || this.metadataPreviewLoading) return;
      const file = event?.dataTransfer?.files?.[0] || null;
      if (file) this.selectMp4File(file);
    },
    async selectMp4File(file) {
      this.clearCreateFlowSuccessMessage();
      this.activeUploadSession = null;
      this.revokeSelectedFileObjectUrl();
      this.selectedFile = null;
      this.metadataPreview = null;
      this.metadataPreviewError = "";
      this.segments = [];
      this.nextSegmentId = 1;
      this.uploadProgress = 0;
      this.uploadQueuePosition = null;
      this.uploadMessage = "";
      this.metadataPreviewLoading = true;

      try {
        const extensionValid = /\.mp4$/i.test(String(file?.name || ""));
        const mimeType = String(file?.type || "").trim().toLowerCase();
        if (!extensionValid || (mimeType && mimeType !== "video/mp4")) {
          throw this.createUploadError("upload_invalid_mp4");
        }
        if (!Number.isFinite(file?.size) || file.size <= 0) {
          throw this.createUploadError("upload_invalid_mp4");
        }
        if (file.size > MP4_MAX_SIZE_BYTES) {
          throw this.createUploadError("upload_size_exceeded");
        }

        this.selectedFileObjectUrl = URL.createObjectURL(file);
        const metadata = await this.readVideoFileMetadata(this.selectedFileObjectUrl);
        if (metadata.duration > MP4_MAX_DURATION_SECONDS) {
          throw this.createUploadError("upload_duration_exceeded");
        }
        if (metadata.videoWidth <= metadata.videoHeight) {
          throw this.createUploadError("upload_vertical_not_supported");
        }
        if (metadata.videoHeight > MP4_MAX_HEIGHT) {
          throw this.createUploadError("upload_resolution_exceeded");
        }

        this.selectedFile = file;
        this.metadataPreview = {
          duration: metadata.duration,
          title: file.name,
          size_bytes: file.size,
          thumbnail: "",
          user: file.name,
          is_upload: true,
        };
        this.flowValidatedKey = `upload:${file.name}:${file.size}:${file.lastModified}`;
        this.flowPhase = "configure";
      } catch (error) {
        this.revokeSelectedFileObjectUrl();
        this.metadataPreviewError = this.getUploadErrorMessage(error);
        this.flowPhase = "url";
      } finally {
        this.metadataPreviewLoading = false;
      }
    },
    readVideoFileMetadata(objectUrl) {
      return new Promise((resolve, reject) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
          const duration = Number(video.duration);
          const videoWidth = Number(video.videoWidth);
          const videoHeight = Number(video.videoHeight);
          video.removeAttribute("src");
          video.load();
          if (
            !Number.isFinite(duration)
            || duration <= 0
            || !Number.isFinite(videoWidth)
            || !Number.isFinite(videoHeight)
            || videoWidth <= 0
            || videoHeight <= 0
          ) {
            reject(this.createUploadError("upload_invalid_mp4"));
            return;
          }
          resolve({ duration, videoWidth, videoHeight });
        };
        video.onerror = () => {
          video.removeAttribute("src");
          video.load();
          reject(this.createUploadError("upload_invalid_mp4"));
        };
        video.src = objectUrl;
      });
    },
    revokeSelectedFileObjectUrl() {
      if (!this.selectedFileObjectUrl) return;
      URL.revokeObjectURL(this.selectedFileObjectUrl);
      this.selectedFileObjectUrl = "";
    },
    resetUploadState() {
      this.clearUploadPollTimer();
      this.revokeSelectedFileObjectUrl();
      this.inputMode = "url";
      this.selectedFile = null;
      this.fileDropActive = false;
      this.uploadProgress = 0;
      this.uploadQueuePosition = null;
      this.uploadMessage = "";
      this.activeUploadSession = null;
      if (this.$refs.mp4FileInput) {
        this.$refs.mp4FileInput.value = "";
      }
    },
    createUploadError(code, message = "") {
      const error = new Error(message);
      error.code = code;
      return error;
    },
    getUploadErrorMessage(error) {
      const code = String(error?.code || "").trim();
      const messages = {
        upload_plan_required: ["makeClips.uploadPlanRequired", "La subida de MP4 requiere el plan Unlimited."],
        upload_already_active: ["makeClips.uploadAlreadyActive", "Ya tienes una subida MP4 activa."],
        upload_invalid_mp4: ["makeClips.uploadInvalidMp4", "Selecciona un archivo MP4 válido."],
        upload_vertical_not_supported: ["makeClips.uploadVerticalNotSupported", "El video es vertical. Solo se admiten MP4 horizontales."],
        upload_resolution_exceeded: ["makeClips.uploadResolutionExceeded", "La resolución supera 1080p."],
        upload_duration_exceeded: ["makeClips.uploadDurationExceeded", "El video supera el máximo de 12 horas."],
        upload_size_exceeded: ["makeClips.uploadSizeExceeded", "El archivo supera el máximo de 100 GiB."],
        upload_incomplete: ["makeClips.uploadIncomplete", "La subida está incompleta. Inténtalo de nuevo."],
        upload_waiting: ["makeClips.uploadWaiting", "La subida sigue esperando disponibilidad."],
      };
      if (messages[code]) {
        return this.tt(messages[code][0], messages[code][1]);
      }
      return error?.message || this.tt("makeClips.uploadFailed", "No se pudo subir el MP4.");
    },
    clearUploadPollTimer() {
      if (this.uploadPollTimerId == null) return;
      clearTimeout(this.uploadPollTimerId);
      this.uploadPollTimerId = null;
    },
    waitForUploadPoll() {
      this.clearUploadPollTimer();
      return new Promise((resolve) => {
        this.uploadPollTimerId = setTimeout(() => {
          this.uploadPollTimerId = null;
          resolve();
        }, UPLOAD_POLL_INTERVAL_MS);
      });
    },
    startFirstFreeStreamCountdown() {
      if (typeof window === "undefined") return;
      this.stopFirstFreeStreamCountdown();
      this.firstFreeStreamNowMs = Date.now();
      this.firstFreeStreamTimerId = window.setInterval(() => {
        this.firstFreeStreamNowMs = Date.now();
      }, 1000);
    },
    stopFirstFreeStreamCountdown() {
      if (!this.firstFreeStreamTimerId) return;
      window.clearInterval(this.firstFreeStreamTimerId);
      this.firstFreeStreamTimerId = null;
    },
    focusPrimaryVideoUrlInput(attempt = 0) {
      if (this.flowPhase !== "url" && this.flowPhase !== "validating") {
        this.flowPhase = "url";
        this.$nextTick(() => this.focusPrimaryVideoUrlInput());
        return;
      }
      const input = this.$refs.primaryVideoUrlInput;
      if (!input || typeof input.focus !== "function") {
        // Con la transición out-in el input aún no está en el DOM; reintentar brevemente.
        if (attempt < 5) {
          setTimeout(() => this.focusPrimaryVideoUrlInput(attempt + 1), 140);
        }
        return;
      }
      if (typeof input.scrollIntoView === "function") {
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      try {
        input.focus({ preventScroll: true });
      } catch (error) {
        input.focus();
      }
    },
    hasDeepCreateClipsRoute() {
      const routeParams = this.$route?.params || {};
      const routeQuery = this.$route?.query || {};
      const jobIdRaw = routeParams.jobId;
      const clipIdRaw = routeParams.clipId;
      const queryJobIdRaw = routeQuery.job_id != null ? routeQuery.job_id : routeQuery.jobId;
      const normalize = (value) => {
        const raw = Array.isArray(value) ? value[0] : value;
        return typeof raw === "string" ? raw.trim() : "";
      };
      return Boolean(normalize(jobIdRaw) || normalize(clipIdRaw) || normalize(queryJobIdRaw));
    },
    mountLibraryEmbed() {
      if (this.libraryEmbedIntersectionObserver) {
        this.libraryEmbedIntersectionObserver.disconnect();
        this.libraryEmbedIntersectionObserver = null;
      }
      if (this.shouldMountLibraryEmbed) return;
      this.shouldMountLibraryEmbed = true;
      this.$nextTick(() => this.syncEmbeddedScheduleSpacing());
    },
    setupLibraryEmbedLazyMount() {
      // Rutas profundas (/create-clips/:jobId o editor) necesitan la biblioteca de inmediato.
      if (
        this.hasDeepCreateClipsRoute()
        || typeof window === "undefined"
        || typeof window.IntersectionObserver !== "function"
      ) {
        this.mountLibraryEmbed();
        return;
      }
      const librarySection = this.$refs.makeClipsLibrarySection;
      if (!librarySection) {
        this.mountLibraryEmbed();
        return;
      }
      this.libraryEmbedIntersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.mountLibraryEmbed();
          }
        },
        { rootMargin: "600px 0px" }
      );
      this.libraryEmbedIntersectionObserver.observe(librarySection);
    },
    handleCreateClipsResetNav() {
      const scheduleEmbed = this.$refs.makeClipsScheduleEmbed;
      if (scheduleEmbed && typeof scheduleEmbed.resetEmbeddedSelection === "function") {
        scheduleEmbed.resetEmbeddedSelection();
      }
      this.isLibraryFocused = false;
      this.isLibraryEditorVisible = false;
      this.selectedLibraryCollection = null;
      this.syncEmbeddedScheduleSpacing();
    },
    openLibraryFromQueryShortcut() {
      if (this.$route.query.l !== "1") return;
      this.mountLibraryEmbed();
      this.isLibraryFocused = true;
      this.$nextTick(() => {
        this.$refs.makeClipsLibrarySection.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    handleCreateClipsEditorBackNav() {
      const scheduleEmbed = this.$refs.makeClipsScheduleEmbed;
      if (scheduleEmbed && typeof scheduleEmbed.closeEmbeddedEditor === "function") {
        scheduleEmbed.closeEmbeddedEditor();
      }
      this.isLibraryEditorVisible = false;
      this.syncEmbeddedScheduleSpacing();
    },
    getEmbeddedScheduleGapFallback() {
      const fallbackPx = 16;
      const librarySection = this.$refs.makeClipsLibrarySection;
      if (!librarySection || typeof window === "undefined" || typeof window.getComputedStyle !== "function") {
        return fallbackPx;
      }
      const rawGap = window.getComputedStyle(librarySection).getPropertyValue("--make-clips-schedule-gap");
      const parsedGap = Number.parseFloat(rawGap);
      return Number.isFinite(parsedGap) ? parsedGap : fallbackPx;
    },
    syncEmbeddedScheduleSpacing() {
      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") return;
      if (this.embeddedTimelineGapRafId != null) {
        window.cancelAnimationFrame(this.embeddedTimelineGapRafId);
      }
      this.embeddedTimelineGapRafId = window.requestAnimationFrame(() => {
        this.embeddedTimelineGapRafId = null;
        this.$nextTick(() => {
          const fallbackGap = this.getEmbeddedScheduleGapFallback();
          const submitSection = this.$refs.submitSectionPrimary;
          const scheduleEmbed = this.$refs.makeClipsScheduleEmbed;
          const scheduleEmbedRoot = scheduleEmbed && scheduleEmbed.$el ? scheduleEmbed.$el : null;
          const batchesRow = scheduleEmbedRoot
            ? scheduleEmbedRoot.querySelector(".schedule-page--embedded .schedule-row.schedule-row-batches")
            : null;

          if (!submitSection || !batchesRow) {
            this.embeddedTimelineGapPx = fallbackGap;
            return;
          }

          const submitRect = submitSection.getBoundingClientRect();
          const batchesRect = batchesRow.getBoundingClientRect();
          const measuredGap = batchesRect.top - submitRect.bottom;
          if (!Number.isFinite(measuredGap)) {
            this.embeddedTimelineGapPx = fallbackGap;
            return;
          }

          this.embeddedTimelineGapPx = Math.max(0, Math.round(measuredGap * 100) / 100);
        });
      });
    },
    handleLibraryFocusChange(payload) {
      const isPayloadObject = payload && typeof payload === "object" && !Array.isArray(payload);
      const isFocused = isPayloadObject ? payload.isFocused : payload;
      this.isLibraryFocused = !!isFocused;

      const nextSelectedCollection = isPayloadObject ? payload.selectedCollection : null;
      this.selectedLibraryCollection = nextSelectedCollection || null;
      if (!this.isLibraryFocused) {
        this.selectedLibraryCollection = null;
        this.isLibraryEditorVisible = false;
      }
      this.syncEmbeddedScheduleSpacing();
    },
    handleCollectionDeleted(payload) {
      const deletedJobId = String(payload?.jobId || "");
      if (!deletedJobId || deletedJobId !== String(this.processingJobId || "")) return;
      this.finishProcessingPhase();
    },
    handleLibraryEditorVisibilityChange(payload) {
      const isPayloadObject = payload && typeof payload === "object" && !Array.isArray(payload);
      this.isLibraryEditorVisible = isPayloadObject ? !!payload.isVisible : !!payload;
      this.syncEmbeddedScheduleSpacing();
    },
    handleCollectionInsufficientTokensClick() {
      this.goToBillingPacks();
    },
    async syncScheduleSubscriptionStatusFromStore(session = null) {
      try {
        const currentUserStore = useCurrentUserStore();
        await currentUserStore.ensureSessionReady(session, { syncCommerce: true, forceCommerce: true });
        const resolvedRole = String(currentUserStore.useRole || "").trim().toLowerCase();
        this.scheduleSubscriptionHasActivePlan =
          isImpersonating()
          || resolvedRole === "admin"
          || currentUserStore.canSchedulePosts;
        return this.scheduleSubscriptionHasActivePlan;
      } catch (error) {
        console.warn("No se pudo sincronizar el estado de suscripción para mostrar recomendación:", error);
        this.scheduleSubscriptionHasActivePlan = false;
        return false;
      }
    },
    async handleScheduleClipClick(payload) {
      const clip = payload?.clip || null;
      const onComplete = payload?.onComplete;
      if (!clip) return;
      try {
        if (this.userRole === "admin" || isImpersonating()) {
          await this.openScheduleModalForLibraryClip(clip);
          if (typeof onComplete === "function") onComplete();
          return;
        }
        const hasActivePlan = await this.syncScheduleSubscriptionStatusFromStore();
        if (hasActivePlan) {
          await this.openScheduleModalForLibraryClip(clip);
          if (typeof onComplete === "function") onComplete();
          return;
        }
        this.pendingScheduleClipFromLibrary = clip;
        this.showScheduleSubscriptionPromptModal = true;
        if (typeof onComplete === "function") onComplete();
      } catch (error) {
        console.error("No se pudo resolver el flujo de programación desde la biblioteca:", error);
        this.pendingScheduleClipFromLibrary = clip;
        this.scheduleSubscriptionHasActivePlan = false;
        this.showScheduleSubscriptionPromptModal = true;
        if (typeof onComplete === "function") onComplete();
      }
    },
    closeScheduleSubscriptionPromptModal() {
      this.showScheduleSubscriptionPromptModal = false;
      this.pendingScheduleClipFromLibrary = null;
      this.scheduleSubscriptionHasActivePlan = false;
    },
    openSubscriptionFromSchedulePrompt() {
      this.showScheduleSubscriptionPromptModal = false;
      this.pendingScheduleClipFromLibrary = null;
      this.scheduleSubscriptionHasActivePlan = false;
      this.$router.push({ name: "billing" });
    },
    async openScheduleModalForLibraryClip(clip) {
      const scheduleEmbed = this.$refs.makeClipsScheduleEmbed;
      if (!clip || !scheduleEmbed || typeof scheduleEmbed.openScheduleModalForClip !== "function") {
        this.showScheduleSubscriptionPromptModal = false;
        this.pendingScheduleClipFromLibrary = null;
        this.scheduleSubscriptionHasActivePlan = false;
        return;
      }

      // Trigger schedule modal first so transition feels immediate.
      const openSchedulePromise = scheduleEmbed.openScheduleModalForClip(clip);
      this.showScheduleSubscriptionPromptModal = false;
      this.pendingScheduleClipFromLibrary = null;
      this.scheduleSubscriptionHasActivePlan = false;
      await openSchedulePromise;
    },
    async refreshEmbeddedScheduleProgress() {
      const scheduleEmbed = this.$refs.makeClipsScheduleEmbed;
      if (!scheduleEmbed || typeof scheduleEmbed.refreshEmbeddedScheduleData !== "function") return;
      await scheduleEmbed.refreshEmbeddedScheduleData();
      this.syncEmbeddedScheduleSpacing();
    },
    async refreshEmbeddedScheduleSilently() {
      await this.refreshEmbeddedScheduleProgress();
    },
    syncModalScrollLock(shouldLock) {
      syncModalScrollLockState(this, shouldLock);
    },
    tt(key, fallback, params) {
      return typeof this.$t === "function" ? this.$t(key, params) : fallback;
    },
    normalizeAssignValue(value) {
      return String(value || "").trim().toLowerCase();
    },
    setAssignUserScope(scope) {
      if (scope !== "mine" && scope !== "all") return;
      this.assignUserScope = scope;
      this.assignUserNameFilter = "";
    },
    isKnownVideoType(value) {
      return this.videoTypeOptions.some((item) => item.value === value);
    },
    syncSubtitlesPreference() {
      const profile = useCurrentUserStore().profile;
      const enabled = profile?.subtitles_enabled !== false;
      this.subtitlesEnabled = enabled;
      if (profile?.subtitle_font && profile?.subtitle_active_color) {
        this.subtitleStyle = {
          font: profile.subtitle_font,
          activeColor: profile.subtitle_active_color,
          activeEffect: profile.subtitle_active_effect,
        };
      }
      this.adminForms.forEach((form) => {
        form.subtitlesEnabled = enabled;
      });
    },
    async saveSubtitlesPreference(nextValue) {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = getEffectiveUserId(session?.user?.id);
      if (!session || !userId) return;
      try {
        const payload = await updateSubtitlesEnabled(session, userId, nextValue);
        this.subtitlesEnabled = payload.subtitles_enabled;
        const currentUserStore = useCurrentUserStore();
        if (currentUserStore.profile) {
          currentUserStore.profile.subtitles_enabled = payload.subtitles_enabled;
        }
      } catch (error) {
        this.$notify({
          message: error?.message || this.tt("common.genericError", "Ocurrió un error"),
          type: "danger",
        });
      }
    },
    async openSubtitleStyleModal() {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = getEffectiveUserId(session?.user?.id);
      if (!session || !userId) return;
      const { data, error } = await supabase
        .from("user_info")
        .select("subtitle_font, subtitle_active_color, subtitle_active_effect")
        .eq("user_id", userId)
        .single();
      if (error) {
        this.$notify({ message: error.message, type: "danger" });
        return;
      }
      this.subtitleStyle = {
        font: data.subtitle_font,
        activeColor: data.subtitle_active_color,
        activeEffect: data.subtitle_active_effect,
      };
      this.subtitleStyleModalOpen = true;
    },
    closeSubtitleStyleModal() {
      if (!this.subtitleStyleSaving) this.subtitleStyleModalOpen = false;
    },
    async saveSubtitleStyle(style) {
      if (this.subtitleStyleSaving) return;
      const { data: { session } } = await supabase.auth.getSession();
      const userId = getEffectiveUserId(session?.user?.id);
      if (!session || !userId) return;
      this.subtitleStyleSaving = true;
      try {
        const { data, error } = await supabase
          .from("user_info")
          .update({
            subtitle_font: style.font,
            subtitle_active_color: style.activeColor,
            subtitle_active_effect: style.activeEffect,
          })
          .eq("user_id", userId)
          .select("subtitle_font, subtitle_active_color, subtitle_active_effect")
          .single();
        if (error) throw error;
        this.subtitleStyle = {
          font: data.subtitle_font,
          activeColor: data.subtitle_active_color,
          activeEffect: data.subtitle_active_effect,
        };
        const currentUserStore = useCurrentUserStore();
        if (currentUserStore.profile) {
          currentUserStore.profile.subtitle_font = data.subtitle_font;
          currentUserStore.profile.subtitle_active_color = data.subtitle_active_color;
          currentUserStore.profile.subtitle_active_effect = data.subtitle_active_effect;
        }
        this.subtitleStyleModalOpen = false;
      } catch (error) {
        this.$notify({ message: error?.message || this.tt("common.genericError", "Ocurrió un error"), type: "danger" });
      } finally {
        this.subtitleStyleSaving = false;
      }
    },
    setModalVideoType(videoType) {
      if (!this.isKnownVideoType(videoType)) return;
      this.adminVideoType = videoType;
      if (videoType !== "irl") {
        this.irlUserContextEnabled = false;
        this.userContext = "";
      }
    },
    setConfirmVideoType(videoType) {
      this.setModalVideoType(videoType);
      this.isConfirmVideoTypeChangeOpen = false;
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    resetTypeChooseChoreo() {
      this.typeChoosing = false;
      this.typeLeaving = false;
      this.chosenTypeCardOffset = { x: 0, y: 0 };
    },
    centerChosenTypeCard() {
      const container = this.$refs.flowConfigureBody;
      const cards = this.$refs.flowTypeCards;
      if (!container || !cards) return;
      const index = this.videoTypeOptions.findIndex((option) => option.value === this.adminVideoType);
      const chosen = Array.isArray(cards) ? cards[index] : cards;
      if (!chosen) return;
      const containerRect = container.getBoundingClientRect();
      const cardRect = chosen.getBoundingClientRect();
      this.chosenTypeCardOffset = {
        x: Math.round(containerRect.left + containerRect.width / 2 - (cardRect.left + cardRect.width / 2)),
        y: Math.round(containerRect.top + containerRect.height / 2 - (cardRect.top + cardRect.height / 2)),
      };
    },
    async handleFlowVideoTypeSelect(videoType) {
      if (this.loading || this.typeChoosing || !this.isKnownVideoType(videoType)) return;
      this.setModalVideoType(videoType);
      if (!this.canContinueToConfirm) return;
      this.typeChoosing = true;
      await this.$nextTick();
      await new Promise((resolve) => requestAnimationFrame(resolve));
      this.centerChosenTypeCard();
      await this.sleep(FLOW_TYPE_CHOOSE_MOVE_MS);
      this.typeLeaving = true;
      await this.sleep(FLOW_TYPE_CHOOSE_LEAVE_MS);
      this.goToConfirmStep();
      this.resetTypeChooseChoreo();
    },
    setAdminBulkVideoType(item, videoType) {
      if (!item || !this.isKnownVideoType(videoType)) return;
      item.videoType = videoType;
      const form = this.adminForms[item.formIndex];
      if (form) {
        form.videoType = videoType;
      }
    },
    setAdminBulkSubtitles(item) {
      const form = this.adminForms[item?.formIndex];
      if (form) {
        form.subtitlesEnabled = Boolean(item.subtitlesEnabled);
      }
    },
    getVideoTypeLabel(value) {
      const option = this.videoTypeOptions.find((item) => item.value === value);
      if (!option) return "";
      return this.tt(option.labelKey, option.fallback);
    },
    notifyInsufficientTokensInModal() {
      if (!this.showSubmitModal || !this.modalHasInsufficientTokens || this.insufficientTokensToastShown) return;
      if (typeof this.$notify === "function") {
        this.$notify({
          message: this.tt("makeClips.notEnoughTokensForVods", "No tienes tokens suficientes para procesar estos VODs."),
          type: "danger",
        });
      }
      this.insufficientTokensToastShown = true;
    },
    async syncCurrentUserContext(session = null) {
      const currentUserStore = useCurrentUserStore();
      await currentUserStore.ensureSessionReady(session, { syncCommerce: true });
      this.userCreditsRemaining = currentUserStore.creditsRemaining;
      this.userRole = currentUserStore.useRole;
      this.canAssignOnCreate = currentUserStore.hasAdminAccess;
      this.scheduleSubscriptionHasActivePlan =
        isImpersonating()
        || String(currentUserStore.useRole || "").trim().toLowerCase() === "admin"
        || currentUserStore.canSchedulePosts;
      if (!this.canAssignOnCreate) {
        this.selectedAssignee = null;
      }
    },
    async refreshCurrentUserContext(session = null) {
      const currentUserStore = useCurrentUserStore();
      await currentUserStore.refresh(session, { syncCommerce: true });
      this.userCreditsRemaining = currentUserStore.creditsRemaining;
      this.userRole = currentUserStore.useRole;
      this.canAssignOnCreate = currentUserStore.hasAdminAccess;
      this.scheduleSubscriptionHasActivePlan =
        isImpersonating()
        || String(currentUserStore.useRole || "").trim().toLowerCase() === "admin"
        || currentUserStore.canSchedulePosts;
      if (!this.canAssignOnCreate) {
        this.selectedAssignee = null;
      }
    },
    async resolveAssignPermissions() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          this.userRole = null;
          this.canAssignOnCreate = false;
          return;
        }
        await this.syncCurrentUserContext(session);
      } catch (error) {
        console.warn("No se pudo resolver permisos para reasignar:", error);
        this.userRole = null;
        this.canAssignOnCreate = false;
        this.userCreditsRemaining = null;
      }
    },
    async openAssignModal(formIndex = null) {
      if (!this.canAssignOnCreate) return;
      this.activeAssignFormIndex = Number.isInteger(formIndex) ? formIndex : null;
      this.assignUserNameFilter = "";
      this.assignUserScope = "mine";
      this.showAssignModal = true;
      await this.fetchAssignableUsers();
    },
    closeAssignModal() {
      this.showAssignModal = false;
      this.activeAssignFormIndex = null;
      this.assignUserNameFilter = "";
      this.assignUserScope = "mine";
    },
    async fetchAssignableUsers() {
      this.assignUsersLoading = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const data = await fetchAssignableUsers(session);
        const rawUsers = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];
        this.assignUsers = rawUsers
          .filter((user) => user && user.id != null && user.twitch_user === true)
          .map((user) => {
            const userInfo = Array.isArray(user?.user_info) ? user.user_info[0] : user?.user_info;
            return {
              id: user.id,
              username: user.username || "",
              email: user.email || "",
              user_id: user.user_id || null,
              assigned_admin: String(user?.assigned_admin || userInfo?.assigned_admin || "").trim(),
              subtitles_enabled: user?.subtitles_enabled !== false,
            };
          })
          .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
      } catch (error) {
        console.error("Error loading assignable users:", error);
        this.assignUsers = [];
      } finally {
        this.assignUsersLoading = false;
      }
    },
    getAssignUserInitial(user) {
      const username = String(user?.username || user?.user_id || "").trim();
      return username ? username.charAt(0).toUpperCase() : "?";
    },
    selectAssignee(user) {
      if (!user || user.id == null) return;
      const selected = {
        id: user.id,
        username: user.username || "",
        user_id: user.user_id || null,
        subtitles_enabled: user.subtitles_enabled !== false,
      };
      this.selectedAssignee = selected;
      this.subtitlesEnabled = selected.subtitles_enabled;
      this.adminForms.forEach((form) => {
        form.subtitlesEnabled = selected.subtitles_enabled;
      });
      this.closeAssignModal();
    },
    async resolveAuthUserIdFromUserInfoId(userInfoId) {
      if (userInfoId == null) return null;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error(this.tt("makeClips.authRequired", "Debes iniciar sesión."));
      }
      const payload = await fetchUserIdByUserInfoId(session, userInfoId);
      return payload?.user_id || null;
    },
    async reassignCreatedJob(jobId, assignee) {
      if (!jobId || !assignee || assignee.id == null) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error(this.tt("makeClips.authRequired", "Debes iniciar sesión."));
      }
      let targetAuthUserId = assignee.user_id || null;
      if (!targetAuthUserId) {
        targetAuthUserId = await this.resolveAuthUserIdFromUserInfoId(assignee.id);
      }
      if (!targetAuthUserId) {
        throw new Error(this.tt("makeClips.assigneeUserIdNotFound", "No se encontró el user_id del usuario seleccionado."));
      }

      await reassignTwitchJob(session, jobId, targetAuthUserId);
    },
    async openSubmitModal() {
      if (!this.hasRequiredAssignee) {
        if (this.isAdminMultiView && this.adminForms.length > 1) {
          this.adminBulkErrorMessage = this.tt("makeClips.assignUserBeforeCreate", "Debes asignar un usuario antes de crear clips.");
        }
        return;
      }

      if (this.isAdminMultiView && this.adminForms.length > 1) {
        if (this.adminBulkSubmitting) return;

        this.adminBulkErrorMessage = "";
        this.adminBulkSuccessMessage = "";

        const formsForSubmission = this.adminForms
          .map((form, index) => ({ form, index }))
          .filter(({ form }) => !!(form?.videoUrl || "").trim());

        if (formsForSubmission.length === 0) {
          this.adminBulkErrorMessage = this.tt("makeClips.bulkAddAtLeastOneValidUrl", "Agrega al menos una URL válida para crear clips.");
          return;
        }

        let hasValidationErrors = false;
        for (const { form } of formsForSubmission) {
          form.errorMessage = "";
          form.successMessage = "";
          const urlValidation = this.getAdminUrlValidation(form);
          const segmentsValidation = this.getAdminSegmentsValidation(form);
          if (!urlValidation.valid || !segmentsValidation.valid) {
            form.errorMessage = !urlValidation.valid
              ? (urlValidation.message || this.tt("makeClips.enterValidUrl", "Debes ingresar una URL válida."))
              : segmentsValidation.message;
            hasValidationErrors = true;
          }
        }

        if (hasValidationErrors) return;

        this.submitModalMode = "adminBulk";
        this.showSubmitModal = true;
        this.fetchAdminBulkMetadataPreview(formsForSubmission);
        await this.refreshCurrentUserContext();
      }
      // El flujo individual ya no usa este modal: se maneja con los pasos inline (flowPhase).
    },
    closeSubmitModal() {
      if (this.loading) return;
      this.showSubmitModal = false;
      this.insufficientTokensToastShown = false;
      this.submitModalMode = "single";
      this.adminBulkMetadataLoading = false;
      this.adminBulkMetadataError = "";
      this.adminBulkPreviewItems = [];
    },
    goToBillingPacks() {
      this.closeSubmitModal();
      this.$router.push("/billing");
    },
    handlePrimaryFormSubmit() {
      if (this.flowPhase === "url") {
        this.maybeStartUrlValidation(true);
        return;
      }
      if (this.flowPhase === "configure") {
        return;
      }
      if (this.flowPhase === "confirm") {
        this.confirmCreateFlow();
      }
    },
    handlePrimaryUrlPaste() {
      // setTimeout(0) en lugar de $nextTick: el evento input (que actualiza v-model)
      // se dispara después del paste; un microtask leería el valor viejo.
      setTimeout(() => {
        this.clearFlowValidationTimer();
        if (this.isValidUrl) {
          this.markUrlValidationUiPending();
        }
        this.maybeStartUrlValidation(true);
      }, 0);
    },
    clearFlowValidationTimer() {
      if (this.flowValidationTimerId != null) {
        clearTimeout(this.flowValidationTimerId);
        this.flowValidationTimerId = null;
      }
    },
    markUrlValidationUiPending() {
      this.urlValidationPending = true;
      this.flowValidationUiStartedAt = Date.now();
    },
    clearUrlValidationUi() {
      this.urlValidationPending = false;
      this.flowValidationUiStartedAt = null;
    },
    maybeStartUrlValidation(force = false) {
      if (this.flowPhase === "validating" || this.loading) return;
      if (this.flowPhase !== "url") return;
      if (!this.isValidUrl || !this.urlValidation.sourceType) {
        this.clearUrlValidationUi();
        return;
      }

      const sourceType = this.urlValidation.sourceType;
      let normalizedUrl = "";
      try {
        normalizedUrl = this.normalizeUrl(this.videoUrl, sourceType);
      } catch (error) {
        return;
      }

      const validationKey = `${sourceType}:${normalizedUrl}`;

      // Si ya validamos esta URL y tenemos metadata, avanzamos sin volver a consultar.
      if (
        force
        && this.metadataPreview
        && !this.metadataPreviewError
        && this.flowUrlAutoKey === validationKey
      ) {
        this.clearUrlValidationUi();
        this.flowPhase = "configure";
        return;
      }

      if (!force && this.flowUrlAutoKey === validationKey) {
        this.clearUrlValidationUi();
        return;
      }
      this.flowUrlAutoKey = validationKey;
      this.startUrlValidation(sourceType, normalizedUrl);
    },
    async startUrlValidation(sourceType, normalizedUrl) {
      this.clearFlowValidationTimer();
      this.clearCreateFlowSuccessMessage();
      this.urlValidationPending = false;
      this.flowPhase = "validating";
      this.metadataPreviewError = "";
      this.metadataPreview = null;
      const startedAt = this.flowValidationUiStartedAt || Date.now();
      this.flowValidationUiStartedAt = startedAt;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error(this.tt("makeClips.authRequired", "Debes iniciar sesión."));
        }

        const preview = await Promise.race([
          fetchTwitchMetadataPreview(session, {
            source_url: normalizedUrl,
            source_type: sourceType,
          }),
          new Promise((_resolve, reject) => setTimeout(
            () => reject(new Error(this.tt("makeClips.flowValidationTimeout", "La validación está tardando demasiado. Inténtalo de nuevo."))),
            60000,
          )),
        ]);

        await this.waitForFlowMinimumLoader(startedAt);

        // Si el usuario validó un video distinto al anterior, los segmentos previos ya no aplican.
        const validationKey = `${sourceType}:${normalizedUrl}`;
        if (this.flowValidatedKey && this.flowValidatedKey !== validationKey) {
          this.segments = [];
          this.nextSegmentId = 1;
        }
        this.flowValidatedKey = validationKey;

        this.metadataPreview = preview;
        this.clearUrlValidationUi();
        this.flowPhase = "configure";
      } catch (error) {
        await this.waitForFlowMinimumLoader(startedAt);
        this.metadataPreviewError = this.getMetadataPreviewErrorMessage(error, sourceType);
        this.clearUrlValidationUi();
        this.flowPhase = "url";
        this.focusPrimaryVideoUrlInput();
        return;
      }

      // Refrescar créditos fuera del try: si falla, no debe expulsar al usuario del paso 2.
      try {
        await this.refreshCurrentUserContext();
      } catch (error) {
        console.error("Failed to refresh user context after validation:", error);
      }
    },
    async waitForFlowMinimumLoader(startedAt, minimumMs = FLOW_VALIDATION_MIN_MS) {
      const elapsed = Date.now() - startedAt;
      if (elapsed >= minimumMs) return;
      await new Promise((resolve) => setTimeout(resolve, minimumMs - elapsed));
    },
    backToUrlStep() {
      if (this.loading || this.typeChoosing) return;
      this.clearFlowValidationTimer();
      this.clearUrlValidationUi();
      this.resetTypeChooseChoreo();
      this.videoUrl = "";
      this.flowPhase = "url";
      this.createFlowSubmitError = "";
      this.$nextTick(() => this.focusPrimaryVideoUrlInput());
    },
    goToConfirmStep() {
      if (this.loading || !this.canContinueToConfirm) return;
      this.createFlowSubmitError = "";
      this.flowPhase = "confirm";
      Promise.resolve(this.refreshCurrentUserContext()).catch((error) => {
        console.error("Failed to refresh user context on confirm step:", error);
      });
    },
    backToConfigureStep() {
      if (this.loading) return;
      this.createFlowSubmitError = "";
      this.resetTypeChooseChoreo();
      this.flowPhase = "configure";
    },
    async confirmCreateFlow() {
      if (this.isFlowConfirmDisabled) {
        if (this.exceedsUnlimitedDailyStreamLimit && typeof this.$notify === "function") {
          this.$notify({
            message: this.tt(
              "makeClips.unlimitedDailyStreamLimit",
              "El límite diario son 12 horas de video. Puedes recortar el stream para procesarlo. Disculpa las molestias.",
            ),
            type: "warning",
          });
        } else if (this.hasInsufficientTokens && typeof this.$notify === "function") {
          this.$notify({
            message: this.tt("makeClips.notEnoughTokensForVod", "No tienes tokens suficientes para procesar este VOD."),
            type: "danger",
          });
        }
        return;
      }
      const createdJobId = await this.submitJob();
      if (!createdJobId) return;
      if (this.isAdminRole) {
        this.finishCreateFlowForAdmin();
        return;
      }
      if (createdJobId === true) {
        // El job se creó pero no obtuvimos su id: no podemos seguir su progreso en vivo.
        this.resetCreateFlowAfterSubmit();
        return;
      }
      this.enterProcessingPhase(createdJobId);
    },
    finishCreateFlowForAdmin() {
      this.resetUploadState();
      this.videoUrl = "";
      this.segments = [];
      this.nextSegmentId = 1;
      this.adminVideoType = "";
      this.irlUserContextEnabled = false;
      this.userContext = "";
      this.metadataPreview = null;
      this.metadataPreviewError = "";
      this.flowUrlAutoKey = "";
      this.flowValidatedKey = "";
      this.createFlowSubmitError = "";
      this.resetTypeChooseChoreo();
      this.flowPhase = "url";
      this.createFlowSuccessMessage = this.tt(
        "makeClips.flowQueuedSuccessAdmin",
        "Video encolado. Puedes agregar otro de inmediato.",
      );
      this.clearCreateFlowSuccessTimer();
      this.createFlowSuccessTimerId = setTimeout(() => {
        this.createFlowSuccessTimerId = null;
        this.createFlowSuccessMessage = "";
      }, 5000);
    },
    resetCreateFlowAfterSubmit() {
      this.resetUploadState();
      this.videoUrl = "";
      this.segments = [];
      this.nextSegmentId = 1;
      this.adminVideoType = "";
      this.irlUserContextEnabled = false;
      this.userContext = "";
      this.metadataPreview = null;
      this.metadataPreviewError = "";
      this.flowUrlAutoKey = "";
      this.flowValidatedKey = "";
      this.resetTypeChooseChoreo();
      this.createFlowSubmitError = "";
      this.clearUrlValidationUi();
      this.flowPhase = "url";
      this.clearCreateFlowSuccessTimer();
      this.$nextTick(() => this.focusPrimaryVideoUrlInput());
    },
    clearCreateFlowSuccessTimer() {
      if (this.createFlowSuccessTimerId != null) {
        clearTimeout(this.createFlowSuccessTimerId);
        this.createFlowSuccessTimerId = null;
      }
    },
    clearCreateFlowSuccessMessage() {
      this.clearCreateFlowSuccessTimer();
      this.createFlowSuccessMessage = "";
    },
    applySegmentSeconds(index, startSeconds, endSeconds) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment) return;

      const start = Math.max(0, Math.round(Number(startSeconds) || 0));
      const end = Math.max(start + 1, Math.round(Number(endSeconds) || 0));

      segment.start = start;
      segment.end = end;
      segment.startTime = this.secondsToTimeString(start);
      segment.endTime = this.secondsToTimeString(end);
      segment.startHours = Math.floor(start / 3600);
      segment.startMinutes = Math.floor((start % 3600) / 60);
      segment.startSeconds = start % 60;
      segment.endHours = Math.floor(end / 3600);
      segment.endMinutes = Math.floor((end % 3600) / 60);
      segment.endSeconds = end % 60;
      segment._rawInput = {};
    },
    onTimelineSegmentChange(payload) {
      if (!payload) return;
      this.applySegmentSeconds(payload.index, payload.start, payload.end);
    },
    onTimelineDragEnd(index) {
      if (!Number.isInteger(index)) return;
      this.normalizeSegment(index, "startTime");
      this.normalizeSegment(index, "endTime");
    },
    onTimelineAddSegment(payload) {
      if (!payload) return;
      // Solo se permite un único segmento: no agregar más desde la línea de tiempo.
      if (this.segments && this.segments.length >= 1) return;
      const start = Math.max(0, Math.round(Number(payload.start) || 0));
      const end = Math.max(start + 1, Math.round(Number(payload.end) || 0));

      const newSegment = {
        id: this.nextSegmentId++,
        name: "",
        startTime: this.secondsToTimeString(start),
        endTime: this.secondsToTimeString(end),
        start,
        end,
        startHours: Math.floor(start / 3600),
        startMinutes: Math.floor((start % 3600) / 60),
        startSeconds: start % 60,
        endHours: Math.floor(end / 3600),
        endMinutes: Math.floor((end % 3600) / 60),
        endSeconds: end % 60,
        _rawInput: {},
      };

      // Insertar manteniendo el orden secuencial de los segmentos.
      const insertIndex = this.segments.findIndex((segment) => (Number(segment?.start) || 0) > start);
      if (insertIndex === -1) {
        this.segments.push(newSegment);
      } else {
        this.segments.splice(insertIndex, 0, newSegment);
      }
    },
    buildFullRangeSegment() {
      const duration = Math.floor(this.videoDurationSeconds);
      const start = 0;
      // Si conocemos la duración usamos el video completo; si no, un rango editable por defecto.
      const end = duration > 1 ? duration : 300;
      return {
        id: this.nextSegmentId++,
        name: "",
        startTime: this.secondsToTimeString(start),
        endTime: this.secondsToTimeString(end),
        start,
        end,
        startHours: Math.floor(start / 3600),
        startMinutes: Math.floor((start % 3600) / 60),
        startSeconds: start % 60,
        endHours: Math.floor(end / 3600),
        endMinutes: Math.floor((end % 3600) / 60),
        endSeconds: end % 60,
        _rawInput: {},
      };
    },
    ensureSingleFullSegment() {
      // Mantener siempre un único segmento que cubre todo el video por defecto.
      // Solo aplica cuando hay un video cargado en el flujo (paso 2/3).
      if (!this.metadataPreview) return;
      if (this.segments && this.segments.length > 0) return;
      this.segments = [this.buildFullRangeSegment()];
    },
    async confirmSubmitJob() {
      if (this.modalHasInsufficientTokens) {
        this.notifyInsufficientTokensInModal();
        return;
      }
      this.showSubmitModal = false;
      if (this.isAdminBulkModal) {
        await this.submitAllAdminForms();
        return;
      }
      await this.submitJob();
    },
    formatDuration(totalSeconds) {
      if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(Number(totalSeconds))) {
        return "--";
      }
      const seconds = Math.max(0, Math.floor(Number(totalSeconds)));
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
      }
      return `${minutes}:${secs.toString().padStart(2, "0")}`;
    },
    formatCount(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return "--";
      return Number(value).toLocaleString("es-MX");
    },
    formatUploadDate(rawDate) {
      const raw = (rawDate || "").toString();
      if (!/^\d{8}$/.test(raw)) return "--";
      const year = raw.slice(0, 4);
      const month = raw.slice(4, 6);
      const day = raw.slice(6, 8);
      return `${day}/${month}/${year}`;
    },
    formatBulkVodTitle(rawTitle) {
      const title = (rawTitle || this.tt("makeClips.untitled", "Sin título")).toString();
      if (title.length <= 23) return title;
      return `${title.slice(0, 23)}...`;
    },
    getMetadataPreviewRestrictionMessage(index = null) {
      if (Number.isInteger(index) && index >= 0) {
        return this.tt(
          "makeClips.clipMetadataRestrictedWithIndex",
          "No se pudo obtener la metadata de YouTube para el clip #{index}. Configura cookies validas de yt-dlp en el backend o define segmentos manualmente para calcular el costo.",
          { index: index + 1 },
        );
      }
      return this.tt(
        "makeClips.metadataRestrictedFullVod",
        "No se pudo obtener la metadata de YouTube para calcular el costo del VOD completo. Configura cookies validas de yt-dlp en el backend o define segmentos manualmente.",
      );
    },
    getMetadataPreviewErrorMessage(error, sourceType, index = null) {
      const errorCode = String(error?.code || "").trim().toLowerCase();
      if (errorCode === "source_vertical_not_supported") {
        return this.tt(
          "makeClips.metadataVerticalNotSupported",
          "Este video está en formato vertical. Solo admitimos videos horizontales (16:9).",
        );
      }

      if (errorCode === "source_access_denied" && sourceType === "google_drive") {
        if (Number.isInteger(index) && index >= 0) {
          return this.tt(
            "makeClips.googleDriveAccessErrorWithIndex",
            "Google Drive bloqueó el acceso al archivo del clip #{index} por sus permisos. Cambia los permisos del archivo a \"Cualquiera con el enlace\" y asegúrate de que permita descarga.",
            { index: index + 1 },
          );
        }
        return this.tt(
          "makeClips.googleDriveAccessError",
          "Google Drive bloqueó el acceso al archivo por sus permisos. Cambia los permisos del archivo a \"Cualquiera con el enlace\" y asegúrate de que permita descarga.",
        );
      }

      if (errorCode === "source_access_denied") {
        return this.tt(
          "makeClips.metadataSourceAccessDenied",
          "No pudimos acceder a este video. Verifica que sea público e inténtalo de nuevo.",
        );
      }

      if (errorCode === "source_unavailable") {
        return this.tt(
          "makeClips.metadataSourceUnavailable",
          "Este video no está disponible, es privado o ya no existe.",
        );
      }

      if (errorCode === "metadata_preview_timeout") {
        return this.tt(
          "makeClips.flowValidationTimeout",
          "La validación está tardando demasiado. Inténtalo de nuevo.",
        );
      }

      if (
        errorCode === "metadata_extractor_outdated"
        || errorCode === "metadata_service_unavailable"
      ) {
        return this.tt(
          "makeClips.metadataServiceUnavailable",
          "La importación de videos se está actualizando. Inténtalo de nuevo en unos minutos.",
        );
      }

      if (errorCode === "metadata_extraction_failed") {
        return this.tt(
          "makeClips.metadataExtractionFailed",
          "No pudimos leer la información de este video. Inténtalo de nuevo.",
        );
      }

      if (Number.isInteger(index) && index >= 0) {
        return this.tt(
          "makeClips.clipMetadataFetchErrorWithIndex",
          "No se pudo obtener metadata para el clip #{index}.",
          { index: index + 1 },
        );
      }
      return this.tt("makeClips.metadataFetchError", "No se pudo obtener metadata.");
    },
    async fetchMetadataPreview() {
      this.metadataPreviewLoading = true;
      this.metadataPreviewError = "";
      this.metadataPreview = null;
      let detectedSourceType = null;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error(this.tt("makeClips.authRequired", "Debes iniciar sesión."));
        }

        detectedSourceType = this.urlValidation.sourceType;
        if (!detectedSourceType) {
          throw new Error(this.tt("makeClips.detectPlatformError", "No se pudo detectar la plataforma del video."));
        }

        const preview = await fetchTwitchMetadataPreview(session, {
          source_url: this.normalizeUrl(this.videoUrl, detectedSourceType),
          source_type: detectedSourceType,
        });
        const canEstimateWithSegments = Number(this.totalCutSeconds) > 0;
        if (preview?.warning && !canEstimateWithSegments) {
          throw new Error(this.getMetadataPreviewRestrictionMessage());
        }

        this.metadataPreview = preview;
      } catch (error) {
        this.metadataPreviewError = this.getMetadataPreviewErrorMessage(error, detectedSourceType);
      } finally {
        this.metadataPreviewLoading = false;
      }
    },
    async fetchAdminBulkMetadataPreview(formsForSubmission) {
      this.adminBulkMetadataLoading = true;
      this.adminBulkMetadataError = "";
      this.adminBulkPreviewItems = [];

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error(this.tt("makeClips.authRequired", "Debes iniciar sesión."));
        }

        const previews = await Promise.all(formsForSubmission.map(async ({ form, index }) => {
          const urlValidation = this.getAdminUrlValidation(form);
          if (!urlValidation.valid || !urlValidation.sourceType) {
            throw new Error(this.tt("makeClips.clipUrlInvalidWithIndex", "La URL del clip #{index} es inválida.", { index: index + 1 }));
          }

          let metadata;
          try {
            metadata = await fetchTwitchMetadataPreview(session, {
              source_url: this.normalizeUrl(form.videoUrl, urlValidation.sourceType),
              source_type: urlValidation.sourceType,
            });
          } catch (error) {
            throw new Error(this.getMetadataPreviewErrorMessage(error, urlValidation.sourceType, index));
          }
          const normalizedSegments = (form.segments || [])
            .map((segment) => ({
              ...segment,
              start: this.timeToSeconds(segment.startTime || ""),
              end: this.timeToSeconds(segment.endTime || ""),
            }))
            .filter((segment) => Number.isFinite(segment.start) && Number.isFinite(segment.end) && segment.end > segment.start);

          const segmentedDuration = normalizedSegments.reduce((total, segment) => {
            const duration = this.getSegmentDurationSeconds(segment);
            return duration ? total + duration : total;
          }, 0);
          const canEstimateWithSegments = normalizedSegments.length > 0 && segmentedDuration > 0;
          if (metadata?.warning && !canEstimateWithSegments) {
            throw new Error(this.getMetadataPreviewRestrictionMessage(index));
          }

          const fullDuration = Number(metadata?.duration || 0);
          const processingDurationSeconds =
            canEstimateWithSegments ? segmentedDuration : Math.max(0, fullDuration);
          const estimatedTokenCost =
            Number.isFinite(processingDurationSeconds) && processingDurationSeconds > 0
              ? Math.ceil(processingDurationSeconds / 60)
              : 0;

          return {
            formIndex: index,
            videoType: form.videoType,
            subtitlesEnabled: form.subtitlesEnabled !== false,
            metadata,
            segments: normalizedSegments,
            processingDurationSeconds,
            estimatedTokenCost,
          };
        }));

        this.adminBulkPreviewItems = previews;
      } catch (error) {
        this.adminBulkMetadataError = error?.message || this.tt("makeClips.bulkMetadataFetchError", "No se pudo obtener metadata de los VODs.");
      } finally {
        this.adminBulkMetadataLoading = false;
      }
    },
    detectSourceType(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      if (/^https?:\/\/(www\.)?twitch\.tv\//.test(raw)) return "twitch";
      if (/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//.test(raw)) return "youtube";
      if (/^https?:\/\/(www\.)?facebook\.com\//.test(raw)) return "facebook";
      if (/^https?:\/\/(www\.)?kick\.com\//.test(raw)) return "kicks";
      if (/^https?:\/\/drive\.google\.com\//.test(raw)) return "google_drive";

      return null;
    },
    isFacebookReelUrl(value) {
      return !!this.extractFacebookReelId(value);
    },
    extractTwitchVodId(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      let parsed;
      try {
        parsed = new URL(raw);
      } catch (error) {
        return null;
      }
      const protocol = parsed.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") return null;

      const host = parsed.hostname.toLowerCase();
      if (host !== "twitch.tv" && host !== "www.twitch.tv") return null;

      const pathMatch = parsed.pathname.match(/^\/videos\/(\d+)(?:\/.*)?$/);
      if (!pathMatch) return null;
      return pathMatch[1];
    },
    extractYoutubeVideoId(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      let parsed;
      try {
        parsed = new URL(raw);
      } catch (error) {
        return null;
      }
      const protocol = parsed.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") return null;

      const host = parsed.hostname.toLowerCase();
      if (host === "youtu.be" || host === "www.youtu.be") {
        const id = parsed.pathname.replace(/^\/+/, "").split("/")[0];
        return /^[A-Za-z0-9_-]+$/.test(id) ? id : null;
      }

      if (host === "youtube.com" || host === "www.youtube.com") {
        if (parsed.pathname === "/watch") {
          const id = (parsed.searchParams.get("v") || "").trim();
          return /^[A-Za-z0-9_-]+$/.test(id) ? id : null;
        }

        const liveMatch = parsed.pathname.match(/^\/live\/([A-Za-z0-9_-]+)(?:\/.*)?$/);
        if (liveMatch) {
          return liveMatch[1];
        }
      }

      return null;
    },
    extractFacebookVideoIdentity(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      let parsed;
      try {
        parsed = new URL(raw);
      } catch (error) {
        return null;
      }
      const protocol = parsed.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") return null;

      const host = parsed.hostname.toLowerCase();
      if (host !== "facebook.com" && host !== "www.facebook.com") return null;

      const pathMatch = parsed.pathname.match(/^\/([^/]+)\/videos\/(?:[^/]+\/)?([0-9]+)(?:\/.*)?$/);
      if (!pathMatch) return null;

      const page = (pathMatch[1] || "").trim();
      const videoId = (pathMatch[2] || "").trim();
      if (!page || !videoId) return null;
      return { page, videoId };
    },
    extractFacebookReelId(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      let parsed;
      try {
        parsed = new URL(raw);
      } catch (error) {
        return null;
      }
      const protocol = parsed.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") return null;

      const host = parsed.hostname.toLowerCase();
      if (host !== "facebook.com" && host !== "www.facebook.com") return null;

      const rootReelMatch = parsed.pathname.match(/^\/reel\/([^/]+)(?:\/.*)?$/);
      if (rootReelMatch) {
        const reelId = (rootReelMatch[1] || "").trim();
        return reelId || null;
      }

      const pageReelsMatch = parsed.pathname.match(/^\/([^/]+)\/reels\/([^/]+)(?:\/.*)?$/);
      if (pageReelsMatch) {
        const reelId = (pageReelsMatch[2] || "").trim();
        return reelId || null;
      }

      return null;
    },
    extractFacebookWatchVideoId(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      let parsed;
      try {
        parsed = new URL(raw);
      } catch (error) {
        return null;
      }
      const protocol = parsed.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") return null;

      const host = parsed.hostname.toLowerCase();
      if (host !== "facebook.com" && host !== "www.facebook.com") return null;
      if (!/^\/watch\/live\/?$/.test(parsed.pathname) && !/^\/watch\/?$/.test(parsed.pathname)) return null;

      const videoId = (parsed.searchParams.get("v") || "").trim();
      return /^\d+$/.test(videoId) ? videoId : null;
    },
    extractFacebookShareVideoToken(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      let parsed;
      try {
        parsed = new URL(raw);
      } catch (error) {
        return null;
      }
      const protocol = parsed.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") return null;

      const host = parsed.hostname.toLowerCase();
      if (host !== "facebook.com" && host !== "www.facebook.com") return null;

      const pathMatch = parsed.pathname.match(/^\/share\/v\/([^/]+)\/?$/);
      const shareToken = (pathMatch?.[1] || "").trim();
      return shareToken || null;
    },
    extractKickVideoIdentity(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      let parsed;
      try {
        parsed = new URL(raw);
      } catch (error) {
        return null;
      }
      const protocol = parsed.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") return null;

      const host = parsed.hostname.toLowerCase();
      if (host !== "kick.com" && host !== "www.kick.com") return null;

      const pathMatch = parsed.pathname.match(/^\/([^/]+)\/videos\/([^/]+)(?:\/.*)?$/);
      if (!pathMatch) return null;

      const channel = (pathMatch[1] || "").trim();
      const videoId = (pathMatch[2] || "").trim();
      if (!channel || !videoId) return null;
      return { channel, videoId };
    },
    extractGoogleDriveFileId(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      let parsed;
      try {
        parsed = new URL(raw);
      } catch (error) {
        return null;
      }
      const protocol = parsed.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") return null;

      if (parsed.hostname !== "drive.google.com") {
        return null;
      }

      const pathMatch = parsed.pathname.match(/^\/file\/d\/([A-Za-z0-9_-]+)(?:\/.*)?$/);
      if (pathMatch) {
        return pathMatch[1];
      }

      if (parsed.pathname === "/open" || parsed.pathname === "/uc") {
        const id = (parsed.searchParams.get("id") || "").trim();
        if (/^[A-Za-z0-9_-]+$/.test(id)) {
          return id;
        }
      }

      return null;
    },
    getAdminUrlErrorTitle(adminForm) {
      const validation = this.getAdminUrlValidation(adminForm);
      const sourceType = validation.sourceType;
      if (!sourceType) {
        return this.tt("tryClips.urlForm.errors.invalidUrlTitle", "Formato de URL inválido");
      }

      const titles = {
        twitch: this.tt("tryClips.urlForm.errors.invalidTwitchTitle", "Formato de Twitch inválido"),
        youtube: this.tt("tryClips.urlForm.errors.invalidYoutubeTitle", "Formato de YouTube inválido"),
        facebook: this.tt("tryClips.urlForm.errors.invalidFacebookTitle", "Formato de Facebook inválido"),
        kicks: this.tt("tryClips.urlForm.errors.invalidKickTitle", "Formato de Kick inválido"),
        google_drive: this.tt("tryClips.urlForm.errors.invalidGoogleDriveTitle", "Formato de Google Drive inválido"),
      };
      return titles[sourceType] || this.tt("tryClips.urlForm.errors.invalidUrlTitle", "Formato de URL inválido");
    },
    getUrlErrorBySource(sourceType) {
      if (sourceType === "twitch") {
        return this.tt(
          "makeClips.invalidTwitchUrlWithExample",
          "Formato de Twitch inválido. Ejemplo válido: {example}",
          { example: this.getSourceTypeUrlExample("twitch") }
        );
      }
      if (sourceType === "youtube") {
        return this.tt(
          "makeClips.invalidYoutubeUrlWithExample",
          "Formato de YouTube inválido. Ejemplo válido: {example}",
          { example: this.getSourceTypeUrlExample("youtube") }
        );
      }
      if (sourceType === "facebook") {
        return this.tt(
          "makeClips.invalidFacebookUrlWithExample",
          "Formato de Facebook inválido. Ejemplo válido: {example}",
          { example: this.getSourceTypeUrlExample("facebook") }
        );
      }
      if (sourceType === "kicks") {
        return this.tt(
          "makeClips.invalidKickUrlWithExample",
          "Formato de Kick inválido. Ejemplo válido: {example}",
          { example: this.getSourceTypeUrlExample("kicks") }
        );
      }
      if (sourceType === "google_drive") {
        return this.tt(
          "makeClips.invalidGoogleDriveUrlWithExample",
          "Formato de Google Drive inválido. Ejemplo válido: {example}",
          { example: this.getSourceTypeUrlExample("google_drive") }
        );
      }
      return this.getUnrecognizedUrlErrorMessage();
    },
    getSourceTypeUrlExample(sourceType) {
      return SOURCE_TYPE_URL_EXAMPLES[sourceType] || "";
    },
    getUnrecognizedUrlErrorMessage() {
      return `${this.tt("makeClips.invalidUrlFormat", "Formato de URL inválido.")} ${this.tt("makeClips.enterValidUrl", "Debes ingresar una URL válida.")}`;
    },
    validateUrl(value, sourceType) {
      const raw = (value || "").trim();
      if (!raw) return false;
      const st = (sourceType || "").toLowerCase();
      if (!st) return false;
      if (st === "twitch") {
        return !!this.extractTwitchVodId(raw);
      }
      if (st === "youtube") {
        return !!this.extractYoutubeVideoId(raw);
      }
      if (st === "facebook") {
        return !!(
          this.extractFacebookVideoIdentity(raw)
          || this.extractFacebookReelId(raw)
          || this.extractFacebookWatchVideoId(raw)
          || this.extractFacebookShareVideoToken(raw)
        );
      }
      if (st === "kicks") {
        return !!this.extractKickVideoIdentity(raw);
      }
      if (st === "google_drive") {
        return !!this.extractGoogleDriveFileId(raw);
      }
      return false;
    },
    normalizeUrl(value, sourceType) {
      const raw = (value || "").trim();
      const st = (sourceType || "").toLowerCase();
      if (st === "twitch") {
        const vodId = this.extractTwitchVodId(raw);
        if (!vodId) throw new Error("Invalid Twitch VOD URL format");
        return `https://www.twitch.tv/videos/${vodId}`;
      }
      if (st === "youtube") {
        const videoId = this.extractYoutubeVideoId(raw);
        if (!videoId) throw new Error("Invalid YouTube URL format");
        return `https://www.youtube.com/watch?v=${videoId}`;
      }
      if (st === "facebook") {
        const identity = this.extractFacebookVideoIdentity(raw);
        if (identity) return `https://www.facebook.com/${identity.page}/videos/${identity.videoId}`;
        const reelId = this.extractFacebookReelId(raw);
        if (reelId) return `https://www.facebook.com/reel/${reelId}`;
        const watchVideoId = this.extractFacebookWatchVideoId(raw);
        if (watchVideoId) return `https://www.facebook.com/watch/?v=${watchVideoId}`;
        const shareVideoToken = this.extractFacebookShareVideoToken(raw);
        if (shareVideoToken) return `https://www.facebook.com/share/v/${shareVideoToken}/`;
        throw new Error("Invalid Facebook URL format");
      }
      if (st === "kicks") {
        const identity = this.extractKickVideoIdentity(raw);
        if (!identity) throw new Error("Invalid Kick URL format");
        return `https://kick.com/${identity.channel}/videos/${identity.videoId}`;
      }
      if (st === "google_drive") {
        const fileId = this.extractGoogleDriveFileId(raw);
        if (fileId) return `https://drive.google.com/file/d/${fileId}/view`;
        throw new Error("Invalid Google Drive URL format");
      }
      return raw;
    },
    addSingleSegment() {
      this.segmentsExpanded = true;
      let startSeconds = 0;
      if (this.segments && this.segments.length > 0) {
        const lastSegment = this.segments[this.segments.length - 1];
        const lastEnd = lastSegment?.endTime || "";
        if (lastEnd.trim() && this.isValidTimeFormat(lastEnd)) {
          startSeconds = this.timeToSeconds(lastEnd) + 300;
        }
      }
      startSeconds = this.clampSecondsToDay(startSeconds);
      let endSeconds = this.clampSecondsToDay(startSeconds + 300);
      if (endSeconds <= startSeconds && startSeconds < 86399) {
        endSeconds = startSeconds + 1;
      }
      // Si conocemos la duración del video, mantenemos el segmento dentro del rango.
      const videoDuration = Math.floor(this.videoDurationSeconds);
      if (videoDuration > 1) {
        if (startSeconds >= videoDuration - 1) {
          const lastSegment = this.segments[this.segments.length - 1];
          const lastEnd = lastSegment && this.isValidTimeFormat(lastSegment.endTime || "")
            ? this.timeToSeconds(lastSegment.endTime)
            : 0;
          startSeconds = Math.max(lastEnd + 1, Math.max(0, videoDuration - 300));
        }
        endSeconds = Math.min(Math.max(startSeconds + 1, endSeconds), videoDuration);
        if (endSeconds <= startSeconds) {
          endSeconds = Math.min(startSeconds + 1, videoDuration);
        }
        if (startSeconds >= videoDuration || endSeconds <= startSeconds) {
          if (typeof this.$notify === "function") {
            this.$notify({
              message: this.tt("makeClips.flowNoSpaceForSegment", "Ya no hay espacio disponible al final del video para otro segmento."),
              type: "warning",
            });
          }
          return;
        }
      }
      const startTime = this.secondsToTimeString(startSeconds);
      const endTime = this.secondsToTimeString(endSeconds);
      const startHours = Math.floor(startSeconds / 3600);
      const startMinutes = Math.floor((startSeconds % 3600) / 60);
      const startSecs = startSeconds % 60;
      const endHours = Math.floor(endSeconds / 3600);
      const endMinutes = Math.floor((endSeconds % 3600) / 60);
      const endSecs = endSeconds % 60;

      this.segments.push({
        id: this.nextSegmentId++,
        name: "",
        startTime: startTime,
        endTime: endTime,
        start: startSeconds,
        end: endSeconds,
        startHours: startHours,
        startMinutes: startMinutes,
        startSeconds: startSecs,
        endHours: endHours,
        endMinutes: endMinutes,
        endSeconds: endSecs,
        _rawInput: {},
      });
      this.editingSegmentIndex = this.segments.length - 1;
    },
    saveSegment(index) {
      if (index < 0 || index >= this.segments.length) return;
      this.normalizeSegment(index, "startTime");
      this.normalizeSegment(index, "endTime");
      this.editingSegmentIndex = null;
    },
    formatTwoDigits(value) {
      const num = parseInt(value) || 0;
      return num.toString().padStart(2, '0');
    },
    getTimeInputValue(segment, type, component) {
      // If there's a raw input value being typed, show that
      if (segment._rawInput && segment._rawInput[`${type}_${component}`] !== undefined && segment._rawInput[`${type}_${component}`] !== null) {
        return segment._rawInput[`${type}_${component}`];
      }
      // Otherwise show formatted value
      let value = 0;
      if (type === 'start') {
        if (component === 'hours') {
          value = segment.startHours || 0;
        } else if (component === 'minutes') {
          value = segment.startMinutes || 0;
        } else if (component === 'seconds') {
          value = segment.startSeconds || 0;
        }
      } else {
        if (component === 'hours') {
          value = segment.endHours || 0;
        } else if (component === 'minutes') {
          value = segment.endMinutes || 0;
        } else if (component === 'seconds') {
          value = segment.endSeconds || 0;
        }
      }
      return this.formatTwoDigits(value);
    },
    handleTimeInput(event, index, type, component) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment) return;

      let inputValue = event.target.value.replace(/[^0-9]/g, '');
      
      // Allow empty input temporarily while user is typing
      if (inputValue === '') {
        // Store raw input value for display
        if (!segment._rawInput) {
          segment._rawInput = {};
        }
        segment._rawInput[`${type}_${component}`] = '';
        return;
      }
      
      // Parse the input value
      let numValue = parseInt(inputValue) || 0;
      
      // Set max values based on component
      let maxValue = 59;
      if (component === 'hours') {
        maxValue = 23;
      }
      
      // Clamp the value
      numValue = Math.max(0, Math.min(maxValue, numValue));
      
      // Store raw input for display (allows user to continue typing)
      if (!segment._rawInput) {
        segment._rawInput = {};
      }
      segment._rawInput[`${type}_${component}`] = inputValue;
      
      // Update the segment value
      if (type === 'start') {
        if (component === 'hours') {
          segment.startHours = numValue;
        } else if (component === 'minutes') {
          segment.startMinutes = numValue;
        } else if (component === 'seconds') {
          segment.startSeconds = numValue;
        }
      } else {
        if (component === 'hours') {
          segment.endHours = numValue;
        } else if (component === 'minutes') {
          segment.endMinutes = numValue;
        } else if (component === 'seconds') {
          segment.endSeconds = numValue;
        }
      }
      
      // Keep raw input while user is typing - will be cleared on blur
      // This allows user to type freely (e.g., "5" then "9" to make "59")
      
      this.updateTimeFromComponents(index, type);
    },
    updateTimeFromComponents(index, type) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment) return;

      const hours = type === 'start' ? (segment.startHours || 0) : (segment.endHours || 0);
      const minutes = type === 'start' ? (segment.startMinutes || 0) : (segment.endMinutes || 0);
      const seconds = type === 'start' ? (segment.startSeconds || 0) : (segment.endSeconds || 0);

      // Normalize values
      const normalizedHours = Math.max(0, Math.min(23, Math.floor(hours) || 0));
      const normalizedMinutes = Math.max(0, Math.min(59, Math.floor(minutes) || 0));
      const normalizedSeconds = Math.max(0, Math.min(59, Math.floor(seconds) || 0));

      // Update component values
      if (type === 'start') {
        segment.startHours = normalizedHours;
        segment.startMinutes = normalizedMinutes;
        segment.startSeconds = normalizedSeconds;
        segment.startTime = this.secondsToTimeString(normalizedHours * 3600 + normalizedMinutes * 60 + normalizedSeconds);
      } else {
        segment.endHours = normalizedHours;
        segment.endMinutes = normalizedMinutes;
        segment.endSeconds = normalizedSeconds;
        segment.endTime = this.secondsToTimeString(normalizedHours * 3600 + normalizedMinutes * 60 + normalizedSeconds);
      }

      this.updateSegment(index);
      this.ensureEndAfterStart(index);
    },
    normalizeTimeComponent(index, type, component) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment) return;

      // Clear raw input since we're formatting now
      if (segment._rawInput) {
        segment._rawInput[`${type}_${component}`] = null;
      }

      let value;
      if (type === 'start') {
        if (component === 'hours') {
          value = segment.startHours || 0;
          segment.startHours = Math.max(0, Math.min(23, Math.floor(value) || 0));
        } else if (component === 'minutes') {
          value = segment.startMinutes || 0;
          segment.startMinutes = Math.max(0, Math.min(59, Math.floor(value) || 0));
        } else if (component === 'seconds') {
          value = segment.startSeconds || 0;
          segment.startSeconds = Math.max(0, Math.min(59, Math.floor(value) || 0));
        }
      } else {
        if (component === 'hours') {
          value = segment.endHours || 0;
          segment.endHours = Math.max(0, Math.min(23, Math.floor(value) || 0));
        } else if (component === 'minutes') {
          value = segment.endMinutes || 0;
          segment.endMinutes = Math.max(0, Math.min(59, Math.floor(value) || 0));
        } else if (component === 'seconds') {
          value = segment.endSeconds || 0;
          segment.endSeconds = Math.max(0, Math.min(59, Math.floor(value) || 0));
        }
      }

      this.updateTimeFromComponents(index, type);
    },
    incrementTime(index, type, component) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment) return;

      if (type === 'start') {
        if (component === 'hours') {
          segment.startHours = Math.min(23, (segment.startHours || 0) + 1);
        } else if (component === 'minutes') {
          segment.startMinutes = Math.min(59, (segment.startMinutes || 0) + 1);
        } else if (component === 'seconds') {
          segment.startSeconds = Math.min(59, (segment.startSeconds || 0) + 1);
        }
      } else {
        if (component === 'hours') {
          segment.endHours = Math.min(23, (segment.endHours || 0) + 1);
        } else if (component === 'minutes') {
          segment.endMinutes = Math.min(59, (segment.endMinutes || 0) + 1);
        } else if (component === 'seconds') {
          segment.endSeconds = Math.min(59, (segment.endSeconds || 0) + 1);
        }
      }

      this.updateTimeFromComponents(index, type);
    },
    decrementTime(index, type, component) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment) return;

      if (type === 'start') {
        if (component === 'hours') {
          segment.startHours = Math.max(0, (segment.startHours || 0) - 1);
        } else if (component === 'minutes') {
          segment.startMinutes = Math.max(0, (segment.startMinutes || 0) - 1);
        } else if (component === 'seconds') {
          segment.startSeconds = Math.max(0, (segment.startSeconds || 0) - 1);
        }
      } else {
        if (component === 'hours') {
          segment.endHours = Math.max(0, (segment.endHours || 0) - 1);
        } else if (component === 'minutes') {
          segment.endMinutes = Math.max(0, (segment.endMinutes || 0) - 1);
        } else if (component === 'seconds') {
          segment.endSeconds = Math.max(0, (segment.endSeconds || 0) - 1);
        }
      }

      this.updateTimeFromComponents(index, type);
    },
    isValidTimeFormat(timeString) {
      if (!timeString || !timeString.trim()) return false;
      // Accept formats: HH:MM:SS, MM:SS, or SS
      const timeRegex = /^(\d{1,2}:)?(\d{1,2}:)?\d{1,2}$/;
      if (!timeRegex.test(timeString.trim())) return false;
      
      // Validate that each part is within valid range
      const parts = timeString.trim().split(":").map(Number);
      if (parts.length === 1) {
        // SS format
        return parts[0] >= 0 && parts[0] < 60;
      } else if (parts.length === 2) {
        // MM:SS format
        return parts[0] >= 0 && parts[0] < 60 && parts[1] >= 0 && parts[1] < 60;
      } else if (parts.length === 3) {
        // HH:MM:SS format
        return parts[0] >= 0 && parts[1] >= 0 && parts[1] < 60 && parts[2] >= 0 && parts[2] < 60;
      }
      return false;
    },
    timeToSeconds(timeString) {
      if (!timeString || !timeString.trim()) return 0;
      if (!this.isValidTimeFormat(timeString)) return 0;
      
      const parts = timeString.trim().split(":").map(Number);
      if (parts.length === 1) {
        // SS format
        return parts[0];
      } else if (parts.length === 2) {
        // MM:SS format
        return parts[0] * 60 + parts[1];
      } else if (parts.length === 3) {
        // HH:MM:SS format
        return parts[0] * 3600 + parts[1] * 60 + parts[2];
      }
      return 0;
    },
    secondsToTimeString(seconds) {
      if (!seconds || seconds < 0) return "0:00:00";
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    formatTimeInput(index, field) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment || !segment[field]) return;
      
      const timeString = segment[field].trim();
      if (!timeString) return;
      
      // If valid format, normalize it
      if (this.isValidTimeFormat(timeString)) {
        const seconds = this.timeToSeconds(timeString);
        segment[field] = this.secondsToTimeString(seconds);
      }
    },
    updateSegment(index) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (segment) {
        segment.start = this.timeToSeconds(segment.startTime || "");
        segment.end = this.timeToSeconds(segment.endTime || "");
        
        // Sync components from time strings if they exist
        if (segment.startTime && this.isValidTimeFormat(segment.startTime)) {
          const startSeconds = this.timeToSeconds(segment.startTime);
          segment.startHours = Math.floor(startSeconds / 3600);
          segment.startMinutes = Math.floor((startSeconds % 3600) / 60);
          segment.startSeconds = startSeconds % 60;
        }
        
        if (segment.endTime && this.isValidTimeFormat(segment.endTime)) {
          const endSeconds = this.timeToSeconds(segment.endTime);
          segment.endHours = Math.floor(endSeconds / 3600);
          segment.endMinutes = Math.floor((endSeconds % 3600) / 60);
          segment.endSeconds = endSeconds % 60;
        }
      }
    },
    normalizeSegment(index, field) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment) return;

      if (field && segment[field]) {
        const raw = (segment[field] || "").trim();
        if (raw && this.isValidTimeFormat(raw)) {
          segment[field] = this.secondsToTimeString(this.timeToSeconds(raw));
        }
      }

      segment.start = this.timeToSeconds(segment.startTime || "");
      segment.end = this.timeToSeconds(segment.endTime || "");

      if (segment.startTime && this.isValidTimeFormat(segment.startTime)) {
        const startSeconds = this.timeToSeconds(segment.startTime);
        segment.startHours = Math.floor(startSeconds / 3600);
        segment.startMinutes = Math.floor((startSeconds % 3600) / 60);
        segment.startSeconds = startSeconds % 60;
      }
      if (segment.endTime && this.isValidTimeFormat(segment.endTime)) {
        const endSeconds = this.timeToSeconds(segment.endTime);
        segment.endHours = Math.floor(endSeconds / 3600);
        segment.endMinutes = Math.floor((endSeconds % 3600) / 60);
        segment.endSeconds = endSeconds % 60;
      }
    },
    removeSegment(index) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      this.segments.splice(index, 1);
      if (this.editingSegmentIndex === index) {
        this.editingSegmentIndex = null;
      } else if (this.editingSegmentIndex !== null && this.editingSegmentIndex > index) {
        this.editingSegmentIndex--;
      }
      if (this.segments.length === 0) {
        this.segmentsExpanded = false;
      }
    },
    duplicateSegment(index) {
      if (!this.segments || index < 0 || index >= this.segments.length || !this.canAddMoreSegments) return;
      const source = this.segments[index];
      const newSegment = {
        id: this.nextSegmentId++,
        name: (source.name || "").trim() ? `${source.name} (copia)` : "",
        startTime: source.startTime || "",
        endTime: source.endTime || "",
        start: source.start,
        end: source.end,
        startHours: source.startHours ?? 0,
        startMinutes: source.startMinutes ?? 0,
        startSeconds: source.startSeconds ?? 0,
        endHours: source.endHours ?? 0,
        endMinutes: source.endMinutes ?? 0,
        endSeconds: source.endSeconds ?? 0,
        _rawInput: {},
      };
      this.segments.splice(index + 1, 0, newSegment);
      if (this.segments.length > 1) {
        this.segmentsExpanded = true;
      }
    },
    formatTime(seconds) {
      if (!seconds || seconds < 0) return "0:00:00";
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    getSegmentStartError(index) {
      if (index === 0) return null;
      if (!this.segments || index >= this.segments.length) return null;
      const segment = this.segments[index];
      if (!segment || !segment.startTime || !this.isValidTimeFormat(segment.startTime)) return null;
      
      if (index - 1 < 0 || index - 1 >= this.segments.length) return null;
      const prevSegment = this.segments[index - 1];
      if (!prevSegment || !prevSegment.endTime || !this.isValidTimeFormat(prevSegment.endTime)) return null;
      
      const prevEnd = this.timeToSeconds(prevSegment.endTime);
      const currentStart = this.timeToSeconds(segment.startTime);
      
      if (currentStart <= prevEnd) {
        const minStart = prevEnd + 1;
        return this.tt(
          "makeClips.segmentStartAfterPrevious",
          "El inicio debe ser después de {previousEnd} (mínimo: {minimumStart})",
          {
            previousEnd: this.formatTime(prevEnd),
            minimumStart: this.formatTime(minStart),
          }
        );
      }
      return null;
    },
    getSegmentEndError(index) {
      if (!this.segments || index >= this.segments.length) return null;
      const segment = this.segments[index];
      if (!segment || !segment.startTime || !this.isValidTimeFormat(segment.startTime)) return null;
      if (!segment.endTime || !this.isValidTimeFormat(segment.endTime)) return null;
      
      const start = this.timeToSeconds(segment.startTime);
      const end = this.timeToSeconds(segment.endTime);
      
      if (end <= start) {
        return this.tt("makeClips.segmentEndMustBeAfterStart", "El fin debe ser mayor que el inicio");
      }
      return null;
    },
    ensureEndAfterStart(index) {
      if (!this.segments || index < 0 || index >= this.segments.length) return;
      const segment = this.segments[index];
      if (!segment) return;

      const startTime = segment.startTime || "";
      const endTime = segment.endTime || "";
      if (!this.isValidTimeFormat(startTime) || !this.isValidTimeFormat(endTime)) return;

      const startSeconds = this.timeToSeconds(startTime);
      const endSeconds = this.timeToSeconds(endTime);

      if (endSeconds <= startSeconds) {
        let nextEndSeconds = this.clampSecondsToDay(startSeconds + 300);
        if (nextEndSeconds <= startSeconds && startSeconds < 86399) {
          nextEndSeconds = startSeconds + 1;
        }
        const hours = Math.floor(nextEndSeconds / 3600);
        const minutes = Math.floor((nextEndSeconds % 3600) / 60);
        const seconds = nextEndSeconds % 60;

        segment.endHours = hours;
        segment.endMinutes = minutes;
        segment.endSeconds = seconds;
        segment.endTime = this.secondsToTimeString(nextEndSeconds);

        if (segment._rawInput) {
          segment._rawInput.end_hours = null;
          segment._rawInput.end_minutes = null;
          segment._rawInput.end_seconds = null;
        }

        this.updateSegment(index);
      }
    },
    clampSecondsToDay(seconds) {
      if (!seconds || seconds < 0) return 0;
      return Math.min(86399, seconds);
    },
    getSegmentsValidation() {
      if (!this.segments || this.segments.length === 0) {
        return { valid: true, message: "" };
      }

      for (let i = 0; i < this.segments.length; i++) {
        const seg = this.segments[i];
        if (!seg) {
          return { valid: false, message: this.tt("makeClips.segmentTimesRequired", "Cada segmento debe tener tiempos de inicio y fin válidos.") };
        }

        const startTimeStr = seg.startTime || "";
        const endTimeStr = seg.endTime || "";

        if (!startTimeStr.trim() || !this.isValidTimeFormat(startTimeStr)) {
          return { valid: false, message: this.tt("makeClips.segmentStartTimeInvalid", "Cada segmento debe tener una hora de inicio válida.") };
        }

        if (!endTimeStr.trim() || !this.isValidTimeFormat(endTimeStr)) {
          return { valid: false, message: this.tt("makeClips.segmentEndTimeInvalid", "Cada segmento debe tener una hora de fin válida.") };
        }

        const startSeconds = this.timeToSeconds(startTimeStr);
        const endSeconds = this.timeToSeconds(endTimeStr);

        if (endSeconds <= startSeconds) {
          return { valid: false, message: this.tt("makeClips.segmentEndAfterStart", "Cada segmento debe terminar después de que comience.") };
        }

        if (i > 0) {
          const prevSeg = this.segments[i - 1];
          if (!prevSeg || !prevSeg.endTime || !this.isValidTimeFormat(prevSeg.endTime)) {
            return { valid: false, message: this.tt("makeClips.segmentsSequentialNoOverlap", "Los segmentos deben ser secuenciales y no superponerse.") };
          }

          const prevEnd = this.timeToSeconds(prevSeg.endTime);
          if (startSeconds <= prevEnd) {
            return { valid: false, message: this.tt("makeClips.segmentsSequentialNoOverlap", "Los segmentos deben ser secuenciales y no superponerse.") };
          }
        }
      }

      return { valid: true, message: "" };
    },
    getSegmentDurationSeconds(segment) {
      if (!segment) return null;
      const startTime = segment.startTime || "";
      const endTime = segment.endTime || "";
      if (!this.isValidTimeFormat(startTime) || !this.isValidTimeFormat(endTime)) return null;

      const start = this.timeToSeconds(startTime);
      const end = this.timeToSeconds(endTime);
      if (end <= start) return null;
      return end - start;
    },
    formatMinutes(seconds) {
      if (seconds === null || seconds === undefined) return "--";
      const minutes = seconds / 60;
      const rounded = Math.round(minutes * 10) / 10;
      return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
    },
    getDetectedSourceLabelByValue(value) {
      const labels = {
        twitch: "Twitch",
        youtube: "YouTube",
        facebook: "Facebook",
        kicks: "Kick",
        google_drive: "Google Drive",
      };
      const sourceType = this.detectSourceType(value);
      return labels[sourceType] || this.tt("makeClips.unknownSource", "Desconocida");
    },
    getSourceTypeIconUrl(sourceType) {
      return SOURCE_TYPE_ICON_URLS[sourceType] || null;
    },
    getAdminUrlValidation(adminForm) {
      const raw = (adminForm?.videoUrl || "").trim();
      if (!raw) {
        return { valid: false, sourceType: null, message: "" };
      }

      const sourceType = this.detectSourceType(raw);
      if (!sourceType) {
        return {
          valid: false,
          sourceType: null,
          message: this.getUnrecognizedUrlErrorMessage(),
        };
      }

      const isValid = this.validateUrl(raw, sourceType);
      if (!isValid) {
        return {
          valid: false,
          sourceType,
          message: this.getUrlErrorBySource(sourceType),
        };
      }

      return { valid: true, sourceType, message: "" };
    },
    addAdminForm(afterIndex) {
      const insertAt = Number.isInteger(afterIndex) ? afterIndex + 1 : this.adminForms.length;
      this.adminForms.splice(insertAt, 0, {
        id: this.nextAdminFormId++,
        videoUrl: "",
        videoType: "",
        subtitlesEnabled: useCurrentUserStore().profile?.subtitles_enabled !== false,
        segments: [],
        nextSegmentId: 1,
        selectedAssignee: null,
        loading: false,
        successMessage: "",
        errorMessage: "",
      });
    },
    addAdminSegment(cardIndex) {
      const form = this.adminForms[cardIndex];
      if (!form) return;

      let startSeconds = 0;
      if (form.segments.length > 0) {
        const last = form.segments[form.segments.length - 1];
        const lastEnd = (last?.endTime || "").trim();
        if (lastEnd && this.isValidTimeFormat(lastEnd)) {
          startSeconds = this.timeToSeconds(lastEnd) + 300;
        }
      }

      startSeconds = this.clampSecondsToDay(startSeconds);
      let endSeconds = this.clampSecondsToDay(startSeconds + 300);
      if (endSeconds <= startSeconds && startSeconds < 86399) {
        endSeconds = startSeconds + 1;
      }

      form.segments.push({
        id: form.nextSegmentId++,
        startTime: this.secondsToTimeString(startSeconds),
        endTime: this.secondsToTimeString(endSeconds),
        start: startSeconds,
        end: endSeconds,
      });
      form.errorMessage = "";
      form.successMessage = "";
    },
    normalizeAdminSegment(cardIndex, segmentIndex, field) {
      const form = this.adminForms[cardIndex];
      if (!form || segmentIndex < 0 || segmentIndex >= form.segments.length) return;
      const segment = form.segments[segmentIndex];
      if (!segment) return;

      if (field && segment[field]) {
        const raw = (segment[field] || "").trim();
        if (raw && this.isValidTimeFormat(raw)) {
          segment[field] = this.secondsToTimeString(this.timeToSeconds(raw));
        }
      }

      segment.start = this.timeToSeconds(segment.startTime || "");
      segment.end = this.timeToSeconds(segment.endTime || "");
    },
    removeAdminSegment(cardIndex, segmentIndex) {
      const form = this.adminForms[cardIndex];
      if (!form || segmentIndex < 0 || segmentIndex >= form.segments.length) return;
      form.segments.splice(segmentIndex, 1);
      form.errorMessage = "";
      form.successMessage = "";
    },
    getAdminSegmentsValidation(adminForm) {
      const segments = adminForm?.segments || [];
      if (!segments.length) return { valid: true, message: "" };

      for (let i = 0; i < segments.length; i += 1) {
        const seg = segments[i];
        if (!seg) {
          return { valid: false, message: this.tt("makeClips.segmentTimesRequired", "Cada segmento debe tener tiempos de inicio y fin válidos.") };
        }

        const startTimeStr = (seg.startTime || "").trim();
        const endTimeStr = (seg.endTime || "").trim();
        if (!startTimeStr || !this.isValidTimeFormat(startTimeStr)) {
          return { valid: false, message: this.tt("makeClips.segmentStartTimeInvalid", "Cada segmento debe tener una hora de inicio válida.") };
        }
        if (!endTimeStr || !this.isValidTimeFormat(endTimeStr)) {
          return { valid: false, message: this.tt("makeClips.segmentEndTimeInvalid", "Cada segmento debe tener una hora de fin válida.") };
        }

        const startSeconds = this.timeToSeconds(startTimeStr);
        const endSeconds = this.timeToSeconds(endTimeStr);
        if (endSeconds <= startSeconds) {
          return { valid: false, message: this.tt("makeClips.segmentEndAfterStart", "Cada segmento debe terminar después de que comience.") };
        }

        if (i > 0) {
          const prev = segments[i - 1];
          const prevEndStr = (prev?.endTime || "").trim();
          if (!prevEndStr || !this.isValidTimeFormat(prevEndStr)) {
            return { valid: false, message: this.tt("makeClips.segmentsSequentialNoOverlap", "Los segmentos deben ser secuenciales y no superponerse.") };
          }
          const prevEnd = this.timeToSeconds(prevEndStr);
          if (startSeconds <= prevEnd) {
            return { valid: false, message: this.tt("makeClips.segmentsSequentialNoOverlap", "Los segmentos deben ser secuenciales y no superponerse.") };
          }
        }
      }

      return { valid: true, message: "" };
    },
    getAdminTotalCutSeconds(adminForm) {
      const segments = adminForm?.segments || [];
      if (!segments.length) return 0;
      return segments.reduce((total, segment) => {
        const duration = this.getSegmentDurationSeconds(segment);
        return duration ? total + duration : total;
      }, 0);
    },
    async submitAdminForm(cardIndex) {
      const form = this.adminForms[cardIndex];
      if (!form || form.loading) return;

      form.errorMessage = "";
      form.successMessage = "";

      const urlValidation = this.getAdminUrlValidation(form);
      const segmentsValidation = this.getAdminSegmentsValidation(form);
      if (!urlValidation.valid || !segmentsValidation.valid || !this.isKnownVideoType(form.videoType)) {
        form.errorMessage = !urlValidation.valid
          ? (urlValidation.message || this.tt("makeClips.enterValidUrl", "Debes ingresar una URL válida."))
          : (!segmentsValidation.valid
            ? segmentsValidation.message
            : this.tt("makeClips.selectVideoTypeBeforeCreate", "Selecciona un tipo de video antes de crear clips."));
        return;
      }

      form.loading = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error(this.tt("makeClips.authRequired", "Debes iniciar sesión."));
        }

        const requestBody = {
          source_url: this.normalizeUrl(form.videoUrl, urlValidation.sourceType),
          source_type: urlValidation.sourceType,
          video_type: form.videoType,
          subtitles_enabled: form.subtitlesEnabled !== false,
          options: {},
        };

        if (form.segments.length > 0) {
          requestBody.segments = form.segments.map((seg) => ({
            start: this.timeToSeconds(seg.startTime || ""),
            end: this.timeToSeconds(seg.endTime || ""),
          }));
        }

        const jobPayload = await createTwitchJob(session, requestBody);
        if (this.canAssignOnCreate && this.selectedAssignee) {
          await this.reassignCreatedJob(jobPayload?.id, this.selectedAssignee);
        }

        form.successMessage = this.tt("makeClips.clipQueuedSuccess", "Clip encolado correctamente.");
        await this.refreshCurrentUserContext();
        await this.refreshEmbeddedScheduleSilently();
      } catch (error) {
        form.errorMessage = error?.message || this.tt("makeClips.clipQueueError", "No se pudo encolar el clip.");
      } finally {
        form.loading = false;
      }
    },
    async submitAllAdminForms() {
      if (this.adminBulkSubmitting) return;
      if (!this.hasRequiredAssignee) {
        this.adminBulkErrorMessage = this.tt("makeClips.assignUserBeforeCreate", "Debes asignar un usuario antes de crear clips.");
        return;
      }

      this.adminBulkSuccessMessage = "";
      this.adminBulkErrorMessage = "";

      const indexesToSubmit = this.adminForms
        .map((form, index) => ({ form, index }))
        .filter(({ form }) => !!(form?.videoUrl || "").trim())
        .map(({ index }) => index);

      if (indexesToSubmit.length === 0) {
        this.adminBulkErrorMessage = this.tt("makeClips.bulkAddAtLeastOneValidUrl", "Agrega al menos una URL válida para crear clips.");
        return;
      }

      const jobsPayload = [];
      let hasValidationErrors = false;
      for (const index of indexesToSubmit) {
        const form = this.adminForms[index];
        if (!form) continue;
        form.errorMessage = "";
        form.successMessage = "";

        const urlValidation = this.getAdminUrlValidation(form);
        const segmentsValidation = this.getAdminSegmentsValidation(form);
        if (!urlValidation.valid || !segmentsValidation.valid || !this.isKnownVideoType(form.videoType)) {
          form.errorMessage = !urlValidation.valid
            ? (urlValidation.message || this.tt("makeClips.enterValidUrl", "Debes ingresar una URL válida."))
            : (!segmentsValidation.valid
              ? segmentsValidation.message
              : this.tt("makeClips.selectVideoTypeBeforeCreate", "Selecciona un tipo de video antes de crear clips."));
          hasValidationErrors = true;
          continue;
        }

        const payloadItem = {
          source_url: this.normalizeUrl(form.videoUrl, urlValidation.sourceType),
          source_type: urlValidation.sourceType,
          video_type: form.videoType,
          subtitles_enabled: form.subtitlesEnabled !== false,
          options: {},
        };
        if (form.segments.length > 0) {
          payloadItem.segments = form.segments.map((seg) => ({
            start: this.timeToSeconds(seg.startTime || ""),
            end: this.timeToSeconds(seg.endTime || ""),
          }));
        }
        jobsPayload.push({
          index,
          payloadItem,
        });
      }

      if (hasValidationErrors || jobsPayload.length === 0) {
        if (!hasValidationErrors) {
          this.adminBulkErrorMessage = this.tt("makeClips.noValidUrlsToQueue", "No hay URLs válidas para encolar.");
        }
        return;
      }

      this.adminBulkSubmitting = true;
      let successCount = 0;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error(this.tt("makeClips.authRequired", "Debes iniciar sesión."));
        }

        const batchPayload = await createTwitchJobsBatch(session, {
          jobs: jobsPayload.map((item) => item.payloadItem),
        });
        const rawResults = Array.isArray(batchPayload?.results) ? batchPayload.results : [];
        const resultByBatchIndex = new Map(
          rawResults
            .filter((item) => Number.isInteger(item?.index))
            .map((item) => [item.index, item])
        );

        for (let batchIndex = 0; batchIndex < jobsPayload.length; batchIndex += 1) {
          const { index: formIndex } = jobsPayload[batchIndex];
          const form = this.adminForms[formIndex];
          if (!form) continue;

          const resultItem = resultByBatchIndex.get(batchIndex);
          if (resultItem?.success && resultItem?.id) {
            successCount += 1;
            form.successMessage = this.tt("makeClips.clipQueuedSuccess", "Clip encolado correctamente.");

            if (this.canAssignOnCreate && this.selectedAssignee) {
              try {
                await this.reassignCreatedJob(resultItem.id, this.selectedAssignee);
              } catch (reassignError) {
                form.errorMessage = reassignError?.message || this.tt("makeClips.clipQueuedButReassignFailed", "El clip se encoló, pero no se pudo reasignar.");
              }
            }
          } else {
            form.errorMessage = resultItem?.error || this.tt("makeClips.clipQueueItemError", "No se pudo encolar este clip.");
          }
        }

        const failedCount = jobsPayload.length - successCount;
        if (failedCount === 0) {
          this.adminBulkSuccessMessage = this.tt("makeClips.bulkQueueAllSuccess", "Se encolaron {count} clip{suffix} correctamente.", { count: successCount, suffix: successCount === 1 ? "" : "s" });
        } else if (successCount > 0) {
          this.adminBulkErrorMessage = this.tt("makeClips.bulkQueuePartialError", "Se encolaron {success} clip{suffix} y {failed} fallaron.", { success: successCount, suffix: successCount === 1 ? "" : "s", failed: failedCount });
        } else {
          this.adminBulkErrorMessage = this.tt("makeClips.bulkQueueNone", "No se pudo encolar ningún clip. Revisa los errores en cada tarjeta.");
        }

        if (successCount > 0) {
          await this.refreshCurrentUserContext();
          await this.refreshEmbeddedScheduleSilently();
          return;
        }
      } finally {
        this.adminBulkSubmitting = false;
      }
    },
    async submitJob() {
      if (this.loading) return false;
      if (this.inputMode === "url" && !this.isValidUrl) return false;
      if (this.inputMode === "file" && !this.selectedFile) return false;
      if (!this.segmentsValidation.valid) return false;
      if (!this.hasRequiredAssignee) return false;
      if (!this.isKnownVideoType(this.adminVideoType)) return false;

      this.createFlowSubmitError = "";
      this.loading = true;
      let succeeded = false;
      let createdJobId = null;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error(this.tt("makeClips.authRequired", "Debes iniciar sesión."));
        }

        const requestBody = {
          video_type: this.adminVideoType,
          subtitles_enabled: Boolean(this.subtitlesEnabled),
        };
        const trimmedUserContext = (this.userContext || "").trim();
        if (
          this.adminVideoType === "irl"
          && this.irlUserContextEnabled
          && trimmedUserContext
        ) {
          requestBody.user_context = trimmedUserContext;
        }

        if (this.segments.length > 0) {
          requestBody.segments = this.segments.map(seg => ({
            start: seg.start,
            end: seg.end,
          }));
        }

        let jobPayload;
        if (this.inputMode === "file") {
          jobPayload = await this.reserveTwitchUpload(session, requestBody);
          createdJobId = jobPayload?.job_id || null;
        } else {
          const detectedSourceType = this.urlValidation.sourceType;
          if (!detectedSourceType) {
            throw new Error(this.tt("makeClips.detectPlatformError", "No se pudo detectar la plataforma del video."));
          }
          jobPayload = await createTwitchJob(session, {
            ...requestBody,
            source_url: this.normalizeUrl(this.videoUrl, detectedSourceType),
            source_type: detectedSourceType,
            options: {},
          });
          createdJobId = jobPayload?.id || null;
        }

        if (
          this.canAssignOnCreate
          && this.selectedAssignee
          && !(this.inputMode === "file" && this.activeUploadSession?.reassignAttempted)
        ) {
          if (this.inputMode === "file" && this.activeUploadSession) {
            this.activeUploadSession.reassignAttempted = true;
          }
          try {
            await this.reassignCreatedJob(createdJobId, this.selectedAssignee);
          } catch (reassignError) {
            // El job ya se creó: avisar sin bloquear ni invitar a reintentar (evita duplicados).
            console.error("Failed to reassign created job:", reassignError);
            if (typeof this.$notify === "function") {
              this.$notify({
                message: this.tt("makeClips.flowReassignFailed", "El video se encoló, pero no se pudo asignar al usuario seleccionado."),
                type: "warning",
              });
            }
          }
        }

        if (this.inputMode === "file") {
          await this.uploadReservedMp4();
        }

        succeeded = true;
        await this.refreshCurrentUserContext();
        await this.refreshEmbeddedScheduleSilently();
      } catch (error) {
        console.error("Failed to queue job:", error);
        this.createFlowSubmitError = this.inputMode === "file"
          ? this.getUploadErrorMessage(error)
          : (error?.message
            || this.tt("makeClips.flowSubmitError", "No pudimos encolar tu video. Inténtalo de nuevo."));
      } finally {
        this.clearUploadPollTimer();
        this.loading = false;
      }
      return succeeded ? (createdJobId || true) : null;
    },
    async reserveTwitchUpload(session, requestBody) {
      if (this.activeUploadSession) return this.activeUploadSession;
      this.uploadMessage = this.tt("makeClips.uploadReserving", "Reservando subida...");
      const payload = {
        file_name: this.selectedFile.name,
        file_size: this.selectedFile.size,
        ...requestBody,
      };
      const uploadSession = await createTwitchUploadSession(session, payload);
      if (
        !uploadSession?.upload_id
        || !uploadSession?.job_id
        || !uploadSession?.upload_token
        || !uploadSession?.upload_url
        || !Number.isFinite(Number(uploadSession?.chunk_size_bytes))
        || Number(uploadSession.chunk_size_bytes) <= 0
      ) {
        throw this.createUploadError("upload_incomplete");
      }
      this.activeUploadSession = {
        ...uploadSession,
        reassignAttempted: false,
      };
      this.uploadQueuePosition = uploadSession.queue_position ?? null;
      return this.activeUploadSession;
    },
    async uploadReservedMp4() {
      const uploadSession = this.activeUploadSession;
      if (!uploadSession || !this.selectedFile) {
        throw this.createUploadError("upload_incomplete");
      }

      let uploadState;
      while (true) {
        uploadState = await fetchTwitchUploadState(
          uploadSession.upload_url,
          uploadSession.upload_token
        );
        const status = String(uploadState?.status || "").trim().toLowerCase();
        this.uploadQueuePosition = uploadState?.queue_position ?? null;
        if (status === "complete" || status === "completed") {
          this.uploadProgress = 100;
          this.uploadMessage = this.tt("makeClips.uploadComplete", "Subida completada.");
          return;
        }
        if (status === "uploading") break;
        if (status !== "queued" && status !== "waiting") {
          throw this.createUploadError(
            uploadState?.detail?.code || uploadState?.code || "upload_waiting",
            uploadState?.detail?.message || uploadState?.message || ""
          );
        }
        this.uploadMessage = this.uploadQueuePosition == null
          ? this.tt("makeClips.uploadWaiting", "La subida sigue esperando disponibilidad.")
          : this.tt(
            "makeClips.uploadQueuePosition",
            "Posición en cola: {position}",
            { position: this.uploadQueuePosition }
          );
        await this.waitForUploadPoll();
      }

      this.uploadQueuePosition = null;
      this.uploadMessage = this.tt("makeClips.uploadUploading", "Subiendo MP4...");
      const totalBytes = this.selectedFile.size;
      const chunkSize = Number(uploadSession.chunk_size_bytes);
      let offset = Number(uploadState?.received_bytes);
      if (!Number.isFinite(offset) || offset < 0 || offset > totalBytes) {
        throw this.createUploadError("upload_incomplete");
      }
      this.uploadProgress = Math.floor((offset / totalBytes) * 100);

      while (offset < totalBytes) {
        const chunk = this.selectedFile.slice(offset, Math.min(offset + chunkSize, totalBytes));
        await uploadTwitchFileChunk(
          uploadSession.upload_url,
          uploadSession.upload_token,
          chunk,
          offset,
          totalBytes
        );
        offset += chunk.size;
        this.uploadProgress = Math.min(100, Math.floor((offset / totalBytes) * 100));
      }

      this.uploadMessage = this.tt("makeClips.uploadCompleting", "Finalizando subida...");
      await completeTwitchUpload(uploadSession.upload_url, uploadSession.upload_token);
      this.uploadProgress = 100;
      this.uploadMessage = this.tt("makeClips.uploadComplete", "Subida completada.");
    },
    normalizeProcessingStatus(status) {
      return String(status || "").toLowerCase().trim();
    },
    isProcessingRenderingStatus(status) {
      return this.normalizeProcessingStatus(status) === "5_rendering";
    },
    isProcessingTerminalStatus(status) {
      const normalized = this.normalizeProcessingStatus(status);
      return ["6_rendered", "7_scheduling", "8_published"].includes(normalized);
    },
    isProcessingFailedStatus(status) {
      const normalized = this.normalizeProcessingStatus(status);
      return (
        normalized === "expired"
        || normalized === "2.1_failed"
        || normalized === "2_failed"
        || normalized === "failed"
        || normalized.endsWith("_failed")
      );
    },
    enterProcessingPhase(jobId) {
      const durationSeconds = this.processingDurationSeconds;
      const sizeBytes = Number(this.metadataPreview?.size_bytes) || this.estimateSourceSizeBytes(durationSeconds);
      this.processingSnapshot = {
        thumbnail: this.flowThumbnail,
        title: this.flowVideoTitle,
        iconUrl: this.sourceTypeIconUrl,
        label: this.detectedSourceLabel,
        sizeBytes,
        durationSeconds,
        videoType: this.adminVideoType || "gameplay",
      };
      this.processingJobId = jobId;
      this.processingJobStatus = "1_queued";
      this.processingPollErrorCount = 0;
      this.processingStartedAt = Date.now();
      this.clearCreateFlowSuccessMessage();
      this.resetUploadState();
      this.videoUrl = "";
      this.segments = [];
      this.nextSegmentId = 1;
      this.adminVideoType = "";
      this.irlUserContextEnabled = false;
      this.userContext = "";
      this.metadataPreview = null;
      this.metadataPreviewError = "";
      this.flowUrlAutoKey = "";
      this.flowValidatedKey = "";
      this.resetTypeChooseChoreo();
      this.createFlowSubmitError = "";
      this.clearUrlValidationUi();
      this.flowPhase = "processing";
      this.persistProcessingSession();
      this.scheduleProcessingPoll(1200);
    },
    clearProcessingPollTimer() {
      if (this.processingPollTimerId != null) {
        clearTimeout(this.processingPollTimerId);
        this.processingPollTimerId = null;
      }
    },
    scheduleProcessingPoll(delayMs = 4000) {
      this.clearProcessingPollTimer();
      this.processingPollTimerId = setTimeout(() => {
        this.processingPollTimerId = null;
        this.pollProcessingJobStatus();
      }, delayMs);
    },
    async pollProcessingJobStatus() {
      if (this.flowPhase !== "processing" || !this.processingJobId) return;

      const PROCESSING_TIMEOUT_MS = 20 * 60 * 1000;
      if (this.processingStartedAt && (Date.now() - this.processingStartedAt) > PROCESSING_TIMEOUT_MS) {
        this.finishProcessingPhase();
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          this.finishProcessingPhase();
          return;
        }

        const jobsResponse = await fetchTwitchJobs(session, { days: 1 });
        const jobsList = Array.isArray(jobsResponse)
          ? jobsResponse
          : (Array.isArray(jobsResponse?.jobs) ? jobsResponse.jobs : []);
        const job = jobsList.find((item) => String(item?.id) === String(this.processingJobId));
        this.processingPollErrorCount = 0;

        if (this.flowPhase !== "processing") return;

        if (!job) {
          this.scheduleProcessingPoll();
          return;
        }

        this.processingJobStatus = job.status || this.processingJobStatus;
        this.persistProcessingSession();

        if (this.isProcessingFailedStatus(this.processingJobStatus)) {
          if (typeof this.$notify === "function") {
            this.$notify({
              message: this.tt(
                "makeClips.flowProcessingFailedNotice",
                "Hubo un problema procesando tu video. Revisa tus colecciones para más detalles.",
              ),
              type: "danger",
            });
          }
          this.finishProcessingPhase();
          return;
        }

        if (this.isProcessingTerminalStatus(this.processingJobStatus)) {
          this.finishProcessingPhase();
          return;
        }

        this.scheduleProcessingPoll();
      } catch (error) {
        console.error("Failed to poll job status:", error);
        if (this.flowPhase !== "processing") return;
        this.processingPollErrorCount += 1;
        if (this.processingPollErrorCount >= 5) {
          this.finishProcessingPhase();
          return;
        }
        this.scheduleProcessingPoll();
      }
    },
    startProcessingEtaTimer() {
      if (typeof window === "undefined") return;
      this.stopProcessingEtaTimer();
      this.processingEtaNowMs = Date.now();
      this.processingEtaTimerId = window.setInterval(() => {
        this.processingEtaNowMs = Date.now();
      }, 1000);
    },
    stopProcessingEtaTimer() {
      if (!this.processingEtaTimerId) return;
      window.clearInterval(this.processingEtaTimerId);
      this.processingEtaTimerId = null;
    },
    buildProcessingEtaMessage() {
      const snapshot = this.processingSnapshot;
      if (!snapshot) return "";

      const durationSeconds = Number(snapshot.durationSeconds) || 0;
      const contentType = String(snapshot.videoType || "gameplay").trim().toLowerCase();
      const profile = PROCESSING_ETA_PROFILES[contentType];
      if (!durationSeconds || !profile) return "";

      const vodMinutes = durationSeconds / 60;
      const prepSeconds = Math.max(1, Math.ceil(vodMinutes * profile.prepSecondsPerMinute));
      const plannedClipCount = Math.max(1, Math.ceil(vodMinutes / profile.minutesPerPlannedClip));
      const renderSeconds = Math.max(1, Math.ceil(plannedClipCount * profile.renderSecondsPerClip));
      const estimatedTotalSeconds = prepSeconds + renderSeconds;

      const elapsedSeconds = this.processingStartedAt
        ? Math.max(0, Math.floor((this.processingEtaNowMs - this.processingStartedAt) / 1000))
        : 0;

      const status = this.normalizeProcessingStatus(this.processingJobStatus);
      let remainingSeconds = Math.max(0, estimatedTotalSeconds - elapsedSeconds);

      if (status === "1_queued") {
        remainingSeconds = estimatedTotalSeconds;
      } else if (status === "5_rendering") {
        const renderElapsed = Math.max(0, elapsedSeconds - prepSeconds);
        const renderProgress = Math.min(1, renderElapsed / renderSeconds);
        remainingSeconds = Math.max(0, Math.ceil(renderSeconds * (1 - renderProgress)));
      }

      if (remainingSeconds < 60) return "Menos de 1 min restante";
      return `Aprox. ${Math.ceil(remainingSeconds / 60)} min restantes`;
    },
    estimateSourceSizeBytes(durationSeconds) {
      const duration = Number(durationSeconds) || 0;
      if (duration <= 0) return 0;
      const DEFAULT_BITRATE_KBPS = 6000;
      return Math.round((DEFAULT_BITRATE_KBPS * 1000) / 8 * duration);
    },
    persistProcessingSession() {
      if (typeof window === "undefined") return;
      if (this.flowPhase !== "processing" || !this.processingJobId) {
        this.clearProcessingSession();
        return;
      }
      try {
        window.localStorage.setItem(MAKE_CLIPS_PROCESSING_STORAGE_KEY, JSON.stringify({
          processingJobId: this.processingJobId,
          processingJobStatus: this.processingJobStatus,
          processingStartedAt: this.processingStartedAt,
          processingSnapshot: this.processingSnapshot,
        }));
      } catch (error) {
        console.error("Failed to persist processing session:", error);
      }
    },
    clearProcessingSession() {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.removeItem(MAKE_CLIPS_PROCESSING_STORAGE_KEY);
      } catch (error) {
        console.error("Failed to clear processing session:", error);
      }
    },
    restoreProcessingSession() {
      if (typeof window === "undefined") return false;
      if (this.isAdminRole) {
        this.clearProcessingSession();
        return false;
      }
      try {
        const raw = window.localStorage.getItem(MAKE_CLIPS_PROCESSING_STORAGE_KEY);
        if (!raw) return false;

        const saved = JSON.parse(raw);
        const jobId = String(saved?.processingJobId || "").trim();
        if (!jobId) {
          this.clearProcessingSession();
          return false;
        }

        this.processingJobId = jobId;
        this.processingJobStatus = saved?.processingJobStatus || "1_queued";
        this.processingStartedAt = Number(saved?.processingStartedAt) || Date.now();
        this.processingSnapshot = saved?.processingSnapshot || null;
        this.processingPollErrorCount = 0;
        this.videoUrl = "";
        this.resetUploadState();
        this.metadataPreview = null;
        this.metadataPreviewError = "";
        this.flowUrlAutoKey = "";
        this.flowValidatedKey = "";
        this.clearUrlValidationUi();
        this.clearCreateFlowSuccessMessage();
        this.createFlowSubmitError = "";
        this.flowPhase = "processing";
        this.startProcessingEtaTimer();
        this.scheduleProcessingPoll(200);
        return true;
      } catch (error) {
        console.error("Failed to restore processing session:", error);
        this.clearProcessingSession();
        return false;
      }
    },
    finishProcessingPhase() {
      this.clearProcessingPollTimer();
      this.stopProcessingEtaTimer();
      this.clearProcessingSession();
      this.processingJobId = null;
      this.processingJobStatus = "";
      this.processingSnapshot = null;
      this.processingStartedAt = null;
      this.resetCreateFlowAfterSubmit();
    },
  },
};
</script>

<style scoped>
/* Page container */
.make-clips-page {
  position: relative;
  background: transparent;
  color: var(--clipr-text-primary);
  padding: 0;
  --make-clips-schedule-gap: 6px;
  --make-flow-viewport-height: 304px;
  --make-flow-url-input-height: 52px;
  --make-flow-animation-height: 248px;
}

.make-clips-page-content {
  position: relative;
  z-index: 1;
}

.make-clips-page--library-focused {
  height: 100dvh;
  min-height: 100vh;
  background: #0f1115;
}

.make-clips-page--library-focused .make-clips-page-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.url-icons-preload {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Hero */
.make-clips-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 32px;
  animation: fadeInUp 0.6s ease-out;
}

.hero-icon-inline {
  font-size: 32px;
  color: var(--clipr-purple-light);
}

.hero-title {
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 600;
  color: var(--clipr-text-primary);
  letter-spacing: -0.02em;
  margin: 0;
  line-height: 1.2;
}

/* Card - transparente para integrarse con DashboardLayout y TopNavbar */
.make-clips-page .card {
  background: transparent;
  border-radius: var(--clipr-radius-xl);
  border: none;
  box-shadow: none;
  backdrop-filter: none;
  color: var(--clipr-text-primary);
  transition: all 0.35s ease;
  animation: fadeInUp 0.6s ease-out 0.1s both;
}

.make-clips-page .main-card {
  max-width: 1100px;
  margin: 0 auto;
  min-height: auto;
}

.make-clips-page .main-card.main-card--flow-locked .card-content {
  min-height: var(--make-flow-viewport-height);
  padding: 18px 40px 0;
}

.make-clips-page .main-card .card-content {
  padding-bottom: 0;
}

.make-clips-library-section {
  --make-clips-library-space: var(--make-clips-schedule-gap);
  --make-clips-reference-gap: var(--make-clips-schedule-gap);
  width: 100%;
  max-width: none;
  margin: var(--make-clips-schedule-gap) 0 0;
  padding: 0 var(--make-clips-library-space) var(--make-clips-library-space);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--make-clips-library-space);
}

.make-clips-library-section--flow-top {
  margin-top: 0;
}

.make-clips-library-section--focused {
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  padding: 0;
}

.make-clips-library-section--focused .make-clips-schedule-embed {
  height: 100%;
}

.make-clips-library-section > * {
  width: 100%;
  margin: 0;
}

.make-clips-schedule-embed {
  width: 100%;
}

.make-clips-library-section :deep(.schedule-page--embedded) {
  --schedule-embedded-gap: var(--make-clips-library-space);
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.make-clips-library-section :deep(.schedule-page--embedded > *) {
  margin: 0;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row) {
  margin: 0;
}

.make-clips-library-section :deep(.schedule-page--embedded > .schedule-detail-nav-wrap),
.make-clips-library-section :deep(.schedule-page--embedded > .schedule-row.schedule-row-videos) {
  margin-bottom: 25px !important;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches) {
  margin-bottom: 25px !important;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline.assign-cycle-timeline) {
  margin-top: var(--make-clips-reference-gap) !important;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches) {
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: linear-gradient(165deg, rgba(10, 14, 23, 0.9) 0%, rgba(15, 19, 29, 0.72) 100%);
  box-shadow: 0 14px 32px rgba(2, 6, 23, 0.35);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos) {
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.make-clips-library-section--focused :deep(.schedule-page--embedded),
.make-clips-library-section--focused :deep(.schedule-page--embedded > .schedule-row.schedule-row-videos),
.make-clips-library-section--focused :deep(.schedule-page--embedded .schedule-row-body) {
  height: 100%;
  min-height: 0;
}

.make-clips-library-section--focused :deep(.schedule-page--embedded > .schedule-row.schedule-row-videos) {
  flex: 1 1 auto;
  margin: 0 !important;
}

.make-clips-library-section--focused :deep(.clip-workspace) {
  height: 100%;
  min-height: 0;
  max-height: none;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 0;
}

.make-clips-library-section--focused :deep(.schedule-page--embedded:has(.schedule-editor-page)) {
  overflow-x: hidden;
  overflow-y: auto;
}

.make-clips-library-section--focused :deep(.schedule-editor-page) {
  width: 100%;
  max-width: 100%;
  margin: 0;
  flex: 1 1 auto;
  min-height: 0;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline.assign-cycle-timeline) {
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(139, 92, 246, 0.34);
  background: linear-gradient(165deg, rgba(18, 13, 31, 0.88) 0%, rgba(15, 18, 28, 0.76) 100%);
  box-shadow:
    0 14px 32px rgba(2, 6, 23, 0.35),
    0 0 0 1px rgba(168, 85, 247, 0.12),
    0 0 20px rgba(168, 85, 247, 0.06);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .schedule-row-header),
.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .schedule-row-header) {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .calendar-timeline-header) {
  margin-bottom: 14px;
  padding-left: 4px;
  padding-right: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .schedule-timeline-posting-date-label) {
  margin-bottom: 4px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--clipr-text-primary);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .schedule-timeline-posting-date-label i) {
  color: var(--clipr-purple-light);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .calendar-timeline-month) {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--clipr-text-tertiary);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .calendar-timeline-body) {
  min-height: 132px;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-days--posting) {
  gap: 10px;
  padding: 4px 2px 6px;
  scrollbar-color: rgba(167, 139, 250, 0.75) rgba(15, 23, 42, 0.55);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-days--posting::-webkit-scrollbar-track) {
  background: rgba(15, 23, 42, 0.55);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-days--posting::-webkit-scrollbar-thumb) {
  background: linear-gradient(90deg, rgba(167, 139, 250, 0.9), rgba(124, 58, 237, 0.9));
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-card) {
  min-height: 128px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(168, 85, 247, 0.2);
  background: rgba(15, 23, 42, 0.52);
  box-shadow: none;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-card.is-today:not(.is-selected)) {
  border-color: rgba(168, 85, 247, 0.42);
  background: linear-gradient(
    135deg,
    rgba(168, 85, 247, 0.14) 0%,
    rgba(15, 23, 42, 0.58) 100%
  );
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-card.has-events) {
  border-color: rgba(168, 85, 247, 0.34);
  background: linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.58) 0%,
    rgba(168, 85, 247, 0.16) 100%
  );
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-card:hover) {
  transform: translateY(-2px);
  border-color: rgba(168, 85, 247, 0.45);
  box-shadow: 0 8px 20px rgba(2, 6, 23, 0.35), 0 0 0 1px rgba(168, 85, 247, 0.12);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-name) {
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--clipr-text-tertiary);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-number) {
  font-size: 22px;
  font-weight: 700;
  color: var(--clipr-text-primary);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-card.is-today .timeline-day-number) {
  color: var(--clipr-purple-light);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-empty) {
  font-size: 9px;
  letter-spacing: 0.04em;
  line-height: 1.35;
  color: var(--clipr-text-tertiary);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-empty i) {
  color: var(--clipr-purple-light);
  opacity: 0.55;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-event-pill) {
  background: var(--clipr-purple-gradient);
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-event-pill.is-posted) {
  background: linear-gradient(135deg, #22c55e 0%, #15803d 100%);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-loading-state) {
  border-color: rgba(168, 85, 247, 0.22);
  background: linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.58) 0%,
    rgba(168, 85, 247, 0.12) 100%
  );
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .schedule-row-title),
.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .schedule-row-title) {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .schedule-row-subtitle),
.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .schedule-row-subtitle) {
  color: var(--clipr-text-tertiary);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .schedule-collections-filters) {
  margin-bottom: 14px;
  padding: 0;
  border: 0;
  background: transparent;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .schedule-clips-filters-label) {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--clipr-text-tertiary);
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row-header),
.make-clips-library-section :deep(.schedule-page--embedded .schedule-row-body),
.make-clips-library-section :deep(.schedule-page--embedded .schedule-collections-filters) {
  padding-left: 4px;
  padding-right: 4px;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .scroll-arrow-btn) {
  display: none;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .collections-row-scroll) {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow-x: visible;
  overflow-y: visible;
  scroll-snap-type: none;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .collection-item) {
  width: auto;
  min-width: 0;
  max-width: none;
  scroll-snap-align: none;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .scroll-arrow-btn) {
  display: none;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .collections-row-scroll.videos-strip) {
  display: flex;
  flex-wrap: wrap;
  gap: 17px;
  justify-content: flex-start;
  align-items: stretch;
  padding: 15px;
  overflow-x: hidden;
  overflow-y: hidden;
  scroll-snap-type: none;
}

.make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .video-card) {
  flex: 0 0 200px;
  align-self: stretch;
  width: 200px;
  min-width: 0;
  max-width: 200px;
}

@media (max-width: 1399px) {
  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .video-card) {
    flex-basis: 200px;
    width: 200px;
    max-width: 200px;
  }
}

@media (max-width: 1199px) {
  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .video-card) {
    flex-basis: 200px;
    width: 200px;
    max-width: 200px;
  }
}

@media (max-width: 1199px) {
  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .collections-row-scroll) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .collections-row-scroll) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .video-card) {
    flex-basis: min(100%, 300px);
    width: min(100%, 300px);
    max-width: 300px;
  }
}

.admin-forms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(560px, 1fr));
  gap: 20px;
  align-items: start;
}

.make-clips-page .admin-main-card {
  max-width: none;
  min-height: auto;
  margin: 0;
}

.make-clips-page .admin-submit-card {
  max-width: 420px;
  min-height: auto;
  margin: 20px auto 0;
}

.admin-submit-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 22px;
  padding-bottom: 22px;
}

.admin-card-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.admin-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--clipr-text-primary);
}

.admin-add-card-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--clipr-purple-overlay-accent);
  background: var(--clipr-purple-overlay-hover);
  color: var(--clipr-text-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.admin-add-card-btn:hover:not(:disabled) {
  border-color: var(--clipr-purple-overlay-accent);
  background: var(--clipr-purple-overlay-intense);
}

.admin-add-card-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.admin-simple-segments {
  margin-top: 10px;
}

.admin-simple-segment-row {
  display: grid;
  grid-template-columns: auto minmax(120px, 1fr) auto minmax(120px, 1fr) auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}

.admin-simple-segment-index {
  color: var(--clipr-text-secondary);
  font-size: 12px;
  font-weight: 700;
}

.admin-simple-time-input {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--clipr-border-color);
  background: rgba(10, 10, 12, 0.85);
  color: var(--clipr-text-primary);
  font-size: 14px;
}

.admin-simple-time-input:focus {
  outline: none;
  border-color: var(--clipr-border-purple);
  box-shadow: 0 0 0 2px var(--clipr-purple-overlay-focus);
}

.admin-simple-separator {
  color: var(--clipr-text-tertiary);
}

.admin-success-hint {
  color: #66d19e;
}

.admin-error-hint {
  color: #ff8080;
}

.make-clips-page .card-content {
  padding: 36px 40px 40px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}


.make-clips-page .card-content form {
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* Form sections */
.form-section {
  margin-bottom: 32px;
}

.form-section:last-of-type {
  margin-bottom: 0;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--clipr-text-secondary);
  margin-bottom: 14px;
}

.section-label-with-action {
  justify-content: space-between;
  gap: 16px;
}

.section-label-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.segments-section-label {
  color: var(--clipr-text-primary);
  font-size: 15px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}

.segments-inline-trigger-wrap {
  margin-top: 10px;
  text-align: center;
}

.segments-inline-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--clipr-text-tertiary);
  font-size: 13px;
  line-height: 1.45;
  cursor: pointer;
  transition: color 0.2s ease, opacity 0.2s ease;
}

.segments-inline-trigger:hover {
  color: var(--clipr-text-secondary);
}

.segments-inline-trigger-copy {
  color: var(--clipr-text-tertiary);
}

.segments-inline-trigger-action {
  color: var(--clipr-purple-light);
  font-weight: 600;
}

.segments-inline-trigger i {
  font-size: 12px;
  color: var(--clipr-purple-light);
}

.section-label-main-stacked {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.segments-heading-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.segments-summary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.segments-summary-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(168, 85, 247, 0.12);
  border: 1px solid rgba(168, 85, 247, 0.22);
  font-size: 12px;
  font-weight: 600;
  color: var(--clipr-purple-light);
}

.segments-helper-copy {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--clipr-text-tertiary);
  max-width: 64ch;
}

.section-label-actions--compact {
  align-self: flex-start;
}

.segments-link-toggle {
  padding: 0;
  background: transparent;
  border: none;
  color: var(--clipr-text-tertiary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;
}

.segments-link-toggle:hover {
  color: var(--clipr-purple-light);
}

.add-segment-header-btn {
  padding: 9px 14px;
  font-size: 13px;
  background: rgba(168, 85, 247, 0.12) !important;
  border: 1px solid rgba(168, 85, 247, 0.28) !important;
  box-shadow: none !important;
  color: var(--clipr-text-primary) !important;
}

.add-segment-header-btn:hover:not(:disabled) {
  background: rgba(168, 85, 247, 0.18) !important;
  border-color: rgba(168, 85, 247, 0.38) !important;
  box-shadow: none !important;
}

.section-label-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.assign-target-wrap {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.assign-target-btn {
  border: 1px solid var(--clipr-purple-overlay-border);
  background: var(--clipr-purple-overlay-focus);
  color: var(--clipr-text-primary);
  border-radius: 10px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s ease;
}

.assign-target-btn:hover:not(:disabled) {
  background: var(--clipr-purple-overlay-intense);
  border-color: var(--clipr-purple-overlay-accent);
}

.assign-target-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.assign-target-user {
  color: var(--clipr-purple-light);
  font-size: 12px;
  font-weight: 600;
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.section-label i {
  font-size: 16px;
  color: var(--clipr-purple-light);
  opacity: 0.9;
}

/* URL section — aligned with TryUrlForm */
.try-url-form.make-clips-url-form {
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  border-radius: 0;
}

.try-url-hero {
  text-align: center;
  margin: 0 auto 30px;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.try-url-hero-title {
  margin: 0;
  font-size: clamp(2rem, 4.6vw, 3rem);
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.03em;
  background: linear-gradient(120deg, #f8fafc 0%, #ddd6fe 46%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.try-url-hero-subtitle {
  margin: 0;
  max-width: 56ch;
  color: var(--clipr-text-secondary, #cbd5e1);
  font-size: clamp(0.98rem, 1.6vw, 1.12rem);
  line-height: 1.55;
  font-weight: 500;
}

.try-url-hero-subtitle :deep(.try-url-hero-subtitle-accent) {
  background: linear-gradient(105deg, #ddd6fe 0%, #c084fc 45%, #a855f7 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  font-weight: 600;
}

.url-section {
  margin-bottom: 18px;
  padding: 0;
  border: 0;
  background: transparent;
}

.url-section-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--clipr-text-primary, #f8fafc);
  margin-bottom: 14px;
}

.url-section-label-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.source-mode-toggle {
  display: inline-flex;
  padding: 3px;
  border: 1px solid rgba(196, 181, 253, 0.2);
  border-radius: 9px;
  background: rgba(9, 9, 11, 0.6);
}

.source-mode-toggle button {
  padding: 4px 9px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--clipr-text-tertiary, #94a3b8);
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.source-mode-toggle button.active {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.9), rgba(8, 145, 178, 0.85));
  color: #fff;
  box-shadow: 0 3px 10px rgba(124, 58, 237, 0.24);
}

.source-mode-toggle button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.mp4-file-input {
  display: none;
}

.mp4-dropzone {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 13px 18px;
  border: 1px dashed rgba(196, 181, 253, 0.42);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(88, 28, 135, 0.12), rgba(8, 145, 178, 0.08));
  color: var(--clipr-text-secondary, #cbd5e1);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.mp4-dropzone:hover,
.mp4-dropzone--active {
  border-color: rgba(103, 232, 249, 0.72);
  background: linear-gradient(135deg, rgba(126, 34, 206, 0.18), rgba(6, 182, 212, 0.13));
  transform: translateY(-1px);
}

.mp4-dropzone--selected {
  border-style: solid;
}

.mp4-dropzone--disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.mp4-dropzone > i {
  color: #c4b5fd;
  font-size: 24px;
}

.mp4-dropzone-spinner {
  animation: make-flow-queue-spin 0.9s linear infinite;
}

.mp4-dropzone-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.mp4-dropzone-copy strong,
.mp4-dropzone-copy span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mp4-dropzone-copy strong {
  color: var(--clipr-text-primary, #f8fafc);
  font-size: 0.85rem;
}

.mp4-dropzone-copy span,
.mp4-requirements {
  color: var(--clipr-text-tertiary, #94a3b8);
  font-size: 0.72rem;
}

.mp4-requirements {
  margin: 7px 2px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.url-section-platform-icons {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 5px 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(9, 9, 11, 0.45);
}

.url-section-platform-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
  opacity: 0.92;
}

.url-input-unified {
  display: flex;
  align-items: stretch;
  background: rgba(9, 9, 11, 0.72);
  border: 1px solid rgba(196, 181, 253, 0.28);
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.url-input-unified:focus-within {
  border-color: rgba(34, 211, 238, 0.55);
  box-shadow:
    0 0 0 3px rgba(34, 211, 238, 0.12),
    0 12px 28px rgba(34, 211, 238, 0.08);
  transform: translateY(-1px);
}

.url-input-icon-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background: linear-gradient(180deg, rgba(168, 85, 247, 0.16) 0%, rgba(168, 85, 247, 0.08) 100%);
  border-right: 1px solid rgba(148, 163, 184, 0.12);
}

.url-input-icon-segment i {
  font-size: 17px;
  color: #c4b5fd;
}

.url-input-field {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
}

.url-input-unified .url-input {
  width: 100%;
  min-width: 0;
  padding: 16px 18px;
  font-size: 12px;
  background: transparent;
  border: none;
  color: var(--clipr-text-primary, #f8fafc);
}

.url-input-unified .url-input:focus {
  outline: none;
}

.url-input-unified .url-input::placeholder {
  color: var(--clipr-text-tertiary, #64748b);
}

.url-input-unified .url-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.url-input-badge-segment {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  border-left: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(15, 23, 42, 0.55);
}

.url-input-badge-segment i {
  font-size: 16px;
  color: inherit;
}

.url-badge-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}

.url-input-badge-segment--pending {
  color: var(--clipr-text-tertiary, #94a3b8);
}

.url-input-badge-segment--twitch {
  color: #a78bfa;
}

.url-input-badge-segment--youtube {
  color: #f87171;
}

.url-input-badge-segment--facebook {
  color: #60a5fa;
}

.url-input-badge-segment--kicks {
  color: #4ade80;
}

.url-input-badge-segment--google_drive {
  color: #86efac;
}

.url-section-error-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  padding: 11px 12px;
  border-radius: 12px;
  border: 1px solid rgba(248, 113, 113, 0.28);
  background:
    radial-gradient(circle at top left, rgba(248, 113, 113, 0.12), transparent 62%),
    rgba(69, 10, 10, 0.22);
}

.url-section-error-note-icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 113, 113, 0.14);
  border: 1px solid rgba(252, 165, 165, 0.24);
  color: #fca5a5;
  font-size: 0.9rem;
}

.url-section-error-note-icon i {
  font-size: 0.95rem;
  line-height: 1;
}

.url-section-error-note-copy {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.url-section-error-note-copy strong {
  color: #fecaca;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.35;
}

.url-section-error-note-copy span {
  color: #fda4af;
  font-size: 0.78rem;
  line-height: 1.45;
}

.url-section-drive-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  padding: 11px 12px;
  border-radius: 12px;
  border: 1px solid rgba(52, 211, 153, 0.24);
  background:
    radial-gradient(circle at top left, rgba(52, 211, 153, 0.12), transparent 62%),
    rgba(9, 9, 11, 0.55);
}

.url-section-drive-note-icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(52, 211, 153, 0.14);
  border: 1px solid rgba(134, 239, 172, 0.24);
  color: #86efac;
  font-size: 0.9rem;
}

.url-section-drive-note-copy {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.url-section-drive-note-copy strong {
  color: #bbf7d0;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.35;
}

.url-section-drive-note-copy span {
  color: #94a3b8;
  font-size: 0.78rem;
  line-height: 1.45;
}

.input-feedback {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 13px;
}

.input-feedback.error {
  color: #f87171;
}

.input-feedback.hint {
  color: var(--clipr-text-tertiary);
}

.input-feedback i {
  font-size: 14px;
  flex-shrink: 0;
}

.boolean-switch-field {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
}

.boolean-switch-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--clipr-text-secondary);
}

.boolean-switch {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  width: fit-content;
  max-width: 100%;
  padding: 4px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.25);
  gap: 4px;
}

.boolean-switch-option {
  padding: 10px 18px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--clipr-text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, opacity 0.2s;
}

.boolean-switch-option:hover:not(.boolean-switch-option-active):not(:disabled) {
  color: var(--clipr-text-primary);
}

.boolean-switch-option-active {
  background: #f1f5f9;
  color: #0f172a;
}

.boolean-switch-option:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .boolean-switch {
    width: 100%;
  }

  .boolean-switch-option {
    flex: 1 1 0;
    text-align: center;
  }
}

/* Video + segments layout */
.video-segments-layout {
  display: block;
  margin-top: 16px;
}

@media (max-width: 900px) {
  .video-segments-layout {
    display: block;
  }
}

.video-preview-col {
  min-width: 0;
}

.video-thumbnail-wrapper {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  background: var(--clipr-bg-elevated);
  border: 1px solid rgba(168, 85, 247, 0.35);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.2),
    0 0 24px var(--clipr-purple-glow),
    0 0 48px rgba(168, 85, 247, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.3);
}

.video-thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.video-thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--clipr-purple-light);
  font-size: 48px;
  opacity: 0.6;
}

.video-thumbnail-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: var(--clipr-text-primary);
  font-size: 24px;
}

.video-preview-info-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 40px 16px 16px 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.5) 40%, transparent 100%);
  text-align: left;
}

.video-preview-title {
  margin: 0 0 2px 0;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.video-preview-channel {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
}

.segments-col {
  min-width: 0;
}

/* Segment mockup style */
.segment-list-mockup {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.segment-item-mockup {
  width: 100%;
}

.segment-item-compact {
  padding: 14px 16px !important;
  background: rgba(11, 14, 25, 0.82);
  border: 1px solid rgba(168, 85, 247, 0.16);
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
}

.segment-row-compact {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.segment-row-compact .segment-name-input {
  flex: 1;
  min-width: 180px;
}

.segment-time-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--clipr-text-tertiary);
}

.segment-time-input {
  width: 112px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: monospace;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(168, 85, 247, 0.18);
  border-radius: 10px;
  color: var(--clipr-text-primary);
  transition: var(--clipr-transition);
}

.segment-time-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.segment-time-input:focus {
  outline: none;
  border-color: rgba(168, 85, 247, 0.42);
  box-shadow: 0 0 0 2px var(--clipr-purple-overlay-focus);
}

.segment-time-inline-group {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.segment-time-fields {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.segment-name-input {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid transparent;
  color: var(--clipr-text-primary);
  border-radius: 10px;
}

.segment-name-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.segment-name-input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(168, 85, 247, 0.28);
}

.segment-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}

.segment-action-btn {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  color: var(--clipr-text-tertiary);
  cursor: pointer;
  transition: var(--clipr-transition);
  font-size: 18px;
}

.segment-action-btn:hover:not(:disabled) {
  color: var(--clipr-purple-light);
  border-color: var(--clipr-border-purple);
  background: var(--clipr-purple-overlay-soft);
}

.segment-action-btn.segment-remove-btn:hover:not(:disabled) {
  color: #f87171;
  border-color: rgba(239, 68, 68, 0.4);
  background: rgba(239, 68, 68, 0.1);
}

.segment-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.segment-item-mockup .segment-action-btn {
  padding: 0;
}

.add-segment-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  background: rgba(168, 85, 247, 0.14);
  color: var(--clipr-text-primary);
  border: 1px solid rgba(168, 85, 247, 0.22);
  border-radius: 12px;
  cursor: pointer;
  transition: var(--clipr-transition);
  box-shadow: none;
}

.add-segment-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  background: rgba(168, 85, 247, 0.2);
  border-color: rgba(168, 85, 247, 0.34);
}

.add-segment-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Segments section */
.segments-section {
  margin-bottom: 28px;
  padding: 20px 22px;
  background: linear-gradient(180deg, rgba(13, 17, 30, 0.82) 0%, rgba(8, 11, 20, 0.72) 100%);
  border: 1px solid rgba(168, 85, 247, 0.12);
  border-radius: 20px;
}

.segments-empty-state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(168, 85, 247, 0.12);
}

.segments-empty-copy {
  min-width: 0;
}

.segments-empty-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 12px;
  font-weight: 600;
  color: var(--clipr-text-secondary);
}

.segments-empty-text {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--clipr-text-tertiary);
}

.add-segment-empty-btn {
  flex-shrink: 0;
}

.segments-rules-copy {
  margin: 0 0 14px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--clipr-text-tertiary);
}

.segment-index-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  height: 38px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(168, 85, 247, 0.14);
  border: 1px solid rgba(168, 85, 247, 0.2);
  color: var(--clipr-purple-light);
  font-size: 12px;
  font-weight: 700;
}

.segment-name-input-inline {
  flex: 1 1 220px;
}

.segment-time-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 40px;
  color: var(--clipr-text-tertiary);
}

.segment-duration-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(168, 85, 247, 0.12);
  border: 1px solid rgba(168, 85, 247, 0.18);
  color: var(--clipr-purple-light);
  font-family: monospace;
  font-size: 13px;
  font-weight: 700;
}

.segment-duration-chip--muted {
  color: var(--clipr-text-tertiary);
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
}

.make-clips-page .card-header {
  border-bottom: 1px solid var(--clipr-border-color);
  padding: 20px 20px 15px;
}

.make-clips-page .card-title {
  color: var(--clipr-text-primary);
  font-weight: 600;
  font-size: clamp(20px, 2.5vw, 24px);
  letter-spacing: -0.02em;
}

.make-clips-page .card-body {
  padding: 20px;
  color: var(--clipr-text-primary);
}

/* Text colors */
.make-clips-page .text-muted {
  color: var(--clipr-text-secondary) !important;
}

.make-clips-page label {
  color: var(--clipr-text-secondary);
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
  display: block;
}

.make-clips-page .small {
  color: var(--clipr-text-secondary);
  font-size: 13px;
}

/* Form controls dark theme */
.make-clips-page .form-control {
  background-color: rgba(15, 15, 18, 0.9);
  border: 1px solid var(--clipr-border-color);
  color: var(--clipr-text-primary);
  border-radius: var(--clipr-radius-lg);
  padding: 12px 16px;
  font-size: 15px;
  transition: var(--clipr-transition);
}

.make-clips-page .input-enhanced {
  padding: 8px 18px;
  font-size: 15px;
}

.make-clips-page .form-control:focus {
  outline: none;
  border-color: var(--clipr-border-purple);
  box-shadow: 0 0 0 3px var(--clipr-purple-glow);
  background-color: rgba(15, 15, 18, 0.95);
  transform: translateY(-1px);
}

.make-clips-page .form-control::placeholder {
  color: var(--clipr-text-tertiary);
}

.make-clips-page .form-control:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.make-clips-page .form-control[readonly] {
  background-color: rgba(15, 15, 18, 0.7);
  cursor: default;
}

.make-clips-page .form-control-sm {
  padding: 8px 12px;
  font-size: 14px;
}

/* Buttons dark theme */
.make-clips-page .btn {
  border-radius: var(--clipr-radius-lg);
  font-weight: 600;
  transition: var(--clipr-transition);
  border: none;
}

.make-clips-page .btn-success {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: #fff;
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.3);
}

.make-clips-page .btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
}

.make-clips-page .btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #fff;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.make-clips-page .btn-danger:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.make-clips-page .btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

/* P-Button component override */
.make-clips-page .p-button {
  background: var(--clipr-purple-gradient);
  color: #fff;
  box-shadow: 0 8px 30px var(--clipr-purple-glow);
  border: none;
  border-radius: var(--clipr-radius-lg);
  font-weight: 600;
  transition: var(--clipr-transition);
  padding: 14px 32px;
}

.make-clips-page .p-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--clipr-shadow-card-hover);
}

.make-clips-page .p-button--success {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.3);
}

.make-clips-page .p-button--success:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
}

.make-clips-page .p-button--info {
  background: var(--clipr-purple-gradient);
  box-shadow: 0 8px 30px var(--clipr-purple-glow);
}

.make-clips-page .p-button--info:hover:not(:disabled) {
  box-shadow: var(--clipr-shadow-card-hover);
}

.make-clips-page .p-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Alerts dark theme */
.make-clips-page .alert {
  border-radius: var(--clipr-radius-md);
  border: 1px solid;
  padding: 16px 20px;
  transition: var(--clipr-transition);
}

.make-clips-page .alert-info {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.08));
  color: #93c5fd;
  border-color: rgba(59, 130, 246, 0.35);
}

.make-clips-page .example-alert {
  border-left: 3px solid rgba(59, 130, 246, 0.5);
  padding: 18px 22px;
}

.make-clips-page .example-icon {
  font-size: 20px;
  color: #60a5fa;
}

.make-clips-page .alert-success {
  background-color: rgba(34, 197, 94, 0.15);
  color: #bbf7d0;
  border-color: rgba(34, 197, 94, 0.4);
}

.make-clips-page .success-alert {
  border-left: 3px solid rgba(34, 197, 94, 0.5);
}

.make-clips-page .alert strong {
  color: inherit;
  font-weight: 600;
}

/* Code elements */
.make-clips-page code {
  user-select: all;
  background-color: rgba(15, 15, 18, 0.9);
  color: var(--clipr-purple-light);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  border: 1px solid var(--clipr-border-color);
}

/* Error messages */
.make-clips-page .text-danger {
  color: #f87171 !important;
  font-size: 12px;
  display: block;
  margin-top: 4px;
}

/* Segment items */
.segments-container {
  margin-top: 16px;
  animation: fadeIn 0.4s ease-out;
}

.segments-scroll-wrap {
  max-height: 340px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.segments-scroll-wrap::-webkit-scrollbar {
  width: var(--clipr-scrollbar-size);
}

.segments-scroll-wrap::-webkit-scrollbar-track {
  background: var(--clipr-scrollbar-track);
  border-radius: 999px;
}

.segments-scroll-wrap::-webkit-scrollbar-thumb {
  background: var(--clipr-scrollbar-thumb);
  border-radius: 999px;
  border: 2px solid var(--clipr-scrollbar-thumb-border);
}

.segments-scroll-wrap::-webkit-scrollbar-thumb:hover {
  background: var(--clipr-scrollbar-thumb-hover);
}

.segment-col {
  min-width: 0; /* permite que flex/grid funcione bien */
}

.segment-item {
  background: linear-gradient(135deg, rgba(18, 18, 24, 0.95), rgba(12, 12, 16, 0.9));
  border-radius: var(--clipr-radius-lg);
  border: 1px solid var(--clipr-border-color);
  padding: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.segment-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: var(--clipr-purple-gradient);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: var(--clipr-radius-lg) 0 0 var(--clipr-radius-lg);
}

.segment-item:hover::before {
  opacity: 1;
}

.segment-item:hover {
  border-color: var(--clipr-purple-overlay-strong);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px var(--clipr-purple-overlay-focus);
  transform: translateY(-2px);
}

/* Asegura que el contenido del segmento se muestre completo */
.segment-item .segment-time-block {
  flex: 0 0 auto;
}

.segment-item .time-picker {
  flex-wrap: nowrap;
}

/* Segment animations */
.segment-list-enter-active,
.segment-list-leave-active {
  transition: all 0.4s ease;
}

.segment-list-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.segment-list-leave-to {
  opacity: 0;
  transform: translateX(-20px) scale(0.95);
}

.segment-list-move {
  transition: transform 0.4s ease;
}

.gap-2 {
  gap: 0.5rem;
}

/* Intro section */
.intro-section {
  padding: 20px;
  background: linear-gradient(135deg, var(--clipr-purple-overlay-light), rgba(79, 70, 229, 0.08));
  border-radius: var(--clipr-radius-lg);
  border: 1px solid var(--clipr-border-color);
  margin-bottom: 24px;
}

.intro-title {
  color: var(--clipr-text-primary);
  font-weight: 600;
  font-size: 18px;
  display: flex;
  align-items: center;
}

.steps-guide {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--clipr-purple-gradient);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}

.step-text {
  color: var(--clipr-text-secondary);
  font-size: 14px;
}

/* Form labels with help */
.form-label-with-help {
  display: flex;
  align-items: center;
  gap: 8px;
}

.help-icon {
  color: var(--clipr-text-tertiary);
  font-size: 14px;
  cursor: help;
  transition: color 0.2s ease;
}

.help-icon:hover {
  color: var(--clipr-purple-light);
}

.help-text {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 12px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 12px;
}

.error-message i {
  font-size: 14px;
}


.segment-description {
  font-size: 13px;
  line-height: 1.5;
}

.segments-header {
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--clipr-border-color);
  transition: var(--clipr-transition);
}

.segments-header.collapsible:hover {
  opacity: 0.8;
}

.segments-toggle-icon {
  color: var(--clipr-text-secondary);
  font-size: 18px;
  transition: transform 0.3s ease, color 0.2s ease;
}

.segments-toggle-icon.rotated {
  transform: rotate(-90deg);
}

.segments-header.collapsible:hover .segments-toggle-icon {
  color: var(--clipr-purple-light);
}

.segments-count {
  color: var(--clipr-text-secondary);
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}


.segment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--clipr-border-color);
}

.segment-number {
  color: var(--clipr-purple-light);
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.segment-remove-btn {
  padding: 4px 8px;
}

.segment-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-weight: 600;
  justify-content: center;
  text-align: center;
}

.segment-label i {
  font-size: 13px;
  color: var(--clipr-purple-light);
}

.segment-time-block + .segment-time-block {
  margin-top: 12px;
}

.segment-duration {
  text-align: center;
}

.segment-input {
  transition: all 0.2s ease;
}

.segment-input:focus {
  border-color: var(--clipr-border-purple);
  box-shadow: 0 0 0 2px var(--clipr-purple-glow);
}

.format-hint {
  display: block;
  margin-top: 6px;
  font-size: 11px;
  opacity: 0.8;
}

.segment-duration-display {
  padding-top: 8px;
}

.duration-badge {
  background: var(--clipr-purple-overlay-soft);
  border: 1px solid var(--clipr-purple-overlay-hover);
  border-radius: 6px;
  padding: 6px 10px;
  display: inline-block;
}

.segment-errors {
  margin-top: 12px;
  padding: 10px 12px;
  background-color: rgba(239, 68, 68, 0.1);
  border-radius: 12px;
  border-left: 3px solid #ef4444;
}

.segment-errors .error-message {
  color: #f87171;
  margin-bottom: 6px;
}

.segment-errors .error-message:last-child {
  margin-bottom: 0;
}

.add-first-segment-btn {
  padding: 12px 24px;
}

/* Submit section */
.submit-section {
  margin-top: auto;
  padding-top: 36px;
  border-top: 1px solid var(--clipr-border-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 4px;
}

.submit-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.submit-section-inline {
  margin-top: 24px;
  padding-top: 0;
  border-top: none;
}

.submit-section-inline .submit-wrapper {
  align-items: stretch;
}

.submit-section-inline .submit-btn {
  width: 100%;
  min-width: 0;
}

.submit-btn {
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 700;
  background: var(--clipr-purple-gradient) !important;
  box-shadow: var(--clipr-shadow-button), 0 0 20px var(--clipr-purple-glow) !important;
  border: none !important;
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--clipr-shadow-button-hover), 0 0 28px var(--clipr-purple-glow) !important;
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.submit-section-primary {
  margin-top: 28px;
}

.submit-rail {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px;
  border-radius: 22px;
  border: 1px solid rgba(168, 85, 247, 0.2);
  background:
    radial-gradient(circle at top left, rgba(168, 85, 247, 0.18), transparent 42%),
    linear-gradient(135deg, rgba(15, 18, 32, 0.96) 0%, rgba(9, 12, 22, 0.92) 100%);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.24);
}

.submit-rail-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.submit-rail-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--clipr-purple-light);
}

.submit-rail-title {
  margin: 0;
  font-size: 28px;
  line-height: 1;
  color: var(--clipr-text-primary);
}

.submit-rail-text {
  margin: 0;
  max-width: 54ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--clipr-text-secondary);
}

.submit-rail-actions {
  width: min(100%, 320px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
}

.submit-assign-wrap {
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.submit-assign-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  text-transform: none;
  letter-spacing: 0;
  border-radius: 14px;
}

.submit-assign-wrap .assign-target-user {
  max-width: 100%;
}

.submit-btn-primary {
  min-height: 60px;
  padding: 18px 28px !important;
  border-radius: 16px !important;
  font-size: 16px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 48%, #4f46e5 100%) !important;
  box-shadow: 0 18px 32px rgba(79, 70, 229, 0.28), 0 0 24px rgba(168, 85, 247, 0.18) !important;
}

.submit-btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 22px 36px rgba(79, 70, 229, 0.34), 0 0 28px rgba(168, 85, 247, 0.22) !important;
}

.submit-btn-primary i {
  font-size: 18px;
}

.submit-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  font-size: 13px;
  color: var(--clipr-text-tertiary);
  text-align: center;
}

.insufficient-tokens-hint {
  color: #f59e0b;
}

.insufficient-tokens-hint a,
.submit-hint-link {
  color: var(--clipr-purple-light);
}

.submit-hint-link {
  border: 0;
  background: transparent;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
}

.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

@keyframes checkmark {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 4px 15px rgba(34, 197, 94, 0.3);
  }
  50% {
    box-shadow: 0 4px 25px rgba(34, 197, 94, 0.5);
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Responsive */
@media (max-width: 768px) {
  .make-clips-page {
    padding: 0;
  }

  .make-clips-page-load-overlay {
    padding: 18px;
  }

  .make-clips-hero {
    margin-bottom: 32px;
  }

  .hero-icon-wrapper {
    width: 60px;
    height: 60px;
    margin-bottom: 16px;
  }

  .hero-icon {
    font-size: 26px;
  }

  .hero-subtitle {
    font-size: 14px;
  }
  
  .make-clips-page .card-content {
    padding: 24px 20px 28px;
  }

  .url-input-badge-segment {
    display: flex;
  }

  .example-chips {
    grid-template-columns: 1fr; /* 1 columna en móvil */
  }

  .admin-forms-grid {
    grid-template-columns: 1fr;
  }

  .admin-simple-segment-row {
    grid-template-columns: 1fr;
    gap: 8px;
    justify-items: stretch;
  }

  .admin-simple-separator {
    display: none;
  }

  .segment-item {
    padding: 16px;
  }

  .section-label-with-action {
    flex-direction: column;
    align-items: stretch;
  }

  .section-label-actions--compact {
    width: 100%;
  }

  .segments-inline-trigger {
    width: 100%;
    align-items: flex-start;
    text-align: left;
    flex-wrap: wrap;
  }

  .segments-link-toggle {
    width: 100%;
    text-align: left;
  }

  .add-segment-header-btn,
  .add-segment-empty-btn {
    width: 100%;
    justify-content: center;
  }

  .segments-empty-state {
    flex-direction: column;
    align-items: stretch;
  }

  .segments-summary {
    width: 100%;
  }

  .segment-row-compact {
    align-items: stretch;
  }

  .segment-index-chip {
    min-width: 44px;
  }

  .segment-name-input-inline {
    width: 100%;
    flex-basis: 100%;
  }

  .segment-time-inline-group {
    width: 100%;
    justify-content: space-between;
  }

  .segment-time-fields {
    flex: 1 1 120px;
  }

  .segment-time-input {
    width: 100%;
  }

  .segment-time-arrow {
    display: none;
  }

  .segment-duration-chip {
    margin-right: auto;
  }

  .segment-actions {
    margin-left: 0;
  }

  .submit-rail {
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .submit-rail-title {
    font-size: 22px;
  }

  .submit-rail-actions {
    width: 100%;
  }

  .submit-btn {
    min-width: 100%;
    padding: 14px 28px;
  }

  .submit-section-inline .submit-btn {
    width: 100%;
  }
}

/* Time Picker Styles */
.time-picker {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}

.time-input-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.time-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--clipr-text-secondary);
  text-transform: uppercase;
  margin: 0;
}

.time-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.time-input {
  width: 60px;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.5rem 0.25rem;
  border: 2px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-md);
  background-color: rgba(15, 15, 18, 0.9);
  color: var(--clipr-text-primary);
  transition: all 0.2s ease;
  appearance: textfield;
  -moz-appearance: textfield;
}

.time-input::-webkit-outer-spin-button,
.time-input::-webkit-inner-spin-button {
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
}

.time-input:focus {
  outline: none;
  border-color: var(--clipr-border-purple);
  box-shadow: 0 0 0 0.2rem var(--clipr-purple-glow);
  background-color: rgba(15, 15, 18, 0.95);
}

.time-input:disabled {
  background-color: rgba(15, 15, 18, 0.6);
  cursor: not-allowed;
  opacity: 0.6;
}

.time-btn {
  width: 24px;
  height: 20px;
  padding: 0;
  border: 1px solid var(--clipr-border-color);
  background-color: rgba(15, 23, 42, 0.9);
  color: var(--clipr-text-secondary);
  font-size: 0.7rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.time-btn-up {
  border-radius: var(--clipr-radius-sm) var(--clipr-radius-sm) 0 0;
  border-bottom: none;
}

.time-btn-down {
  border-radius: 0 0 var(--clipr-radius-sm) var(--clipr-radius-sm);
  border-top: none;
}

.time-btn:hover:not(:disabled) {
  background-color: var(--clipr-purple-overlay-hover);
  border-color: var(--clipr-border-purple);
  color: var(--clipr-purple-light);
}

.time-btn:active:not(:disabled) {
  background-color: var(--clipr-purple-overlay-intense);
  transform: scale(0.95);
}

.time-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.time-separator {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--clipr-text-secondary);
  margin-top: 1.5rem;
  line-height: 1;
}

@media (max-width: 768px) {
  .time-picker {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .time-input {
    width: 50px;
    font-size: 1rem;
    padding: 0.4rem 0.2rem;
  }
  
  .time-separator {
    font-size: 1.2rem;
  }
}

/* Modal overlay & container */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.modal-fade-enter-active .confirm-modal--create,
.modal-fade-leave-active .confirm-modal--create {
  transition:
    opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .confirm-modal--create,
.modal-fade-leave-to .confirm-modal--create {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
  filter: blur(7px);
}

@media (prefers-reduced-motion: reduce) {
  .modal-fade-enter-active .confirm-modal--create,
  .modal-fade-leave-active .confirm-modal--create {
    transition: opacity 0.18s ease;
  }

  .modal-fade-enter-from .confirm-modal--create,
  .modal-fade-leave-to .confirm-modal--create {
    transform: none;
    filter: none;
  }
}

.confirm-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(2, 6, 23, 0.78);
  backdrop-filter: blur(10px);
}

.confirm-modal {
  width: min(540px, 100%);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  background: var(--clipr-bg-elevated);
  border: 1px solid rgba(168, 85, 247, 0.35);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.2),
    0 0 24px var(--clipr-purple-glow),
    0 0 48px rgba(168, 85, 247, 0.12),
    0 24px 64px rgba(0, 0, 0, 0.4);
}

.confirm-modal--create {
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background:
    radial-gradient(120% 90% at 50% -10%, rgba(168, 85, 247, 0.28), transparent 60%),
    linear-gradient(180deg, #131a2e 0%, #0b1120 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 30px 90px rgba(2, 6, 23, 0.6);
  color: #f8fafc;
}

.confirm-modal--bulk {
  width: min(1120px, 100%);
}

.confirm-modal--create .confirm-modal-header {
  padding: 20px 22px 12px;
}

.confirm-modal--create .confirm-modal-header-icon {
  width: 40px;
  height: 40px;
  margin: 0 auto 10px;
  border-radius: 12px;
  font-size: 18px;
}

.confirm-modal--create .confirm-modal-title {
  margin: 0;
  font-size: clamp(1.15rem, 2.4vw, 1.35rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #f8fafc;
}

.confirm-modal--create .confirm-modal-body {
  padding: 18px 24px 20px;
}

.confirm-modal--create .confirm-modal-content {
  gap: 14px;
}

.confirm-modal--create .confirm-modal-content > * {
  animation: confirm-modal-section-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.confirm-modal--create .confirm-modal-content > *:nth-child(2) {
  animation-delay: 0.05s;
}

.confirm-modal--create .confirm-modal-content > *:nth-child(3) {
  animation-delay: 0.1s;
}

.confirm-modal--create .confirm-modal-content > *:nth-child(4) {
  animation-delay: 0.15s;
}

.confirm-modal--create .confirm-modal-content > *:nth-child(5) {
  animation-delay: 0.2s;
}

.confirm-modal--create .confirm-modal-content > *:nth-child(6) {
  animation-delay: 0.25s;
}

.confirm-modal--create .confirm-vod-summary-item {
  animation: confirm-modal-section-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.confirm-modal--create .confirm-vods-summary > .confirm-vod-summary-item:nth-child(2) {
  animation-delay: 0.05s;
}

.confirm-modal--create .confirm-vods-summary > .confirm-vod-summary-item:nth-child(3) {
  animation-delay: 0.1s;
}

.confirm-modal--create .confirm-modal-content--bulk {
  gap: 14px;
}

.confirm-modal--create .confirm-bulk-left {
  gap: 10px;
}

.confirm-modal--create .confirm-vods-summary {
  gap: 10px;
  padding-bottom: 2px;
}

.confirm-modal--create .confirm-vod-summary-item {
  gap: 10px;
  padding: 10px;
}

.confirm-modal--create .confirm-video-card {
  gap: 12px;
  padding: 12px;
}

.confirm-modal--create .confirm-video-title {
  margin: 0 0 6px;
  line-height: 1.35;
}

.confirm-modal--create .confirm-video-meta {
  gap: 10px;
  row-gap: 6px;
}

.confirm-modal--create .confirm-assignee-card {
  padding: 8px 10px;
}

.confirm-modal--create .confirm-assignee-header {
  margin-bottom: 3px;
}

.confirm-modal--create .confirm-segments-card {
  padding: 12px 14px;
}

.confirm-modal--create .confirm-segments-header {
  margin-bottom: 8px;
}

.confirm-modal--create .confirm-segments-list {
  gap: 8px;
}

.confirm-modal--create .confirm-segment-item {
  gap: 10px;
  padding: 8px 10px;
}

.confirm-modal--create .confirm-token-card {
  padding: 12px 14px;
}

.confirm-modal--create .confirm-token-header {
  margin-bottom: 10px;
}

.confirm-modal--create .confirm-token-body {
  gap: 7px;
}

.confirm-modal--create .confirm-token-row {
  padding: 1px 0;
}

.confirm-modal--create .confirm-token-divider {
  margin: 4px 0;
}

.confirm-modal--create .confirm-token-alert {
  margin-top: 10px;
  padding: 9px 10px;
}

.confirm-modal--create .confirm-token-insufficient-note {
  margin: 7px 0 0;
}

.confirm-modal--create .confirm-modal-footer {
  gap: 8px;
  padding: 12px 22px 14px;
}

.confirm-modal--create .confirm-modal-btn {
  min-width: 132px;
  padding: 9px 16px;
}

/* Header */
.confirm-modal-header {
  position: relative;
  padding: 28px 28px 20px;
  text-align: center;
  background: linear-gradient(180deg, var(--clipr-purple-overlay-light) 0%, transparent 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.confirm-modal--create .confirm-modal-header {
  padding: 28px 28px 16px;
  background: transparent;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
}

.confirm-modal-header-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--clipr-purple-overlay-strong), rgba(217, 70, 239, 0.2));
  border: 1px solid var(--clipr-purple-overlay-intense);
  color: var(--clipr-purple-light);
  font-size: 22px;
}

.confirm-modal-title {
  margin: 0 0 4px;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--clipr-text-primary);
  letter-spacing: -0.02em;
}

.confirm-modal-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--clipr-text-secondary);
}

.confirm-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--clipr-border-color);
  border-radius: 8px;
  background: transparent;
  color: var(--clipr-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}
.confirm-modal-close:hover {
  background: transparent;
  border-color: var(--clipr-border-purple);
  color: var(--clipr-text-primary);
}

.confirm-modal--create .confirm-modal-close {
  top: 14px;
  right: 14px;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.6);
  color: #cbd5e1;
  transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.confirm-modal--create .confirm-modal-close:hover {
  background: rgba(30, 41, 59, 0.9);
  border-color: rgba(196, 181, 253, 0.38);
  color: #fff;
}

/* Body */
.confirm-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px 26px;
}

.confirm-modal-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 140px;
  color: var(--clipr-text-secondary);
  font-size: 0.9rem;
}

.confirm-modal--create .confirm-modal-state {
  color: #cbd5e1;
  animation: confirm-modal-section-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.confirm-modal-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--clipr-purple-overlay-hover);
  border-top-color: var(--clipr-purple-light);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.confirm-modal--create .confirm-modal-spinner {
  border-color: rgba(148, 163, 184, 0.24);
  border-top-color: #67e8f9;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.confirm-modal-state-error {
  color: #f87171;
}
.confirm-modal-state-error i {
  font-size: 2rem;
  opacity: 0.9;
}

.confirm-modal--create .confirm-modal-state-error {
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(248, 113, 113, 0.28);
  background: rgba(127, 29, 29, 0.22);
  color: #fda4af;
}

/* Video card */
.confirm-modal-content {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

@keyframes confirm-modal-section-in {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.985);
    filter: blur(5px);
  }
  100% {
    opacity: 1;
    transform: none;
    filter: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .confirm-modal--create .confirm-modal-state,
  .confirm-modal--create .confirm-modal-content > *,
  .confirm-modal--create .confirm-vod-summary-item {
    animation: none;
  }
}

.confirm-modal-content--bulk {
  display: grid;
  grid-template-columns: minmax(300px, 360px) minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}

.confirm-bulk-left {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.confirm-vods-summary {
  display: flex;
  flex-direction: row;
  gap: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 6px;
  scroll-snap-type: x proximity;
}

.confirm-vod-summary-item {
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px;
  border-radius: 14px;
  background: var(--clipr-bg-elevated);
  border: 1px solid rgba(168, 85, 247, 0.25);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.15),
    0 0 16px rgba(168, 85, 247, 0.08);
  scroll-snap-align: start;
}

.confirm-modal--create .confirm-vod-summary-item {
  background: rgba(2, 6, 23, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.32);
}

.confirm-video-card {
  display: flex;
  gap: 18px;
  padding: 18px;
  border-radius: 14px;
  background: var(--clipr-bg-elevated);
  border: 1px solid rgba(168, 85, 247, 0.25);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.15),
    0 0 16px rgba(168, 85, 247, 0.08);
}

.confirm-modal--create .confirm-video-card {
  background: rgba(2, 6, 23, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.32);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.confirm-video-thumb-wrap {
  position: relative;
  flex-shrink: 0;
  width: 120px;
  height: 68px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.4);
}

.confirm-video-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.confirm-video-thumb-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--clipr-text-secondary);
  font-size: 1.5rem;
  opacity: 0.5;
}

.confirm-video-duration-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.75);
  font-size: 0.7rem;
  font-weight: 600;
  color: #fff;
}

.confirm-modal--create .confirm-video-thumb-wrap {
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.confirm-modal--create .confirm-video-meta-item {
  color: #cbd5e1;
}

.confirm-modal--create .confirm-video-duration-badge {
  background: rgba(15, 23, 42, 0.88);
  border: 1px solid rgba(148, 163, 184, 0.24);
}

.confirm-video-info {
  flex: 1;
  min-width: 0;
}

.confirm-video-title {
  margin: 0 0 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.confirm-video-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  row-gap: 8px;
}

.confirm-video-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: var(--clipr-text-secondary);
}
.confirm-video-meta-item i {
  font-size: 0.9rem;
  opacity: 0.8;
}

.confirm-video-type-pill {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-top: 10px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(168, 85, 247, 0.14);
  border: 1px solid rgba(168, 85, 247, 0.28);
  color: var(--clipr-purple-light);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.confirm-modal--create .confirm-video-type-pill {
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(196, 181, 253, 0.42);
  color: #ddd6fe;
}

.confirm-video-type-card {
  padding: 16px 18px;
  border-radius: 14px;
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.08), transparent 48%),
    rgba(2, 6, 23, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: inset 0 0 0 1px rgba(168, 85, 247, 0.08);
}

.confirm-video-type-card--compact {
  padding: 12px;
}

.confirm-video-context-card {
  margin-top: 12px;
  padding: 12px;
  border-radius: 14px;
  background:
    radial-gradient(circle at top left, rgba(168, 85, 247, 0.14), transparent 42%),
    rgba(2, 6, 23, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.24);
}

.confirm-video-context-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: var(--clipr-text-primary);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.confirm-video-context-header i {
  color: #c4b5fd;
  font-size: 0.95rem;
}

.confirm-video-context-helper {
  margin: 10px 0;
  color: var(--clipr-text-secondary);
  font-size: 0.78rem;
  line-height: 1.4;
}

.confirm-video-context-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.confirm-video-context-toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.confirm-video-context-switch {
  position: relative;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.28);
  transition: background 0.25s ease, box-shadow 0.25s ease;
  flex-shrink: 0;
}

.confirm-video-context-switch::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(2, 6, 23, 0.45);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.confirm-video-context-toggle input:checked + .confirm-video-context-switch {
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.85), rgba(14, 165, 233, 0.8));
  box-shadow: 0 0 12px rgba(34, 211, 238, 0.35);
}

.confirm-video-context-toggle input:checked + .confirm-video-context-switch::after {
  transform: translateX(18px);
}

.confirm-video-context-toggle-label {
  color: var(--clipr-text-primary);
  font-size: 0.86rem;
  font-weight: 600;
}

.confirm-video-context-input {
  width: 100%;
  min-height: 84px;
  resize: vertical;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(15, 23, 42, 0.82);
  color: var(--clipr-text-primary);
  font-size: 0.93rem;
  line-height: 1.4;
  padding: 10px 12px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.confirm-video-context-input:focus {
  outline: none;
  border-color: rgba(34, 211, 238, 0.48);
  background: rgba(15, 23, 42, 0.94);
  box-shadow:
    0 12px 26px rgba(34, 211, 238, 0.12),
    inset 0 0 0 1px rgba(34, 211, 238, 0.14);
}

.confirm-video-context-input::placeholder {
  color: var(--clipr-text-muted);
}

.confirm-context-reveal-enter-active {
  transition:
    opacity 0.38s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.38s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.38s cubic-bezier(0.22, 1, 0.36, 1);
}

.confirm-context-reveal-leave-active {
  transition:
    opacity 0.22s cubic-bezier(0.4, 0, 1, 1),
    transform 0.22s cubic-bezier(0.4, 0, 1, 1),
    filter 0.22s cubic-bezier(0.4, 0, 1, 1);
}

.confirm-context-reveal-enter,
.confirm-context-reveal-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.98);
  filter: blur(6px);
}

.confirm-video-type-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.confirm-video-type-header i {
  color: #c084fc;
  font-size: 0.95rem;
}

.confirm-video-type-helper {
  margin: -2px 0 12px;
  color: var(--clipr-text-secondary);
  font-size: 0.8rem;
  line-height: 1.45;
}

.make-flow-subtitles-toggle {
  margin-top: 14px;
}

.make-flow-subtitles-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.65rem;
  padding: 16px 22px 8px;
}

.make-flow-subtitle-style-button {
  border: 1px solid rgba(192, 132, 252, 0.32);
  border-radius: 10px;
  background: rgba(168, 85, 247, 0.12);
  color: #e9d5ff;
  padding: 0.52rem 0.7rem;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
}

.make-flow-subtitle-style-button:disabled {
  cursor: wait;
  opacity: 0.55;
}

.make-flow-subtitles-pill {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  width: min(100%, 300px);
  padding: 13px 20px 11px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(165deg, rgba(88, 28, 135, 0.14) 0%, rgba(15, 23, 42, 0.62) 48%, rgba(2, 6, 23, 0.72) 100%);
  box-shadow:
    0 10px 28px rgba(2, 6, 23, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  cursor: pointer;
  user-select: none;
  transition:
    border-color 0.24s ease,
    box-shadow 0.24s ease,
    background 0.24s ease,
    transform 0.24s ease;
  animation: make-flow-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.12s backwards;
}

.make-flow-subtitles-pill:hover:not(:has(input:disabled)) {
  transform: translateY(-1px);
  border-color: rgba(196, 181, 253, 0.26);
}

.make-flow-subtitles-pill--inactive {
  border-color: rgba(100, 116, 139, 0.18);
}

.make-flow-subtitles-pill--active {
  border-color: rgba(34, 211, 238, 0.36);
  background:
    linear-gradient(165deg, rgba(34, 211, 238, 0.14) 0%, rgba(15, 23, 42, 0.68) 52%, rgba(2, 6, 23, 0.78) 100%);
  box-shadow:
    0 12px 30px rgba(34, 211, 238, 0.13),
    0 0 0 1px rgba(34, 211, 238, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.07);
}

.make-flow-subtitles-pill-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
}

.make-flow-subtitles-pill-label {
  color: rgba(226, 232, 240, 0.92);
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
}

.make-flow-subtitles-pill--inactive .make-flow-subtitles-pill-label {
  color: rgba(148, 163, 184, 0.88);
}

.make-flow-subtitles-pill--active .make-flow-subtitles-pill-label {
  color: #f8fafc;
}

.make-flow-subtitles-pill-switch {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.make-flow-subtitles-pill-switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.make-flow-subtitles-pill-track {
  position: relative;
  width: 46px;
  height: 26px;
  border-radius: 999px;
  background: rgba(71, 85, 105, 0.55);
  box-shadow: inset 0 2px 5px rgba(2, 6, 23, 0.35);
  transition: background 0.24s ease, box-shadow 0.24s ease;
}

.make-flow-subtitles-pill--active .make-flow-subtitles-pill-track {
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.92), rgba(14, 165, 233, 0.88));
  box-shadow:
    0 0 14px rgba(34, 211, 238, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.make-flow-subtitles-pill-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 7px rgba(2, 6, 23, 0.38);
  transition: transform 0.26s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.make-flow-subtitles-pill-switch input:checked + .make-flow-subtitles-pill-track .make-flow-subtitles-pill-thumb {
  transform: translateX(20px);
}

.make-flow-subtitles-pill-status {
  display: block;
  width: 100%;
  text-align: center;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.make-flow-subtitles-pill-status.is-off {
  color: rgba(148, 163, 184, 0.62);
}

.make-flow-subtitles-pill-status.is-on {
  color: #5eead4;
}

.make-flow-configure-body--choosing .make-flow-subtitles-wrap {
  opacity: 0.45;
  pointer-events: none;
  filter: blur(1px);
}

.confirm-video-type-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.confirm-video-type-grid--compact {
  gap: 8px;
}

.confirm-video-type-option {
  position: relative;
  overflow: hidden;
  height: 76px;
  min-height: 76px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 14px;
  background: rgba(2, 6, 23, 0.7);
  text-align: left;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.confirm-video-type-option:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(196, 181, 253, 0.38);
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.32);
}

.confirm-video-type-option:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.confirm-video-type-option--active {
  border-color: rgba(34, 211, 238, 0.55);
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.16), transparent 48%),
    rgba(15, 23, 42, 0.82);
  box-shadow:
    0 16px 28px rgba(34, 211, 238, 0.15),
    inset 0 0 0 1px rgba(34, 211, 238, 0.16);
}

.confirm-video-type-option-shine {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    105deg,
    transparent 40%,
    rgba(255, 255, 255, 0.2) 50%,
    transparent 60%
  );
  transform: translateX(-120%);
  opacity: 0;
  pointer-events: none;
  z-index: 2;
}

.confirm-video-type-option--active .confirm-video-type-option-shine {
  opacity: 1;
  animation: confirm-video-type-shine 0.7s ease-in-out forwards;
}

@keyframes confirm-video-type-shine {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(120%);
  }
}

.confirm-video-type-option-icon {
  margin-left: 4px;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #c4b5fd;
  font-size: 1rem;
  border: 1px solid rgba(196, 181, 253, 0.24);
  background: rgba(168, 85, 247, 0.12);
}

.confirm-video-type-option--active .confirm-video-type-option-icon {
  color: #67e8f9;
  border-color: rgba(34, 211, 238, 0.34);
  background: rgba(34, 211, 238, 0.14);
}

.confirm-video-type-option-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.confirm-video-type-option-copy strong {
  color: var(--clipr-text-primary);
  font-size: 0.78rem;
  line-height: 1.15;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.confirm-video-type-option-copy small {
  color: var(--clipr-text-secondary);
  font-size: 0.68rem;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.confirm-video-type-option-check {
  margin-right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.34);
  color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.72rem;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease;
}

.confirm-video-type-option--active .confirm-video-type-option-check {
  color: #67e8f9;
  border-color: rgba(34, 211, 238, 0.48);
  background: rgba(34, 211, 238, 0.16);
}

@media (prefers-reduced-motion: reduce) {
  .confirm-video-type-option {
    transition: none;
  }

  .confirm-video-type-option--active .confirm-video-type-option-shine {
    animation: none;
    opacity: 0;
  }
}

.confirm-assignee-card {
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(168, 85, 247, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.18);
}

.confirm-modal--create .confirm-assignee-card {
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(196, 181, 253, 0.28);
}

.confirm-assignee-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--clipr-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.confirm-assignee-header i {
  color: #c084fc;
  font-size: 0.78rem;
}

.confirm-assignee-body {
  display: flex;
  align-items: center;
  min-height: 18px;
  min-width: 0;
}

.confirm-assignee-name {
  color: var(--clipr-purple-light);
  font-size: 0.82rem;
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.confirm-assignee-empty {
  color: var(--clipr-text-secondary);
  font-size: 0.76rem;
  line-height: 1.25;
}

/* Segments summary card */
.confirm-segments-card {
  padding: 16px 18px;
  border-radius: 14px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.confirm-modal--create .confirm-segments-card {
  background: rgba(2, 6, 23, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.32);
}

.confirm-segments-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.confirm-segments-header i {
  color: #818cf8;
  font-size: 0.95rem;
}

.confirm-modal--create .confirm-segments-header i {
  color: #c4b5fd;
}

.confirm-segments-timeline {
  display: block;
  width: 100%;
  max-width: 460px;
  margin: 6px 0 4px;
}

.confirm-segments-helper {
  margin: 0 0 12px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: rgba(148, 163, 184, 0.9);
}

.confirm-manual-trim {
  max-width: 460px;
}

.confirm-manual-trim-fields {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.confirm-segments-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.confirm-segment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.confirm-modal--create .confirm-segment-item {
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.82);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.confirm-segment-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.confirm-segment-index {
  color: var(--clipr-text-secondary);
  font-size: 0.8rem;
  font-weight: 600;
}

.confirm-segment-range {
  color: var(--clipr-text-primary);
  font-size: 0.88rem;
  font-weight: 600;
  white-space: nowrap;
}

.confirm-segment-duration {
  color: var(--clipr-text-secondary);
  font-size: 0.82rem;
  font-weight: 600;
}

.confirm-segments-empty {
  font-size: 0.84rem;
  color: var(--clipr-text-secondary);
}

/* Token card */
.confirm-token-card {
  padding: 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.06) 100%);
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.confirm-modal--create .confirm-token-card {
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.1), transparent 48%),
    rgba(2, 6, 23, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.32);
}

.confirm-token-card--insufficient {
  background: linear-gradient(135deg, rgba(248, 113, 113, 0.08) 0%, rgba(239, 68, 68, 0.06) 100%);
  border-color: rgba(248, 113, 113, 0.25);
}

.confirm-token-card--limit {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(245, 158, 11, 0.06) 100%);
  border-color: rgba(251, 191, 36, 0.28);
}

.confirm-modal--create .confirm-token-card--limit {
  background:
    radial-gradient(circle at top right, rgba(251, 191, 36, 0.12), transparent 48%),
    rgba(2, 6, 23, 0.7);
  border-color: rgba(251, 191, 36, 0.28);
}

.confirm-token-alert--limit {
  border-color: rgba(251, 191, 36, 0.28);
  background: rgba(251, 191, 36, 0.08);
}

.confirm-token-alert--limit .confirm-token-alert-message {
  color: #fcd34d;
}

.confirm-modal--create .confirm-token-card--insufficient {
  background:
    radial-gradient(circle at top right, rgba(248, 113, 113, 0.12), transparent 48%),
    rgba(2, 6, 23, 0.7);
  border-color: rgba(248, 113, 113, 0.28);
}

.confirm-token-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
}
.confirm-token-header i {
  color: #22c55e;
  font-size: 1rem;
  margin-top: 2px;
}
.confirm-token-card--insufficient .confirm-token-header i {
  color: #f87171;
}

.confirm-modal--create .confirm-token-header i {
  color: #67e8f9;
}

.confirm-modal--create .confirm-token-card--insufficient .confirm-token-header i {
  color: #fda4af;
}

.confirm-token-header-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.confirm-token-header-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.confirm-token-header-rate {
  font-size: 0.73rem;
  font-weight: 500;
  color: var(--clipr-text-secondary);
}

.confirm-token-header-rate--daily {
  color: #67e8f9;
  font-weight: 600;
}

.confirm-token-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.confirm-token-hint {
  margin-top: 0;
  font-size: 0.78rem;
  color: var(--clipr-text-secondary);
  line-height: 1.45;
}

.confirm-token-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: var(--clipr-text-secondary);
  padding: 2px 0;
}

.confirm-token-cost,
.confirm-token-balance {
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.confirm-token-cost--free {
  color: #86efac;
}

.confirm-token-hint--free {
  color: #86efac;
  font-size: 0.82rem;
  margin-top: 4px;
}

.confirm-token-divider {
  height: 1px;
  margin: 6px 0;
  background: rgba(255, 255, 255, 0.08);
}

.confirm-modal--create .confirm-token-divider {
  background: linear-gradient(90deg, transparent, rgba(196, 181, 253, 0.34), transparent);
}

.confirm-token-row--result {
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.confirm-token-remaining {
  color: #22c55e;
  font-weight: 700;
}

.confirm-modal--create .confirm-token-remaining {
  color: #67e8f9;
}

.confirm-token-remaining--negative {
  color: #f87171;
}

.confirm-token-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(248, 113, 113, 0.15);
  font-size: 0.85rem;
  font-weight: 600;
  color: #f87171;
}

.confirm-modal--create .confirm-token-alert {
  border-radius: 12px;
  border: 1px solid rgba(248, 113, 113, 0.28);
  background: rgba(127, 29, 29, 0.22);
  color: #fda4af;
}

.confirm-token-alert-message {
  display: flex;
  align-items: center;
  gap: 8px;
}

.confirm-token-alert i {
  flex-shrink: 0;
  font-size: 1rem;
}

.confirm-token-alert-cta {
  border: 1px solid var(--clipr-purple-overlay-accent);
  background: var(--clipr-purple-overlay-hover);
  color: #e9d5ff;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-modal--create .confirm-token-alert-cta {
  border: 1px solid rgba(196, 181, 253, 0.42);
  background: rgba(15, 23, 42, 0.55);
  color: #ddd6fe;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}

.confirm-token-alert-cta:hover {
  background: var(--clipr-purple-overlay-intense);
}

.confirm-modal--create .confirm-token-alert-cta:hover {
  background: rgba(30, 41, 59, 0.9);
  border-color: rgba(216, 180, 254, 0.68);
  transform: translateY(-1px);
}

.confirm-token-insufficient-note {
  margin: 10px 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #fca5a5;
}

/* Footer */
.confirm-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 18px 28px 26px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.2);
}

.confirm-modal--create .confirm-modal-footer {
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(2, 6, 23, 0.35);
}

.confirm-modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 144px;
  padding: 11px 22px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.confirm-modal-btn--secondary {
  background: rgba(255, 255, 255, 0.08);
  color: var(--clipr-text-secondary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.confirm-modal-btn--secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  color: var(--clipr-text-primary);
}

.confirm-modal--create .confirm-modal-btn {
  border-radius: 12px;
  min-height: 42px;
}

.confirm-modal--create .confirm-modal-btn--secondary {
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.6);
  color: #cbd5e1;
  transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.confirm-modal--create .confirm-modal-btn--secondary:hover:not(:disabled) {
  background: rgba(30, 41, 59, 0.9);
  border-color: rgba(196, 181, 253, 0.38);
  color: #fff;
}

.confirm-modal-btn--primary {
  background: linear-gradient(135deg, #a855f7, #d946ef);
  color: #fff;
  box-shadow: 0 4px 14px var(--clipr-purple-overlay-bold);
}
.confirm-modal-btn--primary:hover:not(:disabled) {
  box-shadow: var(--clipr-shadow-button-hover);
  transform: translateY(-1px);
}
.confirm-modal-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.confirm-modal--create .confirm-modal-btn--primary {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 52%, #06b6d4 100%);
  box-shadow: 0 8px 18px rgba(124, 58, 237, 0.22);
  font-weight: 700;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
}

.confirm-modal--create .confirm-modal-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 12px 30px rgba(99, 102, 241, 0.35);
}

.confirm-modal--create .confirm-modal-btn--primary:active:not(:disabled) {
  transform: translateY(0);
}

@media (max-width: 980px) {
  .confirm-modal--bulk {
    width: min(700px, 100%);
  }

  .confirm-modal-content--bulk {
    grid-template-columns: 1fr;
  }
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
}

/* Assign modal - diseño unificado (idéntico a VODClips) */
.assign-modal .modal-dialog,
.assign-modal-dialog {
  max-width: 560px;
}

.assign-modal-content {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid rgba(168, 85, 247, 0.28);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5), 0 0 32px var(--clipr-purple-glow);
  background: var(--clipr-bg-card);
}

.assign-modal-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #a855f7, #d946ef, #9146ff);
  box-shadow: 0 2px 12px var(--clipr-purple-overlay-bold);
}

.assign-modal-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--clipr-border-color);
  background:
    radial-gradient(circle at top left, rgba(168, 85, 247, 0.24), transparent 36%),
    rgba(15, 23, 42, 0.28);
}

.assign-modal-header-inner {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.assign-modal-header-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: var(--clipr-purple-gradient);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 22px var(--clipr-purple-glow);
  flex: 0 0 auto;
}

.assign-modal-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--clipr-purple-overlay-strong), rgba(217, 70, 239, 0.2));
  border: 1px solid var(--clipr-purple-overlay-border);
  color: #c084fc;
  font-size: 20px;
}

.assign-modal-title-wrap {
  flex: 1;
  min-width: 0;
}

.assign-modal-title {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: var(--clipr-text-primary);
}

.assign-modal-subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--clipr-text-secondary);
  line-height: 1.45;
}

.assign-modal-close {
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  border: 1px solid var(--clipr-border-color);
  background: transparent;
  color: var(--clipr-text-secondary);
  opacity: 1;
  line-height: 1;
  cursor: pointer;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.assign-modal-close:hover {
  color: var(--clipr-text-primary);
  border-color: var(--clipr-border-purple);
  background: transparent;
}

.assign-modal-body {
  padding: 16px 18px 18px;
}

.assign-modal-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 24px;
  color: var(--clipr-text-secondary);
  font-size: 14px;
}

.assign-modal-spinner {
  font-size: 20px;
  color: #c084fc;
}

.assign-modal-empty {
  flex-direction: column;
  gap: 10px;
}

.assign-modal-empty i {
  font-size: 28px;
  opacity: 0.5;
}

.assign-user-scope-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.25);
  margin-bottom: 12px;
}

.assign-user-scope-btn {
  min-height: 30px;
  border: none;
  border-radius: 8px;
  padding: 0 10px;
  background: transparent;
  color: var(--clipr-text-secondary);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.assign-user-scope-btn.active {
  background: rgba(168, 85, 247, 0.2);
  color: var(--clipr-purple-light);
}

.assign-user-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.35);
  color: var(--clipr-text-tertiary);
  margin-bottom: 16px;
}

.assign-user-search-input {
  width: 100%;
  min-height: 40px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--clipr-text-primary);
  caret-color: var(--clipr-text-primary);
  font-size: 13px;
}

.assign-user-search-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.assign-user-search-input:focus {
  box-shadow: none;
}

.assign-user-search-wrap:focus-within {
  border-color: var(--clipr-border-purple);
  background: rgba(15, 23, 42, 0.7);
  box-shadow: 0 0 0 2px var(--clipr-purple-glow);
}

.assign-users-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(340px, 45vh);
  overflow-y: auto;
  padding-right: 2px;
}

.assign-users-list::-webkit-scrollbar {
  width: var(--clipr-scrollbar-size);
}

.assign-users-list::-webkit-scrollbar-track {
  background: var(--clipr-scrollbar-track);
  border-radius: 999px;
}

.assign-users-list::-webkit-scrollbar-thumb {
  background: var(--clipr-scrollbar-thumb);
  border-radius: 999px;
  border: 2px solid var(--clipr-scrollbar-thumb-border);
}

.assign-users-list::-webkit-scrollbar-thumb:hover {
  background: var(--clipr-scrollbar-thumb-hover);
}

.assign-user-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(15, 23, 42, 0.35);
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}

.assign-user-row:hover {
  border-color: var(--clipr-border-purple);
  background: rgba(168, 85, 247, 0.14);
  transform: translateY(-1px);
}

.assign-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(168, 85, 247, 0.2);
  color: var(--clipr-purple-light);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  flex: 0 0 auto;
}

.assign-user-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.assign-user-name {
  font-weight: 600;
  font-size: 13px;
  color: var(--clipr-text-primary);
  overflow-wrap: anywhere;
}

.assign-user-copy small {
  color: var(--clipr-text-tertiary);
  font-size: 11px;
}

.assign-user-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--clipr-purple-gradient);
  color: #fff;
}

.assign-user-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.assign-user-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.assign-modal-footer {
  padding: 0 18px 18px;
  border-top: none;
  background: transparent;
  display: flex;
  justify-content: flex-end;
}

.assign-modal-btn {
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.assign-modal-btn--secondary {
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid var(--clipr-purple-overlay-strong);
  color: var(--clipr-text-secondary);
}

.assign-modal-btn--secondary:hover:not(:disabled) {
  background: rgba(25, 35, 55, 0.95);
  border-color: var(--clipr-purple-overlay-border);
  color: var(--clipr-text-primary);
  transform: translateY(-1px);
}

.assign-modal-btn--secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 991px) {
  .make-clips-page {
    margin-left: -15px;
    margin-right: -15px;
    padding: 0 1rem 1.25rem;
  }

  .make-clips-page.make-clips-page--library-focused {
    height: 100%;
    min-height: 0;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }

  .make-clips-library-section--focused :deep(.clip-workspace) {
    padding: 0;
  }
}

/* Mobile spacing system */
@media (max-width: 768px) {
  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .collections-row-scroll) {
    grid-template-columns: 1fr;
    overflow-x: visible;
    touch-action: pan-y;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .collections-row-scroll.videos-strip) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 15px;
    overflow-x: visible;
    overflow-y: visible;
    scroll-snap-type: none;
    touch-action: auto;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .video-card) {
    width: 100%;
    max-width: none;
    min-width: 0;
    justify-self: stretch;
  }

  .make-clips-page {
    --make-clips-mobile-rhythm: 1rem;
    --make-clips-space-xs: 0.65rem;
    --make-clips-space-sm: 0.85rem;
    --make-clips-space-md: 1rem;
    --make-clips-space-lg: 1.25rem;
    --make-clips-mobile-inset: 0;
    --make-clips-section-gap: var(--make-clips-mobile-rhythm);
    --make-clips-title-rhythm: var(--make-clips-mobile-rhythm);
    --make-clips-flow-rhythm: var(--make-clips-mobile-rhythm);
    margin-left: -15px;
    margin-right: -15px;
    padding: 0 1rem 1.25rem;
  }

  .make-clips-page-content {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .make-clips-library-section {
    --make-clips-library-space: var(--make-clips-mobile-rhythm);
    margin-top: var(--make-clips-schedule-gap);
    padding-left: var(--make-clips-mobile-inset);
    padding-right: var(--make-clips-mobile-inset);
    padding-bottom: var(--make-clips-mobile-rhythm);
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches) {
    padding: 14px 12px;
    border-radius: 14px;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline.assign-cycle-timeline) {
    margin-top: var(--make-clips-reference-gap) !important;
    padding: 14px 12px;
    border-radius: 14px;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .calendar-timeline-header) {
    padding-left: 2px;
    padding-right: 2px;
    margin-bottom: var(--make-clips-mobile-rhythm);
    padding-bottom: 10px;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .schedule-timeline-posting-date-label) {
    font-size: 15px;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-card) {
    min-height: 110px;
    padding: 8px 10px;
    border-radius: 12px;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-timeline .timeline-day-number) {
    font-size: 18px;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .schedule-row-header),
  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-videos .schedule-row-header) {
    margin-bottom: var(--make-clips-mobile-rhythm);
    padding-bottom: 10px;
  }

  .make-clips-library-section :deep(.schedule-page--embedded .schedule-row.schedule-row-batches .schedule-collections-filters) {
    margin-bottom: var(--make-clips-mobile-rhythm);
    padding: 0;
  }

  .make-clips-page .main-card,
  .make-clips-page .admin-main-card,
  .make-clips-page .admin-submit-card {
    margin-top: 0;
    margin-bottom: 0;
    min-height: 0;
  }

  .make-clips-page .card-content {
    padding: var(--make-clips-mobile-rhythm) var(--make-clips-mobile-inset);
  }

  .make-clips-page .card-content form {
    display: flex;
    flex-direction: column;
    gap: var(--make-clips-flow-rhythm);
  }

  .make-clips-page .card-content form > * {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .make-clips-page .main-card :deep(.card-body),
  .make-clips-page .admin-main-card :deep(.card-body),
  .make-clips-page .admin-submit-card :deep(.card-body) {
    padding: 0;
  }

  .url-input-badge-segment {
    flex: 0 0 auto;
    min-width: 0;
    padding-left: 12px;
    padding-right: 12px;
    justify-content: center;
  }

  .url-input-badge-segment span {
    display: none;
  }

  .form-section,
  .url-section,
  .segments-section {
    margin-bottom: var(--make-clips-mobile-rhythm);
  }

  .form-section.url-section {
    margin-bottom: var(--make-clips-mobile-rhythm);
  }

  .url-section-label {
    margin-bottom: var(--make-clips-mobile-rhythm);
  }

  .input-feedback {
    margin-top: var(--make-clips-mobile-rhythm);
  }

  .boolean-switch-field {
    gap: var(--make-clips-mobile-rhythm);
    margin-top: var(--make-clips-mobile-rhythm);
  }

  .video-segments-layout,
  .segments-container {
    margin-top: 0;
  }

  .video-segments-layout {
    margin-top: 0;
    margin-bottom: 0;
  }

  .segments-rules-copy {
    margin-bottom: var(--make-clips-mobile-rhythm);
  }

  .segment-list-mockup {
    gap: var(--make-clips-space-sm);
  }

  .segment-item-compact {
    padding: var(--make-clips-space-md) var(--make-clips-space-md) !important;
  }

  .segments-empty-state {
    gap: var(--make-clips-space-md);
    padding: var(--make-clips-space-md);
  }

  .submit-section {
    padding-top: 0;
  }

  .submit-section-primary {
    margin-top: 0;
  }

  .submit-section.submit-section-inline.submit-section-primary {
    margin-top: 0;
  }

  .submit-rail {
    gap: var(--make-clips-space-md);
    padding: var(--make-clips-space-lg);
  }

  .submit-rail-copy,
  .submit-rail-actions {
    gap: var(--make-clips-space-sm);
  }

  .submit-hint {
    margin-top: var(--make-clips-space-md);
  }

  .admin-forms-grid {
    gap: var(--make-clips-space-lg);
  }

  .admin-card-topbar {
    margin-bottom: var(--make-clips-space-md);
  }

  .make-clips-page .admin-submit-card {
    margin-top: 0;
  }

  .confirm-modal-overlay,
  .confirm-modal-body,
  .confirm-modal-footer,
  .assign-modal-body,
  .assign-modal-footer {
    padding: var(--make-clips-space-lg);
  }

  .confirm-modal-header,
  .assign-modal-header {
    padding: var(--make-clips-space-lg) var(--make-clips-space-lg) var(--make-clips-space-md);
  }

  .confirm-modal-overlay {
    min-height: 100dvh;
    align-items: center;
    justify-content: center;
    padding: max(var(--make-clips-space-md), env(safe-area-inset-top))
      var(--make-clips-space-md)
      max(var(--make-clips-space-md), env(safe-area-inset-bottom));
  }

  .confirm-modal {
    width: min(100%, 560px);
    max-height: calc(100dvh - (var(--make-clips-space-md) * 2));
    border-radius: 16px;
  }

  .confirm-modal-header {
    padding: calc(var(--make-clips-space-lg) + 0.1rem) calc(var(--make-clips-space-lg) + 0.1rem)
      var(--make-clips-space-md);
  }

  .confirm-modal-body,
  .confirm-modal-footer {
    padding: calc(var(--make-clips-space-lg) + 0.1rem);
  }

  .confirm-modal-content,
  .confirm-modal-content--bulk,
  .confirm-bulk-left {
    gap: var(--make-clips-space-lg);
  }

  .confirm-video-card,
  .confirm-vod-summary-item,
  .confirm-assignee-card,
  .confirm-segments-card,
  .confirm-token-card {
    padding: calc(var(--make-clips-space-md) + 0.2rem);
  }

  .confirm-modal-btn {
    min-width: 0;
    flex: 1 1 0;
    white-space: nowrap;
  }

  .modal.assign-modal {
    position: fixed;
    inset: 0;
    z-index: 3000;
  }

  .modal.show.assign-modal {
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: var(--make-clips-space-lg);
  }

  .modal.show.assign-modal .modal-dialog {
    margin: 0;
    width: 100%;
  }
}

@media (max-width: 575px) {
  .make-clips-page {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .confirm-modal-overlay {
    padding: 12px;
  }

  .confirm-modal {
    max-height: calc(100dvh - 24px);
    border-radius: 14px;
  }

  .confirm-modal-header {
    padding: 16px 16px 12px;
  }

  .confirm-modal-header-icon {
    width: 42px;
    height: 42px;
    margin-bottom: 10px;
    font-size: 19px;
  }

  .confirm-modal-title {
    font-size: 1.05rem;
    line-height: 1.25;
  }

  .confirm-modal-subtitle {
    font-size: 0.82rem;
    line-height: 1.45;
    padding-inline: 6px;
  }

  .confirm-modal-body {
    padding: 14px 16px 16px;
  }

  .confirm-modal-content,
  .confirm-modal-content--bulk,
  .confirm-bulk-left {
    gap: 14px;
  }

  .confirm-video-card,
  .confirm-vod-summary-item {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
  }

  .confirm-video-thumb-wrap {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    border-radius: 10px;
  }

  .confirm-video-title {
    margin-bottom: 8px;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .confirm-video-meta {
    gap: 10px;
  }

  .confirm-token-card,
  .confirm-segments-card,
  .confirm-assignee-card,
  .confirm-video-type-card,
  .confirm-video-context-card {
    padding: 14px;
  }

  .confirm-video-type-grid {
    grid-template-columns: 1fr;
  }

  .confirm-token-alert {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .confirm-token-alert-cta {
    width: 100%;
    min-height: 34px;
  }

  .confirm-modal-footer {
    padding: 12px 16px 14px;
    gap: 8px;
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .confirm-modal--create .confirm-modal-footer {
    flex-direction: row;
    align-items: stretch;
    flex-wrap: nowrap;
  }

  .confirm-modal-btn {
    width: 100%;
    min-height: 42px;
    padding: 10px 14px;
    font-size: 0.86rem;
  }

  .confirm-modal--create .confirm-modal-btn {
    width: auto;
    min-width: 0;
    flex: 1 1 0;
    white-space: normal;
    line-height: 1.2;
    padding: 10px 9px;
    font-size: 0.8rem;
  }
}

/* ============================================================
   Flujo de creación en pasos (URL → configurar → confirmar)
   ============================================================ */

.make-flow-viewport {
  width: 100%;
}

.make-flow-viewport--locked {
  height: var(--make-flow-viewport-height);
  min-height: var(--make-flow-viewport-height);
  max-height: var(--make-flow-viewport-height);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.make-flow-viewport--locked .make-flow-stage--url {
  height: 100%;
  min-height: 100%;
  padding-bottom: 0;
  justify-content: center;
}

.make-flow-viewport--locked .make-flow-stage--processing {
  height: 100%;
  min-height: 100%;
  padding: 0 16px;
  justify-content: center;
}

.make-flow-viewport--locked .try-url-hero {
  margin-bottom: 14px;
  gap: 8px;
}

.make-flow-viewport--locked .try-url-hero-title {
  font-size: clamp(1.75rem, 3.6vw, 2.55rem);
}

.make-flow-viewport--locked .try-url-hero-subtitle {
  font-size: clamp(0.92rem, 1.4vw, 1.02rem);
  line-height: 1.45;
}

.make-flow-viewport--locked .make-flow-stage--url .url-section {
  flex: 0 0 auto;
  min-height: 0;
  overflow: visible;
  margin-bottom: 0;
}

.make-flow-viewport--locked .url-section-label {
  margin-bottom: 10px;
}

.make-flow-viewport--locked .url-input-unified {
  min-height: var(--make-flow-url-input-height);
  height: var(--make-flow-url-input-height);
}

.make-flow-viewport--locked .url-input-unified .url-input {
  padding-top: 0;
  padding-bottom: 0;
}

.make-flow-viewport--locked .make-flow-stage--processing .make-flow-processing-download,
.make-flow-viewport--locked .make-flow-stage--processing .make-flow-processing-render {
  height: var(--make-flow-animation-height);
  min-height: var(--make-flow-animation-height);
  max-height: var(--make-flow-animation-height);
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;
}

.make-flow-viewport--locked .make-flow-stage--processing .make-flow-processing-download :deep(.tsd-stage) {
  margin-bottom: 10px;
}

.make-flow-viewport--locked .make-flow-stage--processing .make-flow-processing-download :deep(.tsd-eyebrow) {
  margin-bottom: 8px;
}

.make-flow-viewport--locked .make-flow-stage--processing .make-flow-processing-download :deep(.tsd-size) {
  margin-top: 8px;
}

.make-flow-viewport--locked .make-flow-stage--processing .make-flow-processing-download :deep(.tsd-eta-slot) {
  margin-top: 4px;
}

.make-flow-viewport--locked .make-flow-stage--processing :deep(.try-download-animation-inner) {
  padding: 0;
}

.make-flow-viewport--locked .make-flow-stage--processing :deep(.try-download-animation-spinner) {
  margin-bottom: 14px;
}

/* Swap de etapas, mismo espíritu que try-substep-fade */
.make-flow-swap-enter-active {
  transition:
    opacity 0.42s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.42s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.42s cubic-bezier(0.22, 1, 0.36, 1);
}

.make-flow-swap-leave-active {
  transition:
    opacity 0.24s cubic-bezier(0.4, 0, 1, 1),
    transform 0.24s cubic-bezier(0.4, 0, 1, 1),
    filter 0.24s cubic-bezier(0.4, 0, 1, 1);
}

.make-flow-swap-enter {
  opacity: 0;
  transform: translateY(18px) scale(0.985);
  filter: blur(6px);
}

.make-flow-swap-leave-to {
  opacity: 0;
  transform: translateY(-12px) scale(0.99);
  filter: blur(4px);
}

.make-flow-viewport--locked .make-flow-swap-enter {
  opacity: 0;
  transform: scale(0.988);
  filter: blur(4px);
}

.make-flow-viewport--locked .make-flow-swap-leave-to {
  opacity: 0;
  transform: scale(0.992);
  filter: blur(3px);
}

/* Entrada escalonada del contenido de los pasos 2 y 3 */
@keyframes make-flow-rise {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
    filter: blur(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

@keyframes make-flow-fade-up {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.make-flow-stage--configure .make-flow-step-header,
.make-flow-stage--confirm .make-flow-step-header {
  animation: make-flow-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

.make-flow-stage--configure .make-flow-configure-body {
  animation: make-flow-rise 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.07s backwards;
}

@media (prefers-reduced-motion: reduce) {
  .make-flow-swap-enter-active,
  .make-flow-swap-leave-active {
    transition: opacity 0.2s ease;
  }

  .make-flow-swap-enter,
  .make-flow-swap-leave-to {
    transform: none;
    filter: none;
  }

  .make-flow-stage--configure .make-flow-step-header,
  .make-flow-stage--confirm .make-flow-step-header,
  .make-flow-stage--configure .make-flow-configure-body {
    animation: none;
  }
}

.make-flow-stage {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-bottom: 28px;
}

.make-flow-viewport--locked .make-flow-stage {
  gap: 14px;
  padding-bottom: 0;
}

/* --- Paso 1: URL --- */
.url-input-unified--validating {
  position: relative;
  cursor: progress;
  border-color: rgba(34, 211, 238, 0.65);
  background: rgba(9, 9, 11, 0.88);
  box-shadow:
    0 0 0 3px rgba(34, 211, 238, 0.16),
    0 14px 32px rgba(34, 211, 238, 0.14);
  animation: make-url-validating-glow 1.4s ease-in-out infinite;
}

.url-input-unified--validating .url-input {
  color: var(--clipr-text-tertiary, #94a3b8);
  cursor: progress;
}

.url-input-unified--validating .url-input-icon-segment i {
  animation: make-url-validating-icon 1.1s ease-in-out infinite;
}

.url-input-unified--validating::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, #22d3ee 35%, #a855f7 65%, transparent 100%);
  background-size: 42% 100%;
  background-repeat: no-repeat;
  animation: make-url-validating-sweep 1.1s linear infinite;
}

@keyframes make-url-validating-sweep {
  0% {
    background-position: -45% 0;
  }
  100% {
    background-position: 145% 0;
  }
}

@keyframes make-url-validating-glow {
  0%, 100% {
    box-shadow:
      0 0 0 3px rgba(34, 211, 238, 0.14),
      0 14px 32px rgba(34, 211, 238, 0.12);
  }
  50% {
    box-shadow:
      0 0 0 4px rgba(34, 211, 238, 0.22),
      0 16px 36px rgba(34, 211, 238, 0.2);
  }
}

@keyframes make-url-validating-icon {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.14);
  }
}

.url-input-badge-segment--validating {
  color: #67e8f9;
}

.url-badge-spinner {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid rgba(34, 211, 238, 0.25);
  border-top-color: #22d3ee;
  animation: make-url-badge-spin 0.8s linear infinite;
}

@keyframes make-url-badge-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .url-input-unified--validating,
  .url-input-unified--validating .url-input-icon-segment i,
  .url-input-unified--validating::after {
    animation: none;
  }
}

.make-flow-url-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;
  color: rgba(148, 163, 184, 0.85);
  font-size: 0.8rem;
}

.make-flow-url-hint i {
  color: #67e8f9;
}

.make-flow-success-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0 0;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(74, 222, 128, 0.35);
  background: rgba(74, 222, 128, 0.08);
  color: #4ade80;
  font-size: 0.86rem;
  font-weight: 600;
}

/* --- Encabezado de pasos 2 y 3 --- */
.make-flow-step-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 6px;
}

.make-flow-back-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(9, 9, 11, 0.72);
  color: var(--clipr-text-secondary, #cbd5e1);
  cursor: pointer;
  transition:
    border-color 0.25s ease,
    color 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.make-flow-back-btn i {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.make-flow-back-btn:hover:not(:disabled) {
  border-color: rgba(196, 181, 253, 0.5);
  color: #e9d5ff;
  background: rgba(168, 85, 247, 0.12);
  box-shadow: 0 6px 18px rgba(168, 85, 247, 0.18);
  transform: translateX(-2px);
}

.make-flow-back-btn:hover:not(:disabled) i {
  transform: translateX(-2px);
}

.make-flow-back-btn:active:not(:disabled) {
  transform: translateX(-2px) scale(0.94);
}

.make-flow-back-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.35);
}

.make-flow-back-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.make-flow-step-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.make-flow-step-title {
  margin: 0;
  color: var(--clipr-text-primary, #f8fafc);
  font-size: clamp(1.2rem, 2.4vw, 1.6rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.make-flow-step-subtitle {
  margin: 0;
  color: var(--clipr-text-tertiary, #94a3b8);
  font-size: 0.9rem;
}

/* --- Paso 2: preview + tipo de video --- */
.make-flow-stage--configure {
  gap: 16px;
  max-width: 720px;
  margin: 0 auto;
  width: 100%;
}

.make-flow-configure-body {
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background:
    radial-gradient(circle at 50% 0%, rgba(168, 85, 247, 0.1), transparent 52%),
    rgba(2, 6, 23, 0.58);
  box-shadow:
    0 24px 48px rgba(2, 6, 23, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  overflow: hidden;
}

.make-flow-configure-body::before {
  content: "";
  position: absolute;
  top: 0;
  left: 12%;
  right: 12%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.55), rgba(34, 211, 238, 0.55), transparent);
  opacity: 0.9;
  z-index: 1;
}

.make-flow-configure-preview {
  padding: 22px 24px 10px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background:
    radial-gradient(circle at 50% 100%, rgba(34, 211, 238, 0.06), transparent 62%);
}

.make-flow-download {
  margin: 0 auto;
}

/* --- Paso "en proceso": descarga/render sustituyendo la URL --- */
.make-flow-stage--processing {
  max-width: 760px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.make-flow-stage--processing .make-flow-processing-download {
  --tsd-node-icon-size: 80px;
  --tsd-conduit-height: 132px;
  margin: 0 auto;
  width: 100%;
  max-width: 720px;
}

.make-flow-stage--processing .make-flow-processing-download :deep(.tsd-stage) {
  gap: 12px;
  margin-bottom: 20px;
}

.make-flow-stage--processing .make-flow-processing-download :deep(.tsd-node) {
  width: 92px;
}

.make-flow-stage--processing .make-flow-processing-download :deep(.tsd-node-icon) {
  border-radius: 20px;
}

.make-flow-stage--processing .make-flow-processing-download :deep(.tsd-node-icon img) {
  width: 46px;
  height: 46px;
}

.make-flow-stage--processing .make-flow-processing-download :deep(.tsd-thumb) {
  width: 252px;
  border-radius: 18px;
}

.make-flow-processing-download :deep(.tsd-eyebrow) {
  margin-bottom: 12px;
  font-size: 0.78rem;
}

.make-flow-processing-download :deep(.tsd-size) {
  margin-top: 12px;
  font-size: 0.96rem;
}

.make-flow-processing-download :deep(.tsd-eta-slot) {
  margin-top: 8px;
}

.make-flow-processing-download :deep(.try-eta-timer) {
  margin-top: 4px;
}

.make-flow-processing-download :deep(.try-eta-timer-value) {
  font-size: 2rem;
}

.make-flow-processing-download :deep(.try-eta-timer-unit) {
  font-size: 0.88rem;
}

.make-flow-processing-render {
  width: 100%;
  max-width: 520px;
}

.make-flow-stage--processing :deep(.try-download-animation) {
  width: 100%;
}

.make-flow-stage--processing :deep(.try-download-animation-spinner) {
  width: 96px;
  height: 96px;
}

@media (max-width: 640px) {
  .make-clips-page {
    --make-flow-viewport-height: 278px;
    --make-flow-animation-height: 224px;
  }

  .make-clips-page .main-card.main-card--flow-locked .card-content {
    padding: 14px 16px 0;
  }

  .make-flow-stage--processing {
    padding: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .make-flow-stage--processing .make-flow-processing-download :deep(.tsd-node) {
    width: clamp(46px, 15vw, 62px);
  }

  .make-flow-stage--processing .make-flow-processing-download {
    max-width: 100%;
    --tsd-node-icon-size: clamp(42px, 13vw, 56px);
    --tsd-conduit-height: clamp(72px, 22vw, 96px);
  }

  .make-flow-stage--processing .make-flow-processing-download :deep(.tsd-stage) {
    max-width: 100%;
    min-width: 0;
    gap: clamp(3px, 1.5vw, 8px);
  }

  .make-flow-stage--processing .make-flow-processing-download :deep(.tsd-node-icon img) {
    width: clamp(24px, 7.5vw, 31px);
    height: clamp(24px, 7.5vw, 31px);
  }

  .make-flow-stage--processing .make-flow-processing-download :deep(.tsd-thumb) {
    width: clamp(104px, 36vw, 150px);
  }

  .make-flow-processing-download :deep(.try-eta-timer-value) {
    font-size: 1.72rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .make-flow-stage--processing {
    animation: none;
  }
}

.make-flow-type-section {
  margin: 0;
  padding: 10px 22px 22px;
}

.make-flow-type-grid {
  gap: 12px;
}

.make-flow-type-grid .confirm-video-type-option {
  height: auto;
  min-height: 92px;
  padding: 14px 12px;
  gap: 12px;
  border-radius: 16px;
  background: rgba(9, 9, 11, 0.62);
  transition:
    transform 0.32s cubic-bezier(0.34, 1.4, 0.64, 1),
    border-color 0.25s ease,
    box-shadow 0.25s ease,
    background 0.25s ease;
  animation: make-flow-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

.make-flow-type-grid .confirm-video-type-option:nth-child(1) {
  animation-delay: 0.16s;
}

.make-flow-type-grid .confirm-video-type-option:nth-child(2) {
  animation-delay: 0.22s;
}

.make-flow-type-grid .confirm-video-type-option:nth-child(3) {
  animation-delay: 0.28s;
}

.make-flow-type-grid .confirm-video-type-option:nth-child(4) {
  animation-delay: 0.34s;
}

.make-flow-type-grid .confirm-video-type-option:hover:not(:disabled) {
  transform: translateY(-3px);
  border-color: rgba(196, 181, 253, 0.45);
  background: rgba(15, 23, 42, 0.78);
  box-shadow:
    0 18px 36px rgba(15, 23, 42, 0.42),
    0 6px 18px rgba(168, 85, 247, 0.14);
}

.make-flow-type-grid .confirm-video-type-option--active {
  border-color: rgba(34, 211, 238, 0.58);
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.18), transparent 52%),
    rgba(15, 23, 42, 0.88);
  box-shadow:
    0 18px 34px rgba(34, 211, 238, 0.16),
    inset 0 0 0 1px rgba(34, 211, 238, 0.18);
}

.make-flow-type-grid .confirm-video-type-option-icon {
  width: 40px;
  height: 40px;
  margin-left: 0;
  border-radius: 11px;
  font-size: 1.05rem;
  transition:
    transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
    color 0.25s ease,
    border-color 0.25s ease,
    background 0.25s ease;
}

.make-flow-type-grid .confirm-video-type-option:hover:not(:disabled) .confirm-video-type-option-icon {
  transform: scale(1.1) rotate(-4deg);
}

.make-flow-type-grid .confirm-video-type-option--active .confirm-video-type-option-icon {
  transform: scale(1.06);
}

@keyframes make-flow-check-pop {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  55% {
    transform: scale(1.22);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.make-flow-type-grid .confirm-video-type-option--active .confirm-video-type-option-check {
  animation: make-flow-check-pop 0.42s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.make-flow-type-grid .confirm-video-type-option-copy strong {
  font-size: 0.88rem;
  line-height: 1.25;
}

.make-flow-type-grid .confirm-video-type-option-copy small {
  font-size: 0.74rem;
  line-height: 1.35;
  color: rgba(148, 163, 184, 0.92);
}

.make-flow-type-grid .confirm-video-type-option-check {
  width: 22px;
  height: 22px;
  margin-right: 2px;
}

.make-flow-type-section .confirm-video-context-card {
  margin-top: 14px;
}

.make-flow-stage--configure-choosing .make-flow-back-btn {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.make-flow-stage--configure-choosing .make-flow-step-heading {
  opacity: 0.3;
  transition: opacity 0.45s ease;
}

.make-flow-configure-body--choosing {
  overflow: visible;
}

.make-flow-configure-body--choosing::before {
  opacity: 0;
  transition: opacity 0.4s ease;
}

.make-flow-configure-body--choosing .make-flow-configure-preview {
  position: relative;
  z-index: 1;
  pointer-events: none;
  opacity: 0.22;
  filter: blur(4px);
  transform: scale(0.97);
  transition:
    opacity 0.5s ease,
    filter 0.5s ease,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.make-flow-download--choosing {
  opacity: 1;
  pointer-events: none;
}

.make-flow-configure-body--choosing .make-flow-type-section {
  position: relative;
  z-index: 4;
}

.make-flow-configure-body--choosing .make-flow-type-grid {
  position: relative;
  z-index: 5;
}

.make-flow-type-grid .make-flow-type-option--dismiss {
  opacity: 0;
  transform: translateY(32px) scale(0.92);
  filter: blur(4px);
  pointer-events: none;
  animation: none;
  transition:
    opacity 0.45s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.52s cubic-bezier(0.4, 0, 0.2, 1),
    filter 0.45s ease;
}

.make-flow-type-grid .make-flow-type-option--dismiss:nth-child(1) {
  transition-delay: 0s;
}

.make-flow-type-grid .make-flow-type-option--dismiss:nth-child(2) {
  transition-delay: 0.05s;
}

.make-flow-type-grid .make-flow-type-option--dismiss:nth-child(3) {
  transition-delay: 0.1s;
}

.make-flow-type-grid .make-flow-type-option--dismiss:nth-child(4) {
  transition-delay: 0.15s;
}

.make-flow-type-grid .make-flow-type-option--chosen {
  position: relative;
  z-index: 20;
  pointer-events: none;
  animation: none;
  transform: translate(var(--make-chosen-x, 0), var(--make-chosen-y, 0)) scale(1.14);
  border-color: rgba(34, 211, 238, 0.72);
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.28), transparent 52%),
    rgba(15, 23, 42, 0.96);
  box-shadow:
    0 8px 24px rgba(2, 6, 23, 0.55),
    0 32px 72px rgba(34, 211, 238, 0.28),
    0 0 0 1px rgba(34, 211, 238, 0.24),
    inset 0 0 0 1px rgba(34, 211, 238, 0.2);
  transition:
    transform 0.82s cubic-bezier(0.3, 1.16, 0.35, 1),
    border-color 0.6s ease,
    box-shadow 0.82s ease,
    background 0.6s ease;
}

.make-flow-type-grid .make-flow-type-option--chosen .confirm-video-type-option-shine {
  opacity: 1;
  animation: make-flow-type-shine 0.82s ease-in-out 0.18s forwards;
}

.make-flow-type-grid .make-flow-type-option--leaving {
  opacity: 0;
  transform: translate(var(--make-chosen-x, 0), calc(var(--make-chosen-y, 0) - 16px)) scale(1.24);
  filter: blur(12px);
  transition:
    opacity 0.38s cubic-bezier(0.4, 0, 1, 1),
    transform 0.38s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.38s ease;
}

@keyframes make-flow-type-shine {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(120%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .make-flow-type-grid .confirm-video-type-option {
    animation: none;
    transition: none;
  }

  .make-flow-type-grid .confirm-video-type-option--active .confirm-video-type-option-check {
    animation: none;
  }

  .make-flow-type-grid .make-flow-type-option--dismiss,
  .make-flow-type-grid .make-flow-type-option--chosen,
  .make-flow-type-grid .make-flow-type-option--leaving {
    transform: none;
    filter: none;
    transition: opacity 0.25s ease;
    transition-delay: 0s;
  }

  .make-flow-type-grid .make-flow-type-option--chosen .confirm-video-type-option-shine {
    animation: none;
    opacity: 0;
  }

  .make-flow-download--choosing,
  .make-flow-configure-body--choosing .make-flow-configure-preview {
    transform: none;
    filter: none;
    transition: opacity 0.25s ease;
  }
}

.make-flow-trim-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.make-flow-segments-list {
  margin-top: 2px;
}

.make-flow-warning-copy {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 0;
  color: #fbbf24;
  font-size: 0.82rem;
  font-weight: 600;
}

.make-flow-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.make-flow-footer-hint {
  margin: 0;
  color: rgba(148, 163, 184, 0.85);
  font-size: 0.82rem;
}

.make-flow-continue-btn {
  min-width: 180px;
}

.make-flow-continue-btn i {
  margin-left: 8px;
}

/* --- Paso 3: confirmación --- */
.make-flow-stage--confirm {
  gap: 12px;
  max-width: 860px;
  margin: 0 auto;
  width: 100%;
}

.make-flow-admin-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px dashed rgba(148, 163, 184, 0.28);
  background: rgba(9, 9, 11, 0.45);
}

.make-flow-admin-bar-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.make-flow-admin-bar-label {
  color: rgba(148, 163, 184, 0.85);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.make-flow-admin-bar-user {
  color: var(--clipr-text-primary, #f8fafc);
  font-size: 0.84rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.make-flow-admin-bar-user--empty {
  color: var(--clipr-text-tertiary, #94a3b8);
  font-weight: 500;
}

.make-flow-admin-assign-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(15, 23, 42, 0.72);
  color: var(--clipr-text-secondary, #cbd5e1);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
}

.make-flow-admin-assign-btn:hover:not(:disabled) {
  border-color: rgba(196, 181, 253, 0.45);
  color: #e9d5ff;
  background: rgba(168, 85, 247, 0.12);
}

.make-flow-admin-assign-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.make-flow-confirm-body {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background:
    radial-gradient(circle at 50% 0%, rgba(168, 85, 247, 0.08), transparent 55%),
    rgba(2, 6, 23, 0.58);
  box-shadow:
    0 24px 48px rgba(2, 6, 23, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  overflow: hidden;
  animation: make-flow-rise 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.07s backwards;
}

.make-flow-confirm-body::before {
  content: "";
  position: absolute;
  top: 0;
  left: 12%;
  right: 12%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.55), rgba(34, 211, 238, 0.55), transparent);
  opacity: 0.9;
  z-index: 1;
}

.make-flow-stage--confirm .make-flow-admin-bar {
  animation: make-flow-fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.05s backwards;
}

.make-flow-confirm-video {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background:
    radial-gradient(circle at 0% 100%, rgba(34, 211, 238, 0.06), transparent 55%);
  animation: make-flow-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.16s backwards;
}

.make-flow-confirm-video-thumb {
  position: relative;
  flex-shrink: 0;
  width: 148px;
  height: 84px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(0, 0, 0, 0.45);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.make-flow-confirm-video:hover .make-flow-confirm-video-thumb {
  border-color: rgba(34, 211, 238, 0.4);
  box-shadow: 0 10px 26px rgba(34, 211, 238, 0.14);
}

.make-flow-confirm-video-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.make-flow-confirm-video:hover .make-flow-confirm-video-thumb img {
  transform: scale(1.05);
}

.make-flow-confirm-video-thumb-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(148, 163, 184, 0.7);
  font-size: 1.4rem;
}

.make-flow-confirm-video-duration {
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 2px 7px;
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.88);
  border: 1px solid rgba(148, 163, 184, 0.24);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
}

.make-flow-confirm-video-copy {
  min-width: 0;
  flex: 1;
}

.make-flow-confirm-video-title {
  margin: 0 0 8px;
  color: var(--clipr-text-primary, #f8fafc);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.make-flow-confirm-video-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  row-gap: 6px;
  color: var(--clipr-text-tertiary, #94a3b8);
  font-size: 0.78rem;
}

.make-flow-confirm-video-meta span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.make-flow-confirm-video-meta i {
  font-size: 0.82rem;
  opacity: 0.85;
}

.make-flow-confirm-video-source {
  padding: 4px 8px;
  color: #ddd6fe;
  border: 1px solid rgba(196, 181, 253, 0.24);
  border-radius: 999px;
  background: rgba(168, 85, 247, 0.1);
  font-weight: 600;
}

.make-flow-confirm-video-source img {
  width: 13px;
  height: 13px;
  object-fit: contain;
}

.make-flow-confirm-video-meta-detail {
  opacity: 0.72;
}

.make-flow-confirm-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  align-items: stretch;
}

.make-flow-confirm-main {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}

.make-flow-confirm-main {
  padding: 20px 22px;
  gap: 22px;
  animation: make-flow-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.24s backwards;
}

.make-flow-confirm-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  color: rgba(148, 163, 184, 0.92);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.make-flow-confirm-section-head::after {
  content: "";
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(148, 163, 184, 0.24), transparent);
}

.make-flow-confirm-type .make-flow-type-selected {
  margin-top: 0;
}

.make-flow-confirm-trim {
  padding-top: 18px;
}

.make-flow-confirm-trim .make-flow-confirm-section-head {
  margin-bottom: 10px;
}

.make-flow-change-type-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  color: #c4b5fd;
  border: 0;
  border-radius: 0;
  background: transparent;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: color 0.18s ease;
}

.make-flow-change-type-btn:hover {
  color: #f3e8ff;
  text-decoration: underline;
}

.make-flow-confirm-type-grid {
  margin-top: 0;
}

.confirm-video-type-change-enter-active,
.confirm-video-type-change-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.confirm-video-type-change-enter,
.confirm-video-type-change-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.make-flow-confirm-timeline {
  margin-top: 0;
}

.make-flow-type-summary .confirm-video-type-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.make-flow-edit-link {
  margin-left: auto;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid rgba(196, 181, 253, 0.35);
  background: transparent;
  color: #c4b5fd;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
}

.make-flow-edit-link:hover:not(:disabled) {
  border-color: rgba(196, 181, 253, 0.6);
  background: rgba(168, 85, 247, 0.12);
  color: #e9d5ff;
}

.make-flow-type-selected {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(168, 85, 247, 0.35);
  background:
    radial-gradient(circle at 0% 50%, rgba(168, 85, 247, 0.16), transparent 55%),
    rgba(168, 85, 247, 0.08);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.make-flow-type-selected:hover {
  border-color: rgba(196, 181, 253, 0.5);
  box-shadow: 0 10px 24px rgba(168, 85, 247, 0.12);
}

.make-flow-type-selected-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(196, 181, 253, 0.35);
  background: rgba(15, 23, 42, 0.72);
  color: #c4b5fd;
  box-shadow: 0 6px 16px rgba(168, 85, 247, 0.16);
}

.make-flow-type-selected-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.make-flow-type-selected-copy strong {
  color: var(--clipr-text-primary, #f8fafc);
  font-size: 0.95rem;
}

.make-flow-type-selected-copy small {
  color: var(--clipr-text-tertiary, #94a3b8);
  font-size: 0.78rem;
}

.make-flow-context-summary {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(9, 9, 11, 0.5);
  color: var(--clipr-text-secondary, #cbd5e1);
  font-size: 0.82rem;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.make-flow-context-summary i {
  flex-shrink: 0;
  margin-top: 2px;
  color: #67e8f9;
}

.make-flow-assignee-body {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.make-flow-footer--confirm {
  margin-top: 0;
  justify-content: center;
  animation: make-flow-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.4s backwards;
}

.upload-progress-card {
  width: min(100%, 560px);
  margin: 16px auto 0;
  padding: 13px 16px;
  border: 1px solid rgba(103, 232, 249, 0.2);
  border-radius: 12px;
  background: rgba(8, 47, 73, 0.2);
  color: var(--clipr-text-secondary, #cbd5e1);
}

.upload-progress-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 0.78rem;
}

.upload-progress-head strong {
  color: #67e8f9;
}

.upload-progress-track {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.16);
}

.upload-progress-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #a855f7, #06b6d4);
  transition: width 0.2s ease;
}

.upload-progress-card small {
  display: block;
  margin-top: 7px;
  color: var(--clipr-text-tertiary, #94a3b8);
  text-align: center;
}

.make-flow-confirm-submit-btn {
  position: relative;
  overflow: hidden;
  width: min(100%, 360px);
  min-width: 260px;
  min-height: 54px;
  padding: 10px 28px;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 52%, #06b6d4 100%);
  box-shadow: 0 8px 20px rgba(124, 58, 237, 0.26);
  font-weight: 700;
  transition:
    transform 0.28s cubic-bezier(0.34, 1.4, 0.64, 1),
    box-shadow 0.28s ease,
    filter 0.28s ease;
}

.make-flow-confirm-submit-btn::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(105deg, transparent 42%, rgba(255, 255, 255, 0.24) 50%, transparent 58%);
  transform: translateX(-130%);
  animation: make-flow-cta-shine 3.8s ease-in-out 1.4s infinite;
}

@keyframes make-flow-cta-shine {
  0% {
    transform: translateX(-130%);
  }
  32%, 100% {
    transform: translateX(130%);
  }
}

.make-flow-confirm-submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  filter: brightness(1.06);
  box-shadow:
    0 14px 34px rgba(99, 102, 241, 0.38),
    0 4px 14px rgba(34, 211, 238, 0.2);
}

.make-flow-confirm-submit-btn:active:not(:disabled) {
  transform: translateY(0) scale(0.99);
}

.make-flow-confirm-submit-btn:disabled::after {
  animation: none;
}

.make-flow-confirm-submit-btn i {
  margin-right: 8px;
}

.make-flow-confirm-submit-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}

.make-flow-confirm-submit-copy small {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.67rem;
  font-weight: 600;
}

.make-flow-queue-spinner {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.make-flow-queue-spinner-aura {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.28) 0%, rgba(103, 232, 249, 0.12) 45%, transparent 72%);
  animation: make-flow-queue-aura 1.8s ease-in-out infinite;
}

.make-flow-queue-spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid transparent;
}

.make-flow-queue-spinner-ring--outer {
  border-top-color: rgba(255, 255, 255, 0.95);
  border-right-color: rgba(255, 255, 255, 0.28);
  filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.35));
  animation: make-flow-queue-spin 0.85s linear infinite;
}

.make-flow-queue-spinner-ring--inner {
  inset: 4px;
  border-bottom-color: rgba(224, 242, 254, 0.9);
  border-left-color: rgba(224, 242, 254, 0.22);
  filter: drop-shadow(0 0 3px rgba(103, 232, 249, 0.45));
  animation: make-flow-queue-spin-reverse 1.15s linear infinite;
}

.make-flow-queue-spinner-orbit {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #f0fdfa;
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.85);
  animation: make-flow-queue-orbit 1.6s linear infinite;
}

@keyframes make-flow-queue-aura {
  0%, 100% {
    opacity: 0.45;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes make-flow-queue-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes make-flow-queue-spin-reverse {
  to {
    transform: rotate(-360deg);
  }
}

@keyframes make-flow-queue-orbit {
  from {
    transform: rotate(0deg) translateX(7px) rotate(0deg);
  }
  to {
    transform: rotate(360deg) translateX(7px) rotate(-360deg);
  }
}

.make-flow-submit-error {
  justify-content: center;
}

@media (min-width: 901px) {
  .make-flow-confirm-main--with-trim {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: stretch;
    gap: 22px;
  }

  .make-flow-confirm-main--with-trim .make-flow-confirm-trim {
    padding-top: 0;
  }

  .make-flow-confirm-main--with-trim .make-flow-confirm-type,
  .make-flow-confirm-main--with-trim .make-flow-confirm-trim {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .make-flow-confirm-main--with-trim .make-flow-type-selected,
  .make-flow-confirm-main--with-trim .confirm-segments-timeline,
  .make-flow-confirm-main--with-trim .confirm-manual-trim {
    flex: 1;
  }

  .make-flow-confirm-main--with-trim .make-flow-type-selected {
    align-items: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .make-flow-confirm-body,
  .make-flow-stage--confirm .make-flow-admin-bar,
  .make-flow-confirm-video,
  .make-flow-confirm-main,
  .make-flow-footer--confirm,
  .make-flow-confirm-submit-btn::after,
  .make-flow-queue-spinner-aura,
  .make-flow-queue-spinner-ring--outer,
  .make-flow-queue-spinner-ring--inner,
  .make-flow-queue-spinner-orbit {
    animation: none;
  }

  .make-flow-queue-spinner-orbit {
    display: none;
  }

  .make-flow-confirm-video:hover .make-flow-confirm-video-thumb img {
    transform: none;
  }

  .make-flow-confirm-submit-btn,
  .make-flow-back-btn,
  .make-flow-back-btn i {
    transition: none;
  }
}

@media (max-width: 640px) {
  .make-flow-step-header {
    gap: 10px;
  }

  .make-flow-back-btn {
    width: 36px;
    height: 36px;
  }

  .make-flow-configure-preview {
    padding: 16px 16px 8px;
  }

  .make-flow-subtitles-wrap {
    padding: 12px 16px 6px;
  }

  .make-flow-subtitles-pill {
    width: 100%;
    max-width: 300px;
    padding: 12px 16px 10px;
  }

  .make-flow-type-section {
    padding: 16px;
  }

  .make-flow-type-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .make-flow-type-grid .confirm-video-type-option {
    min-height: 84px;
  }

  .make-flow-confirm-video {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
  }

  .make-flow-confirm-video-thumb {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
  }

  .make-flow-confirm-main {
    padding: 16px;
  }

  .make-flow-footer:not(.make-flow-footer--confirm) {
    justify-content: stretch;
  }

  .make-flow-footer:not(.make-flow-footer--confirm) .confirm-modal-btn {
    flex: 1;
    justify-content: center;
  }
}

</style>


